import { Injectable, model } from '@angular/core';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import * as authLib from '../modules/shared/models/authenticationModels';
import { BehaviorSubject, Observable, Subject, Subscriber, Subscription, combineLatest, filter, firstValueFrom, from, map, pairwise, shareReplay, startWith } from 'rxjs';
import { CompanyPostingOptions, ExternalDataSource, GetDefaultCurrencyList, IUpdatedDateRange, NtCompany, ProviderConnectRequest } from '../modules/shared/models/nt-company';
import { DialogService } from '../modules/shared/services/dialog.service';
import { ChildActivationEnd, Params, Router } from '@angular/router';
import { ApiResultValues, IApiQueryResult, IApiResult } from '../modules/shared/models/apiResultModels';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogBoxResults } from '../modules/shared/models/dialogDefaults';
import { IGroupContainer, IItem, IKeyValue, ISection, formatDate, getFormDirtyValues } from '../modules/shared/models/group-edit-config';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalEditorComponent } from '../modules/shared/components/modal-editor/modal-editor.component';
import { dialogDefaultFullScreen } from '../modules/shared/models/dialogDefaults';
import { NotValueValidatorDirective } from '../modules/shared/validators/not-value-validator.directive';
import { AppConfig } from '../modules/shared/models/app-config';
import { CompareValueValidator } from '../modules/shared/validators/compare-value-validator';
import moment from 'moment';
import { FormFieldCharacterValidator } from '../modules/shared/validators/form-field-character-validator';
import { Asset, AssetConvertToKit, AssetDisposalModel, KitChild } from '../modules/shared/models/asset';
import { AssetUploadModel } from '../modules/shared/models/asset-upload-model';
import { EqualsValueValidator } from '../modules/shared/validators/equals-value-validator';
import { AssetConvertKitComponent } from '../modules/fixed-asset/components/asset-convert-kit/asset-convert-kit.component';
import { AccrualPrepaymentUploadModel } from '../modules/shared/models/accrual-prepayment-models';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { CompanySummaryModel } from '../modules/shared/models/fixed-asset-register-summary-model';
import { HelpService } from './help.service';
import { AssetSelectListSharedComponent } from '../modules/shared/components/asset-select-list-shared/asset-select-list-shared.component';
import { I, V } from '@angular/cdk/keycodes';
import { LessThanCompareValidator } from '../modules/shared/validators/less-than-compare-validator';
import { formatNumber, formatPercent } from '@angular/common';


export interface IConnectStatusProgress {
    purpose: string;
    state: string;
    error: boolean;
    message: string;
    datasource?: ExternalDataSource;
}

@Injectable()
export class CompanyService {
    serviceLoaded = new BehaviorSubject<boolean>(false);
    private _companyList = new BehaviorSubject<NtCompany[]>(null);
    private _selectedCompany = new BehaviorSubject<NtCompany>(null);
    private _processingStatus = new BehaviorSubject<any>(null);
    private _companySummary = new BehaviorSubject<CompanySummaryModel>(null);

    private _routedCompanyId = new BehaviorSubject<string>(null);

    constructor(private api: ApiService,
        private router: Router,
        private auth: AuthService,
        private dlg: DialogService,
        private matDlg: MatDialog,
        private fb: FormBuilder,
        private snack: MatSnackBar,
        private help: HelpService) {
            console.log("CompanyService", "Constructor is now firing");
            this._routedCompanyId.next(null);
            
            this.auth.IsAuthenticatedDetail.pipe(
                startWith({
                    isAuthenticated: false,
                    reason: authLib.AuthenticationStatusPurpose.Initialisation
                }),
                pairwise()
            ).subscribe(newState => {
                this.userLoginStatusChanged(newState);
            });

            this.serviceLoaded.pipe(
                startWith(false),
                pairwise()
            ).subscribe(r => {
                console.log("CompanyService", "ServiceLoaded has fired with new value", r);
            });

            this.SelectedCompany.pipe(
                startWith(null),
                pairwise()
            ).subscribe(c => {
                this.handleSelectedCompanyChange(c);
            });

            this.api.ExternalDataReconnectRequired.subscribe(v => {
                if(v){
                    this.handleReconnectRequired();
                }                
            });

            this.router.events
                .pipe(
                    filter((evt: any) => evt instanceof ChildActivationEnd), 
                    filter((evt: ChildActivationEnd) => evt.snapshot.component?.name === '_CompanyComponent')
                ).subscribe((events: ChildActivationEnd) => {
                    this._routedCompanyId.next(events.snapshot.params["id"]);
                });

            combineLatest([this.serviceLoaded,
                this._routedCompanyId])
                .pipe(
                    startWith(false, ''),
                    pairwise()
                )
                .subscribe(p => {                    
                    //This will only fire when serviceLoaded (analogous to userAuthenticated) is true
                    console.log("CompanyService", "CombineLatest event", p);
                    //Now check the routed value and see if it has changed
                    const routedIdChanged = p[0][1] !== p[1][1];
                    const serviceLoadedStateChanged = p[0][0] !== p[1][0];
                    const serviceLoaded = p[1][0];
                    const routeId = p[1][1];

                    //What if we get a routeId before we get the service loaded?  We don't know if it is valid.
                    //Also, we do not want to continually fire updates simply because we've observed a route change.
                    if(serviceLoaded){
                        if(serviceLoadedStateChanged){
                            //We might already have a route Id, so not interested in whether it has changed
                            //  just get things started
                            if(routeId){
                                const c = this._companyList.value.find(x=> x.safeId === routeId);
                                if(c){
                                    console.log('CompanyService', 'Service has completed startup and a company matches the requested route');
                                    if(c.pendingDeletion){
                                        console.log('CompanyService', 'Route has changed but company is pending deletion.  Prompt user');
                                        this.cancelCompanyDeletion(c).then(activate => {
                                            if(activate){
                                                console.log('CompanyService', 'User has reactivate company - select and allow to navigate');
                                                this._selectedCompany.next(c);
                                            }else{
                                                console.log('CompanyService', 'Reactivate did not complete so clear requested route Id and allow navigation to take place');
                                                this._routedCompanyId.next('');
                                            }
                                        });
                                    }else{
                                        console.log('CompanyService', 'Company found.  Activate');
                                        this._selectedCompany.next(c);
                                    }
                                }else{
                                    console.log('CompanyService', `Company with id ${routeId} has not been found - redirect to company list`);
                                    this._selectedCompany.next(null);
                                    this.router.navigate(['/company']);
                                }
                            }else{
                                console.log("CompanyService", 'Service has activated, but there is no valid route Id, so let components do their work');
                            }
                        }else{
                            //Only if the routeId has changed do we care about this scenario
                            if(routedIdChanged){
                                if(routeId){
                                    const c = this._companyList.value.find(x=> x.safeId === routeId);
                                    if(c){
                                        console.log('CompanyService', 'Route has changed with a new valid company Id');
                                        if(c.pendingDeletion){
                                            console.log('CompanyService', 'Route has changed but company is pending deletion.  Prompt user');
                                            this.cancelCompanyDeletion(c).then(activate => {
                                                if(activate){
                                                    console.log('CompanyService', 'User has reactivate company - select and allow to navigate');
                                                    this._selectedCompany.next(c);
                                                }else{
                                                    console.log('CompanyService', 'Reactivate did not complete so clear requested route Id and allow navigation to take place');
                                                    this._routedCompanyId.next('');
                                                }
                                            });
                                        }else{
                                            console.log('CompanyService', 'Company found.  Activate');
                                            this._selectedCompany.next(c);
                                        }
                                    }else{
                                        console.log('CompanyService', 'Route has changed but has an invalid company id.  Head to company list');
                                        this._selectedCompany.next(null);
                                        this.router.navigate(['/company']);
                                    }
                                }else if(routeId === ''){
                                    console.log('CompanyService', 'Route has changed with a blank company Id.  Head to company list');
                                    this._selectedCompany.next(null);
                                    this.router.navigate(['/company']);
                                }else{
                                    console.log('CompanyService', 'Route Id has changed, but not to a validcompany id');
                                }
                            }
                        }
                        
                    }
                });

    }

    async handleReconnectRequired(){
        //const res = await this.dlg.showYesNo('External data error', 'An error was detected attempting to communicate with your external accounts package.  We recommend attempting to re-authorise your connection.  Would you like to reconnect?');
        // if(res === DialogBoxResults.Yes){
        //     await this.requestCompanyReconnect();
        // }
        await this.dlg.showOk('External data error',  `An error was reported attempting to communicate with your external accounts package.  If this error appears again, we recommend reauthorising your connection.  Please visit 'Settings' and select 'Accounting Provider' to review the status.`);
    }

    
    async handleSelectedCompanyChange(companies: any[]){
        //companies should be an array of length two - previous and current companies.
        //We need to decide whether a company needs initialising or data needs clearing

        //Financial years
        //Depreciation rules
        //Selected asset
        //SElected date range
        let actionRequired = false;
        let initialisationRequired = false;
        let summaryRefreshRequired = false;
        let yearEndRefreshRequired = false;

        this.dlg.createLog("CompanyService","handleSelectedCompanyChange is starting", companies);
        //Going from a null value to valid value
        if(companies[1] &&  !companies[0]){
            actionRequired = true;
            initialisationRequired = true;
            this.dlg.createLog("CompanyService","Action required because initialisation of company is needed");
        }

        //possible change of company
        if(companies[0] && companies[1]){
            //If the id's are different, init is req
            actionRequired =true;
            if(companies[0].safeId !== companies[1].safeId){
                initialisationRequired = true;
                this.dlg.createLog("CompanyService","Action required because change of company is needed");
            }else{
                summaryRefreshRequired = true;
                if(companies[0].financialYearEnd !== companies[1].financialYearEnd){
                    yearEndRefreshRequired = true;
                }
            }
        }

        //going from valid to null values (after logout for example)
        if(companies[0] && !companies[1]){
            actionRequired = true;
            this.dlg.createLog("CompanyService","Action required because disposal of current company is needed");
        }

        if(actionRequired){
            this.dlg.createLog("CompanyService","handleSelectCompanyChange - Action will be required");
            
            
            if(initialisationRequired){
                this.dlg.createLog("CompanyService","handleSelectCompanyChange - Initialising company");
                this.financialYears = null;
                this.depRulesUICache = [];
                this._loanTypeCounts = [];
                this.locationCache.next([]);
                this.classCache.next([]);

                
                //Suspended service test
                if(companies[1].subscribedService.suspended){
                    const c = companies[1];
                    
                    if(c.subscribedService.paymentState === 'FreeTrialEnded'){
                        this.router.navigate(['/account/organisation/', c.subscribedService.billingOrganisationId, 'subscription', c.subscribedServiceId, 'free-trial']);
                    }else{
                        this.router.navigate(['/account/organisation/', c.subscribedService.billingOrganisationId, 'subscription', c.subscribedServiceId, 'remove-suspension']);
                    }

                    setTimeout(() =>{
                        this._selectedCompany.next(null);
                    }, 250);
                    return;
                }

                const currentYear = await this.getCurrentFinancialYear();
                const dateUpdate: IUpdatedDateRange = {
                    yearId: currentYear.id,
                    startDate: currentYear.yearStart,
                    endDate: currentYear.yearEnd
                };
                this.dlg.createLog("CompanyService","Selected date range information being set", dateUpdate);
                this.updateSelectedDateRange(dateUpdate);
                this.getCompanyProviderTrackingDataInteral(true);
                await this.refreshCompanySummaryData();
                
            }else if(summaryRefreshRequired){
                this.dlg.createLog("CompanyService","handleSelectCompanyChange - Refreshing company summary data");
                await this.refreshCompanySummaryData();
                if(yearEndRefreshRequired){
                    this.financialYears = null;
                    const currentYear = await this.getCurrentFinancialYear();
                    const dateUpdate: IUpdatedDateRange = {
                        yearId: currentYear.id,
                        startDate: currentYear.yearStart,
                        endDate: currentYear.yearEnd
                    };
                    this.dlg.createLog("CompanyService","Selected date range information being set", dateUpdate);
                    this.updateSelectedDateRange(dateUpdate);
                }
            }else{                
                this.updateSelectedDateRange(null);
                this._companySummary.next(null);
                this._processingStatus.next(null);
                this._selectedAsset.next(null);
                this.dlg.createLog("CompanyService","Disposing of company information");
            }            
        }
    }

    async refreshCompanySummaryData(showSnack?: boolean){
        let snack;
        if(showSnack){
            snack = await this.showLoadingSnack("Refreshing company summary data...");
        }
        this.dlg.createLog("CompanyService","refreshing company summary data");
        const res1 = await this.api.getCompanyProcessingStatus(this.SelectedCompanyId);
        if(res1.result === ApiResultValues.Ok){
            this.dlg.createLog("CompanyService","Processing status received", res1.data);
            this._processingStatus.next(res1.data);
        }

        let res2: IApiResult ;

        if(showSnack){
            //This is a user invoked request so ask for the nominals to be refreshed too    
            res2 = await this.api.getCompanySummaryRefresh(this.SelectedCompanyId);
        }else{
            res2 = await this.api.getCompanySummary(this.SelectedCompanyId);
        }

        if(res2.result === ApiResultValues.Ok){
            this.dlg.createLog("CompanyService","Company summary data received", res2.data);
            this._companySummary.next(res2.data);
        }
        if(snack){
            this.closeLoadingSnack(snack);
        }
    }

    async migrateCompanyToV2(){
        const c = this._selectedCompany.value;

        if(c.companyVersion === 2){
            await this.dlg.showOk('Migration not required', 'Your company is already using version 2 of the nettTracker company structure, so no migration is required.');
            return;

        }

        const obs = new Observable<any>(o => {
            const asyncFunc = async() => {
                const form = this.fb.group({
                    enableAssetTaxDepreciation: [true],
                    enablePerpetualFinancialYearEnd: [true]
                });

                const group: IGroupContainer = {
                    title: 'Migrate Company to Version 2',
                    description: 'This company can now be upgraded to version 2 of nettTracker. The latest version of nettTracker includes support for tax based depreciation and perpetual financial year ends.  You can choose to enable these features as part of your migration.  If you have any questions about the migration or the new features, please check out the help section or contact support.',
                    dialogMode: true,
                    collapseGroupNav: true,
                    actionText: 'Start Migration',
                    data: {},
                    form: form,
                    groups: [
                        {
                            header: 'Choose migration options',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Perpetual year ends',
                                    description: 'Perpetual financial year ends allow you to set your year end date once and have it automatically roll forward each year.  You can make postings in previous financial years as needed.  If the company is already awaiting year end, this feature will need to be activated once year end has been completed.',
                                    allowEdit: true,
                                    data: {},
                                    form: form,
                                    items: [
                                        {fieldName: 'enablePerpetualFinancialYearEnd', label: 'Enable perpetual financial year ends', control: 'checkbox' }
                                    ]
                                },{
                                    header: 'Asset tax depreciation',
                                    description: 'Enabling tax based depreciation allows nettTracker to calculate depreciation values for your assets based on tax rules applicable to your region.  This is in addition to the standard accounting depreciation calculations nettTracker performs.',
                                    allowEdit: true,
                                    data: {},
                                    form: form,
                                    items: [
                                        {fieldName: 'enableAssetTaxDepreciation', label: 'Enable asset tax depreciation', control: 'checkbox' }
                                    ]
                                }
                            ]
                        }],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {   
                        dRef.close();
                        o.complete();
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const {enableAssetTaxDepreciation, enablePerpetualFinancialYearEnd} = form.getRawValue();

                        const dlg = await this.dlg.showPleaseWait('Migrating company to version 2.  This may take a few minutes, please wait...');
                        const migResult = await this.api.migrateCompanyToV2(enableAssetTaxDepreciation, enablePerpetualFinancialYearEnd, this.SelectedCompanyId );
                        dlg.close();

                        if(migResult.result === ApiResultValues.Ok){
                            
                            const updatedCompany = migResult.data;
                            const companyList = this._companyList.value;
                            const currentCompany = companyList.find(x=>x.safeId === this.SelectedCompanyId);
                            const idx = companyList.indexOf(currentCompany);
                            companyList[idx]=updatedCompany;

                            this._companyList.next(companyList);
                            this._selectedCompany.next(updatedCompany);
                            
                            dRef.close();
                            await this.dlg.showOk('Migration complete', 'Your company has been successfully migrated to version 2 of the nettTracker company structure.  Please check out the help section for more information on the new features and changes we have made in this version.  If you have any questions regarding this migration, please contact support and do not process a period end until you are happy.');
                            
                            o.next(true);
                            o.complete();
                        }
                    }
                };

                this.matDlg.open(ModalEditorComponent, {
                    data: group,
                    ...dialogDefaultFullScreen
                });

            };
            asyncFunc();
        });
        return firstValueFrom(obs);
    }

    async userLoginStatusChanged(state: authLib.AuthenticationStatusDetail[]) {
        const newState = state[1];
        const prevState = state[0];

        const stateChanged = newState.isAuthenticated !== prevState.isAuthenticated;

        if(stateChanged){
            if (newState.isAuthenticated) {
                //Whenever we get this, we should load the company list.
                this.dlg.createLog("CompanyService","userLoginStatusChanged has been notified of new state:", state );
                await this.loadCompanyList();
            } else if(!newState.isAuthenticated) {
                //Whenever we get this, we should clear any storage etc
                this.dlg.createLog("CompanyService","userLoginStatusChanged to false so company service is handling it's logout requirements:", state );
                this.handleLogout(newState.reason);
            }
            this.serviceLoaded.next(newState.isAuthenticated);
        }

        
    }

    handleLogout(reason: authLib.AuthenticationStatusPurpose) {
        if (reason !== authLib.AuthenticationStatusPurpose.RefreshFail && reason !== authLib.AuthenticationStatusPurpose.Initialisation) {
            console.log("CompanyService handle logout reason is requiring the currentCompany to be removed");
        }
        
        localStorage.removeItem("companyDataStore");    
    }

    async loadCompanyList(): Promise<NtCompany[]> {
        const comps = await this.api.getUserCompanyList();

        const data = comps.data as authLib.ntAuthResult;
        this._companyList.next(data.companies);
        //this.rebuildCompanyList(data.companies);
        
        return data.companies;
    }

    async showLoadingSnack(message?: string) {
        if(message){
            return this.snack.open(message);
        }else{
            return this.snack.open("Please wait while we prepare your view...");
        }
    }

    closeLoadingSnack(snack: MatSnackBarRef<TextOnlySnackBar>, timeout?: number){
        if(timeout){
            setTimeout(() => {
                snack.dismiss();
            }, timeout);
        }else{
            setTimeout(() => {
                snack.dismiss();
            }, 500);
        }
    }

    get Companies() {
        return this._companyList.asObservable();
    }

    get SelectedCompany() {
        return this._selectedCompany.asObservable();
    }

    get CompanySummary() {
        return this._companySummary.asObservable();
    }

    get ProcessingStatus(){
        return this._processingStatus.asObservable();
    }

    private get SelectedCompanyId(): string {
        return this._routedCompanyId.value;
    }

    _selectedDateRange: BehaviorSubject<IUpdatedDateRange> = new BehaviorSubject(null);
    get SelectedDateRange(){
        return this._selectedDateRange.asObservable();
    }

    updateSelectedDateRange(update: IUpdatedDateRange){
        this._selectedDateRange.next(update);
    }

    async getApiExplorerRequest(request: string) {
        return await this.api.getApiExplorerRequest(this.SelectedCompanyId, request);
    }

    async createConnectRequest(provider: string, purpose: string){
        let companyId: string;
        if(purpose !== 'Connect'){
            companyId = this.SelectedCompanyId;
        }

        let requestObject: ProviderConnectRequest;
        
        if(provider === 'Standalone'){
            requestObject = {
                url: '',
                state: 'standalone-state',
                providerName: provider,
                purpose: purpose,
                companyId: companyId
            };

            localStorage.setItem(AppConfig.settings.oAuthCookieName, JSON.stringify(requestObject));
            const params: Params = {
                'state': 'standalone-state'
            };
            this.completeCompanyConnect(provider, params).subscribe();
        }else{
            const result = await this.api.createConnectionRequest(provider, purpose, companyId);
            if(result.result === ApiResultValues.Ok){
                const req = result.data;
                requestObject = {
                    url: req.url,
                    state: req.state,
                    providerName: provider,
                    purpose: purpose,
                    companyId: companyId
                };

                localStorage.setItem(AppConfig.settings.oAuthCookieName, JSON.stringify(requestObject));
                window.location.replace(req.url);
            }
        }
        
    }

    completeCompanyConnect(provider: string, params: Params){
        return new Observable<IConnectStatusProgress>(obs => {
            const asyncFunc = async(o: Subscriber<IConnectStatusProgress>) => {
                const status: IConnectStatusProgress = {
                    purpose: '',
                    state: 'Pending',
                    error: false,
                    message: ''
                };
                
                if(params['error']){
                    o.next({state: 'Error', error: true, message: 'Error has been returned by provider', purpose: ''});
                    o.complete();
                    return;
                }

                const state = params['state'];
                if(!state){
                    o.next({state: 'Error', error: true, message: 'No state parameter was returned by the provider', purpose: ''});
                    o.complete();
                    return;
                }

                const pendingConnect = await this.api.getConnectState();
                if(!pendingConnect){
                    o.next({state: 'Error', error: true, message: 'No pending connection request was found', purpose: ''});
                    o.complete();
                    return;
                }
            
                if(state !== pendingConnect.state){
                    o.next({state: 'Error', error: true, message: 'The reference for this connection returned by the account provider does not match the value nettTracker requires', purpose: ''});
                    o.complete();
                    return;
                }

                status.purpose = pendingConnect.purpose;
                o.next(status);

                const values = [];
                const keys = Object.keys(params);
                keys.forEach(key => {
                    const item = {};
                    item[key]=params[key];
                    values.push(item);
                });
                values.push({provider: provider});

                if(pendingConnect.purpose === 'Reconnect') {
                    status.state = 'Validating credentials';
                    o.next(status);
                    const reconnectResult = await this.completeCompanyReconnect(pendingConnect.companyId, values);
                    if (reconnectResult.result === ApiResultValues.Ok) {
                        status.state = 'Reconnection complete';
                        o.next(status);
                        this.router.navigate(['/company', pendingConnect.companyId, 'dashboard']);
                        o.complete();
                    } else {
                        status.error = true;                       
                        if (reconnectResult.message) {
                            status.message = reconnectResult.message;
                        } else {
                            status.message = 'An error occurred when reconnecting to your online accounts';
                        }
                        o.next(status);
                        o.complete();
                    }
                } else if(pendingConnect.purpose === 'Connect' || pendingConnect.purpose === 'ChangeProvider'){
                    status.state = 'Validating credentials';
                    o.next(status);
                    const connApiResult = await this.api.completeConnectionRequest(values);
                    if(connApiResult.result === ApiResultValues.Ok){
                        ///Can we just advance?  Yes if only one potential datasource
                        const sources = connApiResult.data as ExternalDataSource[];
                        const validSources = sources.filter(x => x.state !== 'ExistingCritical');
                        
                        if (validSources.length === 0) {
                            status.error = true;
                            status.message = "We were unable to identify a valid set of company accounts from the provider you connected to.";
                            o.next(status);
                        } else {
                            if (validSources.length === 1) {
                                if(pendingConnect.purpose === 'Connect'){
                                    status.state = "Creating new company";
                                    o.next(status);
                                    await this.createCompanyInternal(validSources[0], status, obs);
                                }else{
                                    status.state = "Preparing for provider change";
                                    status.datasource = validSources[0];
                                    o.next(status);
                                    this.api.saveSelectedDatasource(validSources[0]);
                                    this.router.navigate(['/company', pendingConnect.companyId, 'change-provider', 'review']);
                                }
                               
                            } else {
                                //More than one?  So we need to display a list in a dialog and handle result
                                status.state = "Multiple companies detected";
                                o.next(status);
                                const dsList = validSources.map(s => {
                                    return { key: s.externalReference, value: s.name };
                                });

                                const form = this.fb.group({
                                    selectedCompany: [[validSources[0].externalReference], Validators.required]
                                });

                                const groupContainer: IGroupContainer = {
                                    title: "Select a company from the list provided",
                                    description: "Multiple companies have been returned from your online accounting provider.  Please choose the company you would like to connect to",
                                    dialogMode: true,
                                    collapseGroupNav: true,
                                    data: validSources,
                                    form: form,
                                    groups: [
                                        {header: 'Group 1', id: '1001', sections:[
                                            {
                                                header: 'Available companies',
                                                description: 'Choose a company',
                                                allowEdit: false,
                                                data: validSources,
                                                form: form,
                                                items: [
                                                    {
                                                        label: 'Companies',
                                                        fieldName: 'selectedCompany',
                                                        control: 'listbox',
                                                        dropdownList: dsList
                                                    }
                                                ]
                                            }
                                        ]}
                                    ],
                                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                                        dRef.close();
                                        status.state = 'Cancelled';
                                        status.message = "User did not select a company from available list";
                                        o.next(status);
                                        o.complete();
                                    },
                                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                                        const {selectedCompany} = form.getRawValue();
                                        const selectedDs = validSources.find(x=>x.externalReference === selectedCompany[0]);
                                        if(pendingConnect.purpose === 'Connect'){
                                            status.state = "Creating new company";
                                            o.next(status);
                                            await this.createCompanyInternal(selectedDs, status, obs);
                                            dRef.close();
                                        }else{
                                            status.state = "Preparing for provider change";
                                            status.datasource = selectedDs;
                                            o.next(status);
                                            this.api.saveSelectedDatasource(selectedDs);
                                            this.router.navigate(['/company', pendingConnect.companyId, 'change-provider', 'review']);
                                        }
                                        o.complete();
                                    }
                                };

                                this.matDlg.open(ModalEditorComponent,{
                                    data: groupContainer,
                                    ...dialogDefaultFullScreen
                                });
                            }
                        }
                    }else{
                        status.error = true;
                        status.message = "No result was returned from the call to complete your connection";
                        o.next(status);
                        o.complete();
                    }
                }
            }
        
            asyncFunc(obs);
        });
        
    }

    private procStartValidate = (v: any): any => {
        const d = moment(v).startOf('month').clone();
        const vDate = moment(v);
        if (d.diff(vDate, 'days') !== 0) {
            return d;   //form.controls.processingStartDate.setValue(d);
        }else{
            return v;
        }
    }

    private yearEndValidate = (v: any): any => {
        const d = moment(v).endOf('month').startOf('day');
        const vDate = moment(v);
        if (d.diff(vDate, 'days') !== 0) {
            return d;
        }else{
            return v;
        }
    }

    async createCompanyStandalone(){
        //Use dialog to capture company name, currency, region and year end date
        const obs = new Observable<IConnectStatusProgress>(o => {
            const asyncFunc = async() => {
                const status: IConnectStatusProgress = {
                    purpose: '',
                    state: 'Pending',
                    error: false,
                    message: 'Requesting standalone company template'
                };
                const dsResult = await this.api.completeConnectionRequest([{provider: 'Standalone'}]);
                
                if(dsResult.result !== ApiResultValues.Ok){
                    status.error = true;
                    status.message = 'No result was returned from the call to create a standalone company template';
                    o.next(status);
                    o.complete();
                    return;
                }

                const sources = dsResult.data;
                const ds = sources[0];
                
                await this.createCompanyInternal(ds, status, o);


                
            }

            asyncFunc();
        })

        return await firstValueFrom(obs);
    }

    createCompany(ds: ExternalDataSource){
        return new Observable<IConnectStatusProgress>(o => {
            const asyncFunc = async(obs) => {
                const status: IConnectStatusProgress = {
                    purpose: '',
                    state: 'Pending',
                    error: false,
                    message: ''
                };

                obs.next(status);

                const result = await this.createCompanyInternal(ds, status, obs);
                obs.complete();
            };

            asyncFunc(o);
        });
    }

    async createCompanyInternal(ds: ExternalDataSource, status: IConnectStatusProgress, obs: Subscriber<IConnectStatusProgress>){
        const combinedModel = JSON.parse(localStorage.getItem("organisationServiceSelection"));
        status.state = "Get billing information";
        obs.next(status);

        const orgResult = await this.api.getBillingOrganisation(combinedModel.organisation.orgId);
        if(orgResult.result !== ApiResultValues.Ok){
            status.error = true;
            status.message = "Your billing information could not be retrieved, which is preventing creation of the new company";
            obs.next(status);
            return;
        }

        const org = orgResult.data;
        let comp: any;

        status.state = "Retrieving external company information";
        obs.next(status);

        const compRes = await this.api.getExternalCompany(ds);
        if(compRes.result !== ApiResultValues.Ok){
            status.error = true;
            status.message = "The external company could not be retrieved";
            obs.next(status);
            return;
        }

        comp = compRes.data.company;
        if(!comp.region){
            comp.region = org.region;
            if(!comp.region){
                comp.region = 'en-GB';
            }
            if(comp.region.indexOf('GB') >=0 ){
                comp.defaultCurrency = 'GBP';
            }else{
                comp.defaultCurrency = 'USD';
            }
        }
        
        if (compRes.data.status === 'ExistingCritical') {
            this.dlg.createLog('CreateCompany','The external company has been retrieved but is marked as ExistingCritical, and cannot therefore be used for a new nettTracker company');
            status.error = true;
            status.message = "There is already an active company that has connected to this external accounts company, which is not allowed.  Please retry and select another online company";
            obs.next(status);
            return;
        }

        const form = this.fb.group({
            companyVersion: [comp.companyVersion, [Validators.required]],
            companyName: [comp.companyName, [Validators.required]],
            processingStartDate: [comp.processingStartDate, Validators.required],
            financialYearEnd: [comp.financialYearEnd, Validators.required],
            defaultCurrency: [comp.defaultCurrency, [Validators.required]],
            perpetualFinancialYear: [comp.perpetualFinancialYear, Validators.required]
        });

        form.controls.processingStartDate.valueChanges.subscribe(v => {
            form.controls.financialYearEnd.updateValueAndValidity();
        });

        const groupItems: IItem[] = [
            {
                label: 'Name',
                fieldName: 'companyName',
                control: 'input'
            }
        ];

        if(this.auth.getCurrentUser.systemAdmin){
            groupItems.push({
                label: 'Version',
                fieldName: 'companyVersion',
                control: 'select',
                dropdownList: [
                    {key: 1, value: 'Version 1'},
                    {key: 2, value: 'Version 2'},
                ]
            });
        }

        const group: IGroupContainer = {
            title: 'Confirm New Company',
            description: 'Please confirm the name and dates that should apply to your new company.  Dates suggested are those we have read from your external accounting provider.',
            dialogMode: true,
            collapseGroupNav: true,
            data: {},
            form: form,
            groups: [
                {
                    header: 'Group1',
                    id: '1001',
                    sections: [
                        {
                            header: 'Company name',
                            description: 'Name of your standalone company',
                            allowEdit: false,
                            data: {},
                            form: form,
                            items: groupItems
                        },
                        {
                            header: 'Currency',
                            description: 'Select the operating currency',
                            allowEdit: false,
                            data: {},
                            form: form,
                            items: [
                                {
                                    label: 'Currency',
                                    fieldName: 'defaultCurrency',
                                    control: 'select',
                                    dropdownList: GetDefaultCurrencyList()
                                }
                            ]
                        },
                        {
                            header: 'Dates',
                            description: 'Enter the financial year start and end dates for the new company',
                            allowEdit: false,
                            data: {},
                            form: form,
                            items: [
                                { label: 'Processing start date', fieldName: 'processingStartDate', control: 'datepicker', controlDataType: comp.defaultCurrency, validatorFn: this.procStartValidate},
                                { label: 'Year-end date', fieldName:'financialYearEnd', control: 'datepicker', controlDataType: comp.defaultCurrency, validatorFn: this.yearEndValidate}
                            ]
                        },
                        {
                            header: 'Advanced settings',
                            description: 'Advanced asset settings',
                            allowEdit: false,
                            data: {},
                            form: form,
                            items: [
                                { label: 'Perpetual financial year', fieldName: 'perpetualFinancialYear', control: 'checkbox'}
                            ]
                        }
                    ]
                }
            ],
            cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                status.error = true;
                status.message = "You have cancelled the new company process.";
                obs.next(status);
                obs.complete();
                dRef.close();
            },
            completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                const {processingStartDate, financialYearEnd, companyName, defaultCurrency, companyVersion, perpetualFinancialYear} = form.getRawValue();
                comp.processingStartDate = processingStartDate;
                comp.financialYearEnd = financialYearEnd;
                comp.companyName = companyName;
                comp.defaultCurrency = defaultCurrency;
                comp.companyVersion = companyVersion;
                comp.perpetualFinancialYear = perpetualFinancialYear;
                const users = org.users.filter(x => x.userId !== this.auth.getCurrentUser.id)
                    .map(u => {
                        return {
                            userId: u.userId,
                            email: u.email,
                            role: 'Owner',
                            name: u.userName
                        }
                    });

                const newCompanyModel = {
                    newCompany: comp,
                    billingOrganisationId: combinedModel.organisation.orgId,
                    service: combinedModel.service,
                    users: users
                };

                if(ds.dataSource !== 'Standalone'){
                    dRef.close();
                }

                status.state = "Posting new company data...";
                obs.next(status);

                const newCompanyResult = await this.api.createCompany(newCompanyModel);
                this.api.clearConnectRequest();

                if(newCompanyResult.result === ApiResultValues.Ok){
                    const newCompanyObj = newCompanyResult.data;
                    if(newCompanyObj.result === 'Rejected'){
                        obs.next({
                            state: 'Duplicate company detected',
                            message: newCompanyResult.message,
                            error: true,
                            purpose: status.purpose
                        });
                        obs.complete();
                        return;
                    }
                    const newCompany = newCompanyObj.company;

                    const companiesOnSameSubscription = this._companyList.value.filter(x=>x.subscribedServiceId === newCompany.subscribedServiceId);
                    companiesOnSameSubscription.forEach(c => {
                        c.subscribedService = newCompany.subscribedService;
                    });
                    const companies = this._companyList.value;
                    companies.unshift(newCompany);
                    this._companyList.next(companies);

                    if(ds.dataSource !== 'Standalone'){
                        status.state = "Synchronising nominal accounts";
                        obs.next(status);

                        const extRes = await this.api.getNominalAccountsExternal(newCompany.safeId);

                        status.state = "Refreshing transactions for current year";
                        obs.next(status);

                        const transRes = await this.api.getTransactionsExternal(newCompany.safeId);
                        status.state = "ALL DONE!! We're just going to activate your new company...";
                        obs.next(status);
                        setTimeout(() => {
                            this.router.navigate(['company', newCompany.safeId, 'settings']);
                        }, 2000);                    
                    }else{
                        dRef.close();
                        this.router.navigate(['company', newCompany.safeId, 'settings']);
                    }

                    
                }else{
                    status.error = true;
                    status.message = "It was not possible to create your new company: " + newCompanyResult.message;
                    obs.next(status);
                }
            }
        }

        this.help.findTooltips(group);

        this.matDlg.open(ModalEditorComponent,
            {
                data: group,
                ...dialogDefaultFullScreen
            }
        )
    }

    createCompanyWithData = async(newCompanyModel: any, status: IConnectStatusProgress, obs: Subscriber<IConnectStatusProgress>, dataSource: any) => {
        
        const newCompanyResult = await this.api.createCompany(newCompanyModel);
        this.api.clearConnectRequest();

        if(newCompanyResult.result === ApiResultValues.Ok){
            const newCompanyObj = newCompanyResult.data;
            const newCompany = newCompanyObj.company;

            const companies = this._companyList.value ? this._companyList.value : [];

            const companiesOnSameSubscription = companies.filter(x=>x.subscribedServiceId === newCompany.subscribedServiceId);
            companiesOnSameSubscription.forEach(c => {
                c.subscribedService = newCompany.subscribedService;
            });
            companies.unshift(newCompany);
            this._companyList.next(companies);

            if(dataSource !== 'Standalone'){
                status.state = "Synchronising nominal accounts";
                obs.next(status);

                const extRes = await this.api.getNominalAccountsExternal(newCompany.safeId);

                status.state = "Refreshing transactions for current year";
                obs.next(status);

                const transRes = await this.api.getTransactionsExternal(newCompany.safeId);
                status.state = "ALL DONE!! We're just going to activate your new company...";
                obs.next(status);
                setTimeout(() => {
                    this.router.navigate(['company', newCompany.safeId, 'settings']);
                }, 2000);                    
            }else{
                this.router.navigate(['company', newCompany.safeId, 'settings']);
            }

            
        }else{
            status.error = true;
            status.message = "It was not possible to create your new company: " + newCompanyResult.message;
            obs.next(status);
        }

        return newCompanyResult;
    }

    async requestCompanyReconnect(){
        const provider = this._selectedCompany.value.externalDataSource.dataSource;
        const result = await this.api.createReconnectionRequest(this.SelectedCompanyId, provider);
        if(result.result === ApiResultValues.Ok){
            const req = result.data;
            const requestObject: ProviderConnectRequest = {
                url: req.url,
                state: req.state,
                purpose: 'Reconnect',
                providerName: provider,
                companyId: this.SelectedCompanyId
            };
            localStorage.setItem(AppConfig.settings.oAuthCookieName, JSON.stringify(requestObject));
            window.location.replace(req.url);   
        }
    }

    async completeCompanyReconnect(companyId: string, values: any[]) {
        return this.api.completeReconnectionRequest(companyId, values);
    }

    async changeCompanyProvider(dataSource: ExternalDataSource, newRegion: string){
        const result = await this.api.changeCompanyProvider(this.SelectedCompanyId, dataSource, newRegion);

        if(result.result === ApiResultValues.Ok){
            const list = this._companyList.value;
            const item = list.find(x=>x.safeId === this.SelectedCompanyId);
            if(list){
                const idx = list.indexOf(item);
                if(idx > -1){
                    list[idx]=result.data;
                    this._companyList.next(list);
                }
            }

            this._selectedCompany.next(result.data);
        }

        return result;
    }

    changeCompanyProviderStartAgain()
    {
        this.router.navigate(['/company', this.SelectedCompanyId, 'change-provider', 'choose']);
    }

    async changeFinancialYear(){
        const obs = new Observable<any>(o => {
            const asyncFunc = async() => {
                const snack = await this.showLoadingSnack();
                const company = this._selectedCompany.value;
                const years = await this.getFinancialYears();
                const currentYear = years.find(x=>!x.closed);

                const minDate = moment(company.lastProcessingDate).toDate();
                const maxDate = moment(currentYear.yearStart).add('month', 18).subtract('days',1).toDate();

                const form = this.fb.group({
                    endOfYearDate: [moment(company.financialYearEnd), Validators.required]
                });

                const group: IGroupContainer = {
                    title: 'Change financial year end date',
                    description: 'This tool will allow you to change the financial year end date for the current year.  Please note that the year-end date must be the last day of a month, and must be at least one month after your current processing date.',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: company,
                    form: form,
                    groups: [
                        {
                            header: 'group1',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Change year-end date',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        {
                                            label: 'New year-end',
                                            control: 'datepicker',
                                            fieldName: 'endOfYearDate',
                                            minDate: minDate,
                                            maxDate: maxDate,
                                            validatorFn: (v: any) => {
                                                const d = moment(v).endOf('month').startOf('day');
                                                const vDate = moment(v);
                                                return d;
                                            }
                                        }
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const {endOfYearDate} = form.getRawValue() as any;
                        const preflight = await this.api.modifyFinancialYear(endOfYearDate, true, this.SelectedCompanyId);

                        if(preflight.result === ApiResultValues.Ok){
                            const preflightData = preflight.data;
                            if(preflightData.result === 'Accepted'){
                                const formattedDate = moment(endOfYearDate).format(company.region);
                                const confirmRes = await this.dlg.showYesNo('Change financial year-end date', `The requested year end date of ${formattedDate} has been accepted.  Please confirm that you wish to proceed with this change`);
                                if(confirmRes === DialogBoxResults.Yes){
                                    const result = await this.api.modifyFinancialYear(endOfYearDate, false, this.SelectedCompanyId);
                                    if(result.result === ApiResultValues.Ok){
                                        const updatedCompany = result.data.updatedCompany;
                                        this.dlg.showOk('Change financial year-end date', 'The company financial year has now been changed.');
                                        const comps = this._companyList.value;
                                        const idx = comps.indexOf(company);
                                        comps[idx]= updatedCompany;
                                        this._companyList.next(comps);
                                        this._selectedCompany.next(updatedCompany);
                                        o.next(updatedCompany);
                                        dRef.close();
                                    }
                                }
                            }else{
                                this.dlg.showOk('Change financial year-end date', `Your request has been rejected:\n  ${preflightData.message}.  \n\nPlease adjust the dates and try again.`);
                            }
                            
                        }
                    }
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );

                this.closeLoadingSnack(snack);
            }

            asyncFunc();
        });

        return firstValueFrom(obs);
    }

    async getCurrentCompanyConnectionStatus(): Promise<any> {
        const result = await this.api.getConnectionStatus(this.SelectedCompanyId);
        if(result.result === ApiResultValues.Ok){
            return result.data;
        }else{
            return null;
        }
    }

    async updateCompany(data: any){
        const result = await this.api.updateCompany(data, this.SelectedCompanyId);
        if(result.result === ApiResultValues.Ok){
            //Replace company in the list
            this.updateInternalCompany(result.data);
            return result.data;
        }
    }

    async enableFeature(featureKey: string){
        const dlg = await this.dlg.showPleaseWait("Please wait while we enable this feature on your company");

        const res = await this.api.enableCompanyFeature(featureKey, this.SelectedCompanyId);
        dlg.close();

        if(res.result === ApiResultValues.Ok){
            this.updateInternalCompany(res.data);

        }

        return res.data;
    }

    private updateInternalCompany(c: any){
        const list = this._companyList.value;
            const item = list.find(x=>x.safeId === this.SelectedCompanyId);
            if(list){
                const idx = list.indexOf(item);
                if(idx > -1){
                    list[idx]= c;
                    this._companyList.next(list);
                }
            }

            this._selectedCompany.next(c);
    }

    async disconnectCompany(companyId: string): Promise<any>{
        const obs = new Observable<any>(o => {
            const asyncFunc = async() => {

                const form = this.fb.group({
                    confirm: [false, [Validators.requiredTrue]]
                });

                if(!companyId){
                    companyId = this.SelectedCompanyId;
                }
                
                const company = this._selectedCompany.value;

                const group: IGroupContainer = {
                    title: 'Disconnect company',
                    description: 'This tool will disconnect your nettTracker company from the online accounts provider that it is current connected to.',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: company,
                    form: form,
                    groups: [
                        {
                            header: 'group1',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Confirm disconnect',
                                    description: 'Disconnecting will prevent nettTracker from operating correctly.  It will no longer be able to post journal entries nor retrieve new asset transactions.  It will not delete this company, nor affect your billing.',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        {
                                            label: 'Confirm disconnect',
                                            fieldName: 'confirm',
                                            control: 'checkbox'
                                        }
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const apiRes = await this.api.disconnectCompany(companyId);
                        if(apiRes.result === ApiResultValues.Ok){
                            o.next(apiRes.data);
                            dRef.close();
                        }
                    }
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
            }
            asyncFunc();
        })

        return await firstValueFrom(obs);
    }

    async createCompanyUser(): Promise<IApiResult>{
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const c = this._selectedCompany.value;
                //start by loading the organisation
                const orgRes = await this.api.getBillingOrganisation(c.billingOrganisationId);
                if(orgRes.result !== ApiResultValues.Ok){
                    this.dlg.showOk('Retrieving billing organisation', 'There was an error retrieving the billing organisation and user list for this company.');
                    o.next(null);
                    return;
                }

                const org = orgRes.data;

                const users: any[] = [];
                org.users.forEach(u => {
                    const ntu = c.users.find(x=>x.userId === u.userId);
                    if(!ntu){
                        users.push(u);
                    }
                });

                if(users.length === 0){
                    this.dlg.showOk('No available organsiation users', 'There are no users on this organisation that are not allocated to this company.  Please create new users for your organisation first.');
                    o.next(null);
                    return;
                }

                const form = this.fb.group({
                    role: 'Standard',
                    user: '',
                    email: '',
                    name: ''
                });

                const section: ISection = {
                    header: 'Create user role',
                    description: 'Select the new company user and their role',
                    form: form,
                    allowEdit: false,
                    data: {},
                    items: [
                        {label: 'User name', fieldName: 'user', control: 'user', dropdownList: [] },
                        {
                            label: 'Role',
                            fieldName: 'role',
                            control: 'select',
                            dropdownList: [
                                { key: 'Standard', value: 'Standard'},
                                { key: 'Owner', value: 'Owner'}
                            ]
                        }
                    ]
                };

                const group: IGroupContainer = {
                    title: 'Create company user',
                    description: 'Please select the user and their role to add to the company',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: {},
                    form: form,
                    groups: [
                        {
                            header: 'group1',
                            id: '1001',
                            sections: [section]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const { user } = form.getRawValue();
                        const apiRes =  await this.api.createNtUser(user, this.SelectedCompanyId);

                        if(apiRes.result === ApiResultValues.Ok){
                            const c = this._selectedCompany.value;
                            c.users.push();
                            this._selectedCompany.next(c);
                            o.next(apiRes);

                            dRef.close();
                        }
                    }
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );

            }

            asyncF();
        });

        return await firstValueFrom(obs);
    
    }

    async updateCompanyUser(u: any): Promise<IApiResult>{
        const obs = new Observable<any>(o => {
            const asyncFunc = async() => {
                const form = this.fb.group({
                    role: [u.role, Validators.required],
                    name: [ u.name, Validators.required],
                    email: [ u.email, Validators.required],
                    userId: [u.userId, Validators.required]

                });

                const section = {
                    header: 'Update user role',
                    description:  'Choose from the available roles',
                    form: form,
                    allowEdit: false,
                    data: u,
                    items: [
                        {
                            label: 'Name',
                            fieldName: 'name',
                            control: 'input',
                            preventEdit: true
                        },{
                            label: 'Email',
                            fieldName: 'email',
                            control: 'input',
                            preventEdit: true
                        },{
                            label: 'Role',
                            fieldName: 'role',
                            control: 'select',
                            dropdownList: [
                                { key: 'Standard', value: 'Standard'},
                                { key: 'Owner', value: 'Owner'}
                            ]
                        }
                    ]
                };
        
                const group: IGroupContainer = {
                    title:  'Update company user role',
                    description: 'Please select the new role that should apply to this user',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: u,
                    form: form,
                    groups: [
                        {
                            header: 'group1',
                            id:'1001',
                            sections: [section]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const { role } = form.getRawValue();
                        const apiRes =  await this.api.updateNtUserRole(u.userId, role, this.SelectedCompanyId);

                        if(apiRes.result === ApiResultValues.Ok){
                            const c = this._selectedCompany.value;
                            const idx = c.users.indexOf(u);
                            c.users[idx]= apiRes.data;
                            this._selectedCompany.next(c);
                            o.next(apiRes);

                            dRef.close();
                        }
                    }
                };

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
            }

            asyncFunc();
        });

        return await firstValueFrom(obs);
    }

    async removeCompanyUser(u: any): Promise<IApiResult>{
        const confirm = await this.dlg.showConfirmDelete();
        if(confirm === DialogBoxResults.Yes){
            const apiRes = await this.api.removeNtUser(u.userId, this.SelectedCompanyId);
            if(apiRes.result === ApiResultValues.Ok){
                const c = this._selectedCompany.value;
                const idx = c.users.indexOf(u);
                c.users.splice(idx, 1);
                this._selectedCompany.next(c);
            }
            return apiRes;
        }else{
            return {
                result: ApiResultValues.ParameterError,
                message: 'Action cancelled',
                data: {}
            };
        }
    }

    //Get lists should just return the api response.  Errors will be displayed by
    //api layer.  The component decides what to display.  This is just a proxy
    //  which in time could also implement caching.
    async getAssetGroups(): Promise<any[]>{
        const api = await this.api.getAssetGroups(this.SelectedCompanyId);
        // if(api.result === ApiResultValues.Ok){
        //     return api.data;
        // }else{
        //     return []
        // }
        return api.data;
    }

    getNominalAccountTypeAheadUpdateFunction(g: IGroupContainer)  {
        return async() => {
            const updatedAccounts = await this.getNominalAccountsExternal();
            updatedAccounts.unshift({id: 0, name: '- please select an account'});
            updatedAccounts.unshift({id: -1, name: 'Create new nominal account for mapping'});
            g.groups.forEach(group => {
                group.sections.forEach(section => {
                    section.items.forEach(item => {
                        if(item.updateMethod && item.control.startsWith('type-ahead')){
                            item.updateMethod(updatedAccounts);
                        }
                    });
                });
            });
        };
    }


    async updateAssetGroup(id: number,): Promise<any> {
        const obs = new Observable<any>(o => {
            const asyncFunc = async() =>{
                const snack = await this.showLoadingSnack();
                const company = this._selectedCompany.value;

                const accounts = await this.getNominalAccounts();
                accounts.unshift({id: 0, name: '- please select an account'});
                accounts.unshift({id: -1, name: 'Create new nominal account for mapping'});

                const depRules = await this.getDepreciationRulesUI();
                let group;
                if(id > 0){
                    const data = await this.api.getAssetGroup(id, this.SelectedCompanyId);
                    group = data.data
                }else{
                    let defRule =depRules.find(x=>x.default);
                    if(!defRule){
                        defRule = depRules[0];
                    }
                    group = {
                        depreciationRuleId: defRule.id,
                        mappings: []
                    };
                }

                const mappableGroupGetMapping = (group: any, purpose: string) => {
                    const lcPurpose = purpose.toLowerCase();
                    const mappingValue =  group.mappings.find(x=>x.purpose.toLowerCase() === lcPurpose);
                    if(mappingValue){
                        return mappingValue.nominalAccountId;
                    }else{
                        return 0;
                    }
                }
                    
                const form = this.fb.group({
                    depreciationRuleId: [group.depreciationRuleId, Validators.required],
                    accountGroupName: [group.accountGroupName, Validators.required],
                    notes: group.notes,
                    useSameBalanceSheetAccountsForDisposals: company.useSameBalanceSheetAccountsForDisposals,
                    mappings: this.fb.group({
                        transactions: [mappableGroupGetMapping(group, 'transactions'), NotValueValidatorDirective.notValueValidatorFactory(0)],
                        depreciationInYear: [mappableGroupGetMapping(group, 'depreciationInYear'), NotValueValidatorDirective.notValueValidatorFactory(0)],
                        depreciationExpenses: [mappableGroupGetMapping(group, 'depreciationExpenses'), NotValueValidatorDirective.notValueValidatorFactory(0)],
                        disposalsCost: [mappableGroupGetMapping(group, 'disposalsCost'), NotValueValidatorDirective.notValueValidatorFactory(0)],
                        disposalsDep:[mappableGroupGetMapping(group, 'disposalsDep'), NotValueValidatorDirective.notValueValidatorFactory(0)],
                        disposalsPnl: [mappableGroupGetMapping(group, 'disposalsPnl'), NotValueValidatorDirective.notValueValidatorFactory(0)],
                        costBf: [mappableGroupGetMapping(group, 'costBf'), NotValueValidatorDirective.notValueValidatorFactory(0)],
                        depreciationBf: [mappableGroupGetMapping(group, 'depreciationBf'), NotValueValidatorDirective.notValueValidatorFactory(0)]
                    })
                });

                let subTrans: Subscription;
                let subDepInYr: Subscription;
                let subDepEx: Subscription;
                const m = form.controls.mappings;
                m.controls.depreciationExpenses.valueChanges.subscribe(v => {
                    m.controls.disposalsPnl.setValue(v, {emitEvent: false});
                    m.controls.disposalsPnl.markAsDirty();
                    m.updateValueAndValidity();
                })

                const updateBalanceSheetControls = (v: boolean) => {
                    const m = form.controls.mappings;
                    if(v){
                        m.controls.disposalsCost.disable();
                        m.controls.disposalsDep.disable();
                        //m.controls.disposalsPnl.disable();
                        if(m.controls.disposalsCost.value !== m.controls.transactions.value){
                            m.controls.disposalsCost.setValue(m.controls.transactions.value);
                            m.controls.disposalsCost.markAsDirty();
                        }
                        
                        if(m.controls.disposalsDep.value !== m.controls.depreciationInYear.value){
                            m.controls.disposalsDep.setValue(m.controls.depreciationInYear.value);
                            m.controls.disposalsDep.markAsDirty();
                        }
                        
                        if(m.controls.disposalsPnl.value !== m.controls.depreciationExpenses.value){
                            m.controls.disposalsPnl.setValue(m.controls.depreciationExpenses.value);
                            m.controls.disposalsPnl.markAsDirty();
                        }

                        subTrans = m.controls.transactions.valueChanges.subscribe(v => {
                            m.controls.disposalsCost.setValue(v, {emitEvent: false});
                            m.controls.disposalsCost.markAsDirty();
                            m.updateValueAndValidity();
                        });
                        subDepInYr = m.controls.depreciationInYear.valueChanges.subscribe(v => {
                            m.controls.disposalsDep.setValue(v, {emitEvent: false});
                            m.controls.disposalsDep.markAsDirty();
                            m.updateValueAndValidity();
                        })
                        
                        
                    }else{
                        m.controls.disposalsCost.enable();
                        m.controls.disposalsDep.enable();
                        //m.controls.disposalsPnl.enable();
                        m.controls.disposalsCost.setValue(mappableGroupGetMapping(group, 'disposalsCost'));
                        m.controls.disposalsDep.setValue(mappableGroupGetMapping(group, 'disposalsDep'));
                        m.controls.disposalsPnl.setValue(mappableGroupGetMapping(group, 'disposalsPnl'));
                        m.controls.disposalsCost.markAsDirty();
                        m.controls.disposalsDep.markAsDirty();
                        m.controls.disposalsPnl.markAsDirty();

                        if(subTrans){
                            subTrans.unsubscribe();
                        }
                        if(subDepEx){
                            subDepEx.unsubscribe();
                        }
                        // if(subDepInYr){
                        //     subDepEx.unsubscribe();
                        // }
                    }
                }
                //Do we need any form rules?  Yes, toggling 'useSame' needs to set and reset values
                form.controls.useSameBalanceSheetAccountsForDisposals.valueChanges.subscribe(v=> {
                    updateBalanceSheetControls(v);
                });

                updateBalanceSheetControls(company.useSameBalanceSheetAccountsForDisposals);

                //Year-end enabled?
                if(!company.useYearEndJournals){
                    form.controls.mappings.controls.costBf.disable();
                    form.controls.mappings.controls.costBf.clearValidators()
                    form.controls.mappings.controls.depreciationBf.disable();
                    form.controls.mappings.controls.depreciationBf.clearValidators();
                }

                if(id === 0){
                    form.controls.depreciationRuleId.markAsDirty();
                }
                
                const groupContainer: IGroupContainer = {
                    title: id === 0 ? 'Create new asset group and account mappings' : 'Update asset group',
                    description: 'nettTracker uses Asset Groups to detect asset purchases and post the required journal entries for depreciation and asset disposals.',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: group,
                    form: form,
                    groups: [
                        { header: 'Group 1', id: '1001', sections: []}
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const update = {};
                        getFormDirtyValues(form, update);
                        if(Object.keys(update).length > 0){
                            const apiResult = await this.api.updateAssetGroup(id, update, this.SelectedCompanyId);
                            if(apiResult.result === ApiResultValues.Ok){
                                dRef.close();
                                o.next(apiResult.data);
                            }else{

                            }
                            
                        }else{
                            dRef.close();
                            o.next(null);
                        }
                    }
                };

                const updateFunc = this.getNominalAccountTypeAheadUpdateFunction(groupContainer);

                let section: ISection = {
                    header: 'Asset Group Details',
                    description: '',
                    allowEdit: false,
                    data: group,
                    form: form,
                    items: [
                        {label: 'Asset group name', fieldName: 'accountGroupName', control: 'input'},
                        {label: 'Default depreciation rule for group', fieldName: 'depreciationRuleId', control: 'select', dropdownList: depRules},
                        {label: 'Entries for additions/disposal made to the same balance sheet account', fieldName: 'useSameBalanceSheetAccountsForDisposals', control: 'checkbox'},
                        {label: 'notes', fieldName: 'notes', control: 'input'}
                    ]
                };
                groupContainer.groups[0].sections.push(section);

                section = {
                    header: 'General account mappings',
                    description: '',
                    data: group,
                    allowEdit: false,
                    form: form.controls.mappings,
                    items: [
                        {label: 'Balance sheet - cost', fieldName: 'transactions', control: 'type-ahead-nominal', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                        {label: "Balance sheet - dep'n", fieldName: 'depreciationInYear', control: 'type-ahead-nominal', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                        {label: "P&L - dep'n expense", fieldName: 'depreciationExpenses', control: 'type-ahead-nominal', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                    ]
                };
                groupContainer.groups[0].sections.push(section);

                section = {
                    header: 'Disposal account mappings',
                    description: '',
                    data: group,
                    allowEdit: false,
                    form: form.controls.mappings,
                    items: [
                        {label: 'Balance sheet - cost of disposal', fieldName: 'disposalsCost', control: 'type-ahead-nominal', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                        {label: "Balance sheet - dep'n on disposal", fieldName: 'disposalsDep', control: 'type-ahead-nominal', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                        {label: 'P&L - gain/loss on diposal', fieldName: 'disposalsPnl', control: 'type-ahead-nominal', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                    ]
                };
                groupContainer.groups[0].sections.push(section);

                if(company.useYearEndJournals){
                    section = {
                        header: 'Accumulated totals',
                        description: '',
                        data: group,
                        allowEdit: false,
                        form: form.controls.mappings,
                        items: [
                            {label: 'Brought forward cost', fieldName: 'costBf', control: 'type-ahead-nominal', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                            {label: 'Brought forward depreciation', fieldName: 'depreciationBf', control: 'type-ahead-nominal', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                        ]
                    };
                    groupContainer.groups[0].sections.push(section);
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: groupContainer,
                        ...dialogDefaultFullScreen
                    }
                );
                
                this.closeLoadingSnack(snack);
            }
            asyncFunc();
        });

        return await firstValueFrom(obs);
    }

    async getAccrualPrepaymentGroups(): Promise<any[]>{
        const api = await this.api.getAccrualPrepaymentGroups(this.SelectedCompanyId);
        return api.data;
    }

    async updateAccrualPrepaymentGroup(id: number): Promise<any>{
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const snack = await this.showLoadingSnack();

                const company = this._selectedCompany.value;
                const accounts = await this.getNominalAccounts();
                accounts.unshift({id: 0, name: '- please select an account'});
                accounts.unshift({id: -1, name: 'Create new nominal account for mapping'});

                let group;
                if(id > 0){
                    const data = await this.api.getAccrualPrepaymentGroup(id, this.SelectedCompanyId);
                    group = data.data;
                }else{
                    group = {
                        mappings: []
                    };
                }

                const mappableGroupGetMapping = (group: any, purpose: string) => {
                    const lcPurpose = purpose.toLowerCase();
                    const mappingValue =  group.mappings.find(x=>x.purpose.toLowerCase() === lcPurpose);
                    if(mappingValue){
                        return mappingValue.nominalAccountId;
                    }else{
                        return 0;
                    }
                }

                const typeList: IKeyValue[] = [
                    {key: 'AccruedExpense', value: 'Accrued expense'},
                    {key: 'AccruedIncome', value: 'Accrued income'},
                    {key: 'PrepaidExpense', value: 'Prepaid expenses'},
                    {key: 'PrepaidIncome', value: 'Deferred income'}
                ];

                const form = this.fb.group({
                    groupName: [group.groupName, Validators.required],
                    type: [group.type, [Validators.required]],
                    notes: group.notes,
                    mappings: this.fb.group({
                        accruedExpenseBS: [mappableGroupGetMapping(group, 'accruedExpenseBS'), [NotValueValidatorDirective.notValueValidatorFactory(0)]],
                        accruedIncomeBS: [mappableGroupGetMapping(group, 'accruedIncomeBS'), [NotValueValidatorDirective.notValueValidatorFactory(0)]],
                        prepaidIncomeBS: [mappableGroupGetMapping(group, 'prepaidIncomeBS'), [NotValueValidatorDirective.notValueValidatorFactory(0)]],
                        prepaidExpenseBS: [mappableGroupGetMapping(group, 'prepaidExpenseBS'), [NotValueValidatorDirective.notValueValidatorFactory(0)]],
                    })
                });

                if(id == 0){
                    form.controls.type.markAsDirty();
                }

                const groupContainer: IGroupContainer = {
                    title: id === 0 ? 'New accrual/prepayment group' : 'Update accrual/prepayment group',
                    description: 'nettTracker uses Accrual and Preprayment groups to post the required journal entries.',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: group,
                    form: form,
                    groups: [{ header: 'Group 1', id: '1001', sections: [
                        {
                            header: 'General information',
                            description: '',
                            data: group,
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Group name', control: 'input', fieldName: 'groupName'},
                                {label: 'Group type', control: 'select', fieldName: 'type', dropdownList: typeList},
                                {label: 'Notes', control: 'richtext', fieldName: 'notes'}
                            ]
                        }
                    ]}],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const update = {};
                        getFormDirtyValues(form, update);
                        if(Object.keys(update).length > 0){
                            const apiRes = await this.api.updateAccrualPrepaymentGroup(id, update, this.SelectedCompanyId);
                            if(apiRes.result === ApiResultValues.Ok){
                                dRef.close();
                                o.next(apiRes.data);
                            }
                        }
                    }
                };

                const updateFunc = this.getNominalAccountTypeAheadUpdateFunction(groupContainer);

                const containerGroup = groupContainer.groups[0];

                const setupMappingsForm = (v: any) => {
                    if(containerGroup.sections.length > 1){
                        containerGroup.sections.pop();
                    }
                    
                    const mapG = form.controls.mappings as FormGroup;
                    if(v === 'AccruedExpense'){
                        mapG.controls["accruedIncomeBS"].markAsPristine();
                        mapG.controls["prepaidExpenseBS"].markAsPristine();
                        mapG.controls["prepaidIncomeBS"].markAsPristine();
                        mapG.controls["accruedIncomeBS"].setValue(0, {emitEvent: false});
                        mapG.controls["prepaidExpenseBS"].setValue(0, {emitEvent: false});
                        mapG.controls["prepaidIncomeBS"].setValue(0, {emitEvent: false});
                        mapG.controls["accruedIncomeBS"].disable();
                        mapG.controls["prepaidExpenseBS"].disable();
                        mapG.controls["prepaidIncomeBS"].disable();
                        mapG.controls["accruedExpenseBS"].enable();
                        containerGroup.sections.push({
                            header: 'Accrued expense mapping',
                            description: '',
                            data: group,
                            allowEdit: false,
                            form: form.controls.mappings,
                            items: [
                                {label: 'Balance sheet account', fieldName: 'accruedExpenseBS', control: 'type-ahead-nominal', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                            ]
                        });
                    }else if(v === 'AccruedIncome'){
                        mapG.controls["accruedExpenseBS"].markAsPristine();
                        mapG.controls["prepaidExpenseBS"].markAsPristine();
                        mapG.controls["prepaidIncomeBS"].markAsPristine();
                        mapG.controls["accruedExpenseBS"].setValue(0, {emitEvent: false});
                        mapG.controls["prepaidExpenseBS"].setValue(0, {emitEvent: false});
                        mapG.controls["prepaidIncomeBS"].setValue(0, {emitEvent: false});
                        mapG.controls["accruedExpenseBS"].disable();
                        mapG.controls["prepaidExpenseBS"].disable();
                        mapG.controls["prepaidIncomeBS"].disable();
                        mapG.controls["accruedIncomeBS"].enable();
                        containerGroup.sections.push({
                            header: 'Accrued income mapping',
                            description: '',
                            data: group,
                            allowEdit: false,
                            form: form.controls.mappings,
                            items: [
                                {label: 'Balance sheet account', fieldName: 'accruedIncomeBS', control: 'type-ahead-nominal', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                            ]
                        });
                    }else if(v === 'PrepaidExpense'){
                        mapG.controls["accruedExpenseBS"].markAsPristine();
                        mapG.controls["accruedIncomeBS"].markAsPristine();
                        mapG.controls["prepaidIncomeBS"].markAsPristine();
                        mapG.controls["accruedExpenseBS"].setValue(0, {emitEvent: false});
                        mapG.controls["accruedIncomeBS"].setValue(0, {emitEvent: false});
                        mapG.controls["prepaidIncomeBS"].setValue(0, {emitEvent: false});
                        mapG.controls["accruedExpenseBS"].disable();
                        mapG.controls["accruedIncomeBS"].disable();
                        mapG.controls["prepaidIncomeBS"].disable();
                        mapG.controls["prepaidExpenseBS"].enable();
                        containerGroup.sections.push({
                            header: 'Prepaid expense mapping',
                            description: '',
                            data: group,
                            allowEdit: false,
                            form: form.controls.mappings,
                            items: [
                                {label: 'Balance sheet account', fieldName: 'prepaidExpenseBS', control: 'type-ahead-nominal', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                            ]
                        });
                    }else if(v === 'PrepaidIncome'){
                        mapG.controls["accruedExpenseBS"].markAsPristine();
                        mapG.controls["accruedIncomeBS"].markAsPristine();
                        mapG.controls["prepaidExpenseBS"].markAsPristine();
                        mapG.controls["accruedExpenseBS"].setValue(0, {emitEvent: false});
                        mapG.controls["accruedIncomeBS"].setValue(0, {emitEvent: false});
                        mapG.controls["prepaidExpenseBS"].setValue(0, {emitEvent: false});
                        mapG.controls["accruedExpenseBS"].disable();
                        mapG.controls["accruedIncomeBS"].disable();
                        mapG.controls["prepaidExpenseBS"].disable();
                        mapG.controls["prepaidIncomeBS"].enable();
                        containerGroup.sections.push({
                            header: 'Deferred incoming mapping',
                            description: '',
                            data: group,
                            allowEdit: false,
                            form: form.controls.mappings,
                            items: [
                                {label: 'Balance sheet account', fieldName: 'prepaidIncomeBS', control: 'type-ahead-nominal', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                            ]
                        })
                    }
                }
                form.controls.type.valueChanges.subscribe(v => {
                    //We need to update the displayed section with the range accordingly
                    setupMappingsForm(v);
                });

                if(id > 0){
                    setupMappingsForm(group.type);
                }
                
                this.matDlg.open(ModalEditorComponent,
                    {
                        data: groupContainer,
                        ...dialogDefaultFullScreen
                    }
                );
                this.closeLoadingSnack(snack);
            }

            asyncF();
        });

        return await firstValueFrom(obs);
    }

    async requestCompanyDeletion(){
        const obs = new Observable<any>(o => {
            const asyncFunc = async() => {
                const company = this._selectedCompany.value;
                const form = this.fb.group({
                    companyName: ['', [Validators.required, CompareValueValidator.compareValueValidatorFactory('companyNameMatch')]],
                    companyNameMatch: [company.companyName]
                });
                const overThirtyDaysMsg = 'Requesting a company deletion will suspend access to this company and lock for a period of 30-days.  After this time, the company database will be permanently deleted and unrecoverable.  During this 30-day period, the deletion can be reversed by selecting the company from the Company List.';
                const lessThirtyDaysMsg = 'This company was created less than 30 days ago and will therefore be deleted immediately.  The database will be permanently deleted and unrecoverable.  PLEASE NOTE: this process will not end your subscription.  If you wish to end your subsription, please contact us through our Help and Support menu.';

                const isOverThirtyDays = moment().diff(company.createdOn, 'days') > 30;
                const groupContainer: IGroupContainer = {
                    title: 'Company Deletion',
                    description: isOverThirtyDays ? overThirtyDaysMsg : lessThirtyDaysMsg,
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: company,
                    form: form,
                    groups: [
                        { 
                            header: 'Group1', 
                            id: '1001', 
                            sections: [
                                {
                                    header: 'Confirm deletion of company',
                                    description: 'Are you SURE that you wish to delete this company?  To confirm deletion, please retype the name of the company precisely',
                                    allowEdit: false,
                                    data: company,
                                    form: form,
                                    items: [
                                        {
                                            label: 'Company name',
                                            fieldName: 'companyName',
                                            control: 'input'
                                        }
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const apiRes = await this.api.scheduleCompanyDeletion(this.SelectedCompanyId);
                        if(apiRes.result === ApiResultValues.Ok){
                            const comps = this._companyList.value;
                            const idx = comps.indexOf(company);

                            if(apiRes.data.deleted){
                                comps.splice(idx, 1);
                                this.dlg.showOk('Delete company', 
                                    `Your company has been successfully deleted`
                            );
                            }else{
                                company.pendingDeletion = apiRes.data.pendingDeletion;
                                company.pendingDeletionDate = apiRes.data.pendingDeletionDate;

                                comps[idx] = company;
                                this.dlg.showOk('Delete company', 
                                        `Your company has been scheduled for deletion on ${formatDate(company.pendingDeletionDate)}`
                                );
                            }

                            this._companyList.next(comps);
                            this._selectedCompany.next(null);
                            this._routedCompanyId.next('');
                            this.router.navigate(['/company']);
                            
                            dRef.close();
                            o.next(company);
                        }
                    }
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: groupContainer,
                        ...dialogDefaultFullScreen
                    }
                );
            }

            asyncFunc();
        });

        return await firstValueFrom(obs);
    }

    ///This method is private.  It should only be invoked when the company service is prompted to select a company via routing;
    private async cancelCompanyDeletion(c: NtCompany): Promise<boolean>{
        if(!c.pendingDeletion){
            return true;
        }

        const confirmResult = await this.dlg.showYesNo('Cancel pending company deletion', 
                `This company is scheduled for deletion on ${formatDate(c.pendingDeletionDate)}.  Do you wish to cancel this pending deletion?`);

        if(confirmResult === DialogBoxResults.Yes){
            const apiResult = await this.api.cancelCompanyDeletion(c.safeId);
            if(apiResult.result === ApiResultValues.Ok){
                const comps = this._companyList.value;
                const idx = comps.indexOf(c);
                c.pendingDeletion = false;
                c.pendingDeletionDate = null;
                comps[idx] = c;
                this._companyList.next(comps);
                await this.dlg.showOk('Cancel pending company deletion',
                        'Your company has been restored for use.');
                return true;
            }else{
                return false;
            }
        }else{
            return false;
        }
    }

    async requestCompanyReset(){
        const obs = new Observable<any>(o => {
            const asyncFunc = async() => {
                const company = this._selectedCompany.value;
                const maxDate = moment(company.processingStartDate).add('month', 18).subtract('days', 1).toDate();
                const minDate = moment(company.processingStartDate).add('month', 1).subtract('days', 1).toDate();

                const form = this.fb.group({
                    mode: ['', Validators.required],
                    yearStart: [company.processingStartDate, Validators.required],
                    yearEnd: [moment(company.processingStartDate).add('year', 1).subtract('days', 1).toDate(), Validators.required],
                    confirmText: ['', [Validators.required, CompareValueValidator.compareValueValidatorFactory('confirmTextMatch')]],
                    confirmTextMatch: ['I confirm this reset']
                });

                form.controls.yearStart.valueChanges.subscribe(v => {
                    //Align to last date
                    const d = moment(v).startOf('month');
                    const vDate = moment(v);
                    if(d !== vDate){
                        form.controls.yearStart.setValue(d.toDate(), {emitEvent: false});
                    }
                    
                    form.controls.yearEnd.updateValueAndValidity();
    
                });
    
                form.controls.yearEnd.valueChanges.subscribe(v => {   
                    const d = moment(v).endOf('month').startOf('day');
                    const vDate = moment(v);
                    if (d.diff(vDate, 'days') > 0) {
                        form.controls.yearEnd.setValue(d.toDate(), { emitEvent: false })
                    }
                });

                const groupContainer: IGroupContainer = {
                    title: 'Reset company',
                    description: 'Use this tool to reset your company and remove data, perhaps after you have explored and experimented with how nettTracker works.',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: company,
                    form: form,
                    groups: [
                        {
                            header: 'Group1',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Reset mode',
                                    description: 'Companies can be fully reset (removing groups and mapping information) or a data reset (removing assets, accruals etc).  Please choose the reset mode, along with financial year information',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        { label: 'Mode', 
                                            control: 'select',
                                            fieldName: 'mode',
                                            dropdownList:[
                                                { key: 'ItemData', value: 'Assets and accrual items plus associated journal entries'},
                                                { key: 'AllData', value: 'All item data PLUS all groups and their mappings'}
                                            ]
                                        }
                                    ]
                                },
                                {
                                    header: 'Financial year dates',
                                    description: 'Please enter the new processing start and year-end dates.',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        {
                                            label: 'Processing start date',
                                            control: 'datepicker',
                                            fieldName: 'yearStart'
                                        },
                                        {
                                            label: 'Financial year end',
                                            control: 'datepicker',
                                            fieldName: 'yearEnd'
                                        }
                                    ]
                                },
                                {
                                    header: 'Confirm the reset',
                                    description: 'Please confirm the reset by entering the text "I confirm this reset"',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        {
                                            label: 'Enter confirm text',
                                            fieldName: 'confirmText',
                                            control: 'input'
                                        }
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        
                        const {mode, yearStart, yearEnd } = form.getRawValue();
                        const apiResult = await this.api.resetCompany(yearStart, yearEnd, company.defaultCurrency, mode, this.SelectedCompanyId);
                        if(apiResult.result === ApiResultValues.Ok){
                            this.dlg.showOk('Reset company', 'The company has now been reset successfully.');
                            const comps = this._companyList.value;
                            const idx = comps.indexOf(company);
                            comps[idx]=apiResult.data;
                            this._companyList.next(comps);
                            this._selectedCompany.next(apiResult.data);
                            o.next(apiResult.data);
                            dRef.close();
                        }
                    }
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: groupContainer,
                        ...dialogDefaultFullScreen
                    }
                )
            }

            asyncFunc();
        });

        return await firstValueFrom(obs);
    }

    financialYears: Observable<any> | null = null;
    getFinancialYears(): Promise<any[]> {
        if(!this.financialYears){
            this.financialYears = from(this.api.getFinancialYears(this.SelectedCompanyId)).pipe(
                map(api => {
                    if(api.result === ApiResultValues.Ok){
                        return api.data;
                    }else{
                        return null;
                    }
                }),
                filter(x => x !== null), 
                shareReplay(1)
            );
        }
        return firstValueFrom(this.financialYears);
    }


    async getCurrentFinancialYear(): Promise<any> {
        const value = await this.getFinancialYears();
        if(value){
            const curyear = value.find(x => !x.closed);
            return curyear;
        }
    }

    async getFinancialYear(id: number): Promise<any>{
       const value = await this.getFinancialYears();
        if(value){
            const curyear = value.find(x => x.id === id);
            return curyear;
        }
    }

    private depRulesUICache: IKeyValue[] = [];
    async getDepreciationRulesUI(): Promise<any[]> {
        if(this.depRulesUICache.length === 0){
            await this.getDepreciationRules();
        }

        return this.depRulesUICache;
    }

    async getDepreciationRules(): Promise<any[]> {
        const api = await this.api.getDepreciationRules(this.SelectedCompanyId);
        if(api.result === ApiResultValues.Ok){
            this.depRulesUICache = api.data.map(r => {
                return {key: r.id, value: r.ruleName}
            });
        }
        return api.data;
    }

    async getDepreciationRule(id: number): Promise<any>{
        const api = await this.api.getDepreciationRule(id, this.SelectedCompanyId);
        return api.data;
    }

    async updateDepreciationRule(id: number): Promise<any>{
        const obs =  new Observable<any>(o => {
            const asyncFunc = async() => {
                const snack = await this.showLoadingSnack();
                let rule;
                if(id > 0){
                    const data = await this.api.getDepreciationRule(id, this.SelectedCompanyId);
                    rule = data.data;
                }else{
                    rule = {
                        ruleName: '',
                        depreciationType: 'StraightLine',
                        percentageReduction: 25,
                        yearstoApply: 3
                    };
                }

                const form = this.fb.group({
                    ruleName: [rule.ruleName, Validators.required],
                    depreciationType: [rule.depreciationType, Validators.required],
                    percentageReduction: [rule.percentageReduction],
                    yearsToApply: [rule.yearsToApply]
                });

                const dropDownListData: {yearData: IKeyValue[], monthData: IKeyValue[], types: IKeyValue[]} = {
                    yearData: [],
                    monthData: [],
                    types: [{key: 'StraightLine', value:'Straight Line (Years)'},
                        {key: 'StraightLineMonths', value: 'Straight Line (Months)'},
                        {key: 'ReducingBalance', value: 'Reducing Balance'},
                        {key: 'DoubleDecline', value: "'Double' Decline"},
                        {key: '150Decline', value: '150% Declining Balance'}
                    ]
                }
                const yearList: IKeyValue[] = [];
                for(let x = 1; x < 101 ;x++){
                    yearList.push({
                        key: x,
                        value: `${x} year${x === 1 ? '' : 's'}`
                    });
                }
                dropDownListData.yearData = yearList;
                const monthList: IKeyValue[] = [];
                for(let x = 1; x < 360 ;x++){
                    monthList.push({
                        key: x,
                        value: `${x} month${x === 1 ? '' : 's'}`
                    });
                }
                dropDownListData.monthData = monthList;

                const applyRules = (v: any) => {
                    if(v === 'ReducingBalance'){
                        form.controls.yearsToApply.disable();
                        form.controls.yearsToApply.clearValidators();
                        form.controls.percentageReduction.enable();
                        form.controls.percentageReduction.addValidators([Validators.required, Validators.min(1), Validators.max(100)]);
                        form.controls.yearsToApply.updateValueAndValidity();
                        form.controls.percentageReduction.updateValueAndValidity();
                    }else if(v === 'DoubleDecline' || v === '150Decline'){    
                        form.controls.yearsToApply.enable();
                        form.controls.yearsToApply.addValidators([Validators.required, Validators.min(1)]);
                        // form.controls.percentageReduction.enable();
                        // form.controls.percentageReduction.addValidators([Validators.required, Validators.min(1)]);
                        let red = v === 'DoubleDecline' ? 2.0 : 1.5;
                        form.controls.percentageReduction.setValue(red);
                        form.controls.yearsToApply.updateValueAndValidity();
                        form.controls.percentageReduction.updateValueAndValidity();
                    }else if(v === 'StraightLine'){
                        form.controls.yearsToApply.enable();
                        form.controls.yearsToApply.addValidators([Validators.required, Validators.min(1)]);
                        form.controls.percentageReduction.disable();
                        form.controls.percentageReduction.clearValidators();
                        form.controls.yearsToApply.updateValueAndValidity();
                        form.controls.percentageReduction.updateValueAndValidity();                        
                        let iitem = section.items[3];
                        const newItem = {
                            ...iitem,
                            dropdownList: yearList
                        };
                        if(iitem.updateMethod){
                            iitem?.updateMethod(newItem);
                        }
                    }else if(v === 'StraightLineMonths'){
                        form.controls.yearsToApply.enable();
                        form.controls.yearsToApply.addValidators([Validators.required, Validators.min(1)]);
                        form.controls.percentageReduction.disable();
                        form.controls.percentageReduction.clearValidators();
                        form.controls.yearsToApply.updateValueAndValidity();
                        form.controls.percentageReduction.updateValueAndValidity();
                        let iitem = section.items[3];
                        const newItem = {
                            ...iitem,
                            dropdownList: monthList
                        };
                        if(iitem.updateMethod){
                            iitem?.updateMethod(newItem);
                        }
                    }
                }
                //setup validation rules
                form.controls.depreciationType.valueChanges.subscribe(v => {
                    applyRules(v);
                });

                if(id === 0){
                    form.controls.depreciationType.markAsDirty();
                }

                //buildFormFromSection(roleSection, user.role, form.controls['role']);
                const section :ISection = {
                    header: 'Depreciation rule',
                    allowEdit: false,
                    data: rule,
                    form: form,
                    items: [
                        {label: 'Rule name', fieldName: 'ruleName', control: 'input'},
                        {label: 'Type of depreciation', fieldName: 'depreciationType', control:'select', dropdownList: dropDownListData.types },
                        {label: 'Percentage reduction per year', fieldName: 'percentageReduction', control: 'input', controlDataType:'number'},
                        {label: 'Years/Months to apply', fieldName: 'yearsToApply', control: 'select', controlDataType: 'number', 
                            dropdownList: rule.depreciationType === 'StraightLine' ? dropDownListData.yearData : rule.depreciationType === 'StraightLineMonths' ? dropDownListData.monthData : []}
                    ]
                };
                const config: IGroupContainer = {
                    title: 'Update depreciation rule',
                    description: 'Depreciation rule',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: rule,
                    form: form,
                    groups: [
                        {header: 'Group 1', id: '1001', sections: [section]}
                    ],
                    cancelAction: (dialogRef: MatDialogRef<ModalEditorComponent>) => {
                        dialogRef.close();
                        o.next(null);
                    },
                    completeAction: async(dialogRef: MatDialogRef<ModalEditorComponent>) => {
                        const update = {};
                        getFormDirtyValues(form, update);
                        if(Object.keys(update).length > 0){
                            if(update['depreciationType'] === '150Decline'){
                                update['depreciationType'] = 'DoubleDecline';
                                update['percentageReduction'] = 1.5;
                            }
                            const api = await this.api.updateDepreciationRule(id, update, this.SelectedCompanyId);
                            if(api.result === ApiResultValues.Ok){
                                this.depRulesUICache = [];
                                o.next(api.data);
                                dialogRef.close();
                            }
                        }else{
                            dialogRef.close();
                            o.next(null);
                        }
                        
                    }
                }

                applyRules(rule.depreciationType);

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: config,
                        ...dialogDefaultFullScreen
                    }
                );
                this.closeLoadingSnack(snack);
            };
            asyncFunc();
        });
     
        return await firstValueFrom(obs);
    }

    async updateDepreciationRuleDefault(r: any): Promise<any>{
        const confirmRes = await this.dlg.showYesNo('Set rule as default', `Are you sure you want to set rule '${r.ruleName}' as default?` );

        if(confirmRes === DialogBoxResults.Yes){
            const api = await this.api.updateDepreciationRuleSetDefault(r.id, this.SelectedCompanyId);
            if(api.result === ApiResultValues.Ok){
                return true;
            }else{
                return false;
            }
        }
    }

    async deleteDepreciationRule(r:any ): Promise<any>{
        const confirmRes = await this.dlg.showYesNo('Set rule as default', `Are you sure you want to set rule '${r.ruleName}' as default?` );

        if(confirmRes === DialogBoxResults.Yes){
            const api = await this.api.deleteDepreciationRule(r.id, this.SelectedCompanyId);
            if(api.result === ApiResultValues.Ok){
                return true;
            }else{
                return false;
            }
        }
    }

    async getNominalAccounts(): Promise<any[]>{
        const api = await this.api.getNominalAccounts(this.SelectedCompanyId);
        return api.data;
    }

    async getNominalAccountsExternal(): Promise<any[]> {
        const pleaseWait = await this.dlg.showPleaseWait('We are retrieving the nominal account information from your selected accounting software, which may take a few moments to completed');
        try{
            const api = await this.api.getNominalAccountsExternal(this.SelectedCompanyId);
            pleaseWait.close();
            if(api.result === ApiResultValues.Ok){
                return api.data;
            }
            
        }catch{
            pleaseWait.close();
        }
        return null;
    }

    async updateNominalAccount(id: number): Promise<any>{
        const obs = new Observable<any>(o => {
            const asyncFunc = async() => {
                const snack = await this.showLoadingSnack();
                let account;
                if(id > 0){
                    const data = await this.api.getNominalAccount(id, this.SelectedCompanyId);
                    account = data.data;
                }else{  
                    account = {
                        id:0
                    };
                }

                const form = this.fb.group({
                    name: [account.name, Validators.required],
                    description: [account.description],
                    category: [account.category],
                    accountGeneralPurpose: [account.accountGeneralPurpose, Validators.required],
                    creditNominalAccountRef: [account.creditNominalAccountRef],
                    debitNominalAccountRef: [account.debitNominalAccountRef],
                });

                const group: IGroupContainer = {
                    title: account.id > 0 ? 'Update nominal account' : 'Create nominal account',
                    description: 'Nominal accounts are used to record financial transactions.  Please enter the details of the nominal account you wish to create or update.',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: account,
                    form: form,
                    groups: [
                        {
                            header: 'Group 1',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Nominal account details',
                                    description: 'Please enter the details of the nominal account you wish to create or update.',
                                    allowEdit: false,
                                    data: account,
                                    form: form,
                                    items: [
                                        {label: 'Name', fieldName: 'name', control: 'input'},
                                        {label: 'Description', fieldName: 'description', control: 'input'},
                                        {label: 'Category', fieldName: 'category', control: 'input'},
                                        {label: 'General purpose account', fieldName: 'accountGeneralPurpose', control: 'select', dropdownList: [
                                            {key: 'FIXED', value: 'Fixed asset'},
                                            {key: 'EXPENSE', value: 'Expense'},
                                            {key: 'INCOME', value: 'Income'},
                                            {key: 'ASSETS', value: 'Other Assets'},
                                            {key: 'LIABILITIES', value: 'Liabilities'},
                                        ]}
                                        
                                    ]
                                },{
                                    header: 'External accounting software references',
                                    description: 'Please enter the details of the external accounting software references.',
                                    allowEdit: false,
                                    data: account,
                                    form: form,
                                    items: [
                                        {label: 'Credit nominal account reference', fieldName: 'creditNominalAccountRef', control: 'input'},
                                        {label: 'Debit nominal account reference', fieldName: 'debitNominalAccountRef', control: 'input'}
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {    
                        const update = {id: account.id};
                        getFormDirtyValues(form, update);
                        if(Object.keys(update).length > 0){
                            const api = await this.api.updateNominalAccount(id, update, this.SelectedCompanyId);
                            if(api.result === ApiResultValues.Ok){
                                o.next(api.data);
                                dRef.close();
                            }
                        }else{
                            dRef.close();
                            o.next(null);
                        }
                    }
                };

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );

                this.closeLoadingSnack(snack);
            };

            asyncFunc();
        });
        return await firstValueFrom(obs);
    }

    async deleteNominalAccount(id: number): Promise<any>{
        const confirmRes = await this.dlg.showYesNo('Delete nominal account', `Are you sure you want to delete this nominal account?` );

        if(confirmRes === DialogBoxResults.Yes){
            const api = await this.api.deleteNominalAccount(id, this.SelectedCompanyId);
            if(api.result === ApiResultValues.Ok){
                return true;
            }else{
                return false;
            }
        }
    }

    async getAssetRegister(groupId: number,
        yearId: number,
        startDate: string,
        endDate: string
    ): Promise<IApiResult>{
        const api = await this.api.getAssetRegister(this.SelectedCompanyId, groupId, yearId, startDate, endDate);
        
        return api;
    }

    async getAssetRegisterAssets(yearId: number,
                startDate: string,
                endDate: string,
                groupId: number,
                pageIndex: number,
                pageSize: number,
                search?: string,
                sortDesc?: boolean,
                sortItem?: string): Promise<IApiResult>{
        const api = await this.api.getAssetRegisterAssets(this.SelectedCompanyId,
            groupId,
            pageIndex,
            pageSize,
            yearId,
            startDate,
            endDate,
            search,
            sortDesc,
            sortItem
        );
        
        return api;
    }

    async downloadAssetRegister(){
        const snack = await this.showLoadingSnack();
        const yearsList = await this.getFinancialYears();
        const dateInfo = this._selectedDateRange.value;
        const c = this._selectedCompany.value;
        const region = c.region ? ( c.region.toLowerCase() === 'en-gb' ? 'DD/MM/YYYY' : 'MM/DD/YYYY' ): (c.defaultCurrency.toLowerCase() === 'usd' || c.defaultCurrency.toLowerCase() === 'cad') ? 'MM/DD/YYYY' : 'DD/MM/YYYY' ;


        const form = this.fb.group({
            startDate: [moment(dateInfo.startDate)],
            endDate: [moment(dateInfo.endDate)],
            yearId: [dateInfo.yearId.toString()]
        });

        form.controls.yearId.valueChanges.subscribe(v => {
            const year = yearsList.find(x=>x.id === parseInt(v));
            form.controls.startDate.setValue(moment(year.yearStart), {emitEvent: false});
            form.controls.endDate.setValue(moment(year.yearEnd), {emitEvent: false});
        })

        const years = yearsList.map((y):IKeyValue => {
            const ys = moment(y.yearStart).format(region);
            const ye = moment(y.yearEnd).format(region);
            const v: IKeyValue = {
                key: y.id.toString(),
                value: `${ys} - ${ye}`
            }
            return v;
        });

        
        const obs = new Observable<any>(o => {
            const formItems: IItem[] = [
                {label: 'Financial year', control: 'select', fieldName: 'yearId', dropdownList: years }                
            ]

            if(c.companyVersion > 1){
                formItems.push({label: 'Start date', control: 'datepicker', fieldName: 'startDate'});
                formItems.push({label: 'End date', control: 'datepicker', fieldName: 'endDate'});
            }

            const group: IGroupContainer = {
                title: 'Asset Register Report',
                actionText: 'Export',
                description: 'Please confirm the date range to use on your asset register report',
                showCompanyStatus: true,
                company: c,
                dialogMode: true,
                collapseGroupNav: true,
                data: dateInfo,
                form: form,
                groups: [
                    {
                        header: 'group',
                        id: '1',
                        sections: [
                            {
                                header: 'Select date range',
                                description: 'Please choose a financial year or custom date range for your report',
                                form: form,
                                allowEdit: false,
                                items: formItems
                            }
                        ]
                    }
                ],
                cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                    dRef.close();
                    o.next(null);
                },
                completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                    const {startDate, endDate, yearId} = form.getRawValue();
                    let sd;
                    let ed;
                    if(startDate.format) {
                        sd = startDate.format('YYYY-MM-DD');
                    }else{
                        sd = startDate;
                    }
                    if(endDate.format) {
                        ed = endDate.format('YYYY-MM-DD');
                    }else{
                        ed = endDate;
                    }
                    if(!sd || !ed){
                        dRef.close();
                        o.next(false);
                    }else{
                        const dlg = await this.dlg.showPleaseWait('Please wait while we prepare your asset register report');
                        this.api.downloadAssetRegister(this.SelectedCompanyId, sd, ed, parseInt(yearId)).subscribe(result =>{
                            dlg.close();
                            if(result){
                                this.dlg.showOk('Asset Register Report', `Your asset register report has now been downloaded`);
                            }
                            o.next(result);
                            dRef.close();
                        });
                    }
                    
                }
            }
            
            this.matDlg.open(ModalEditorComponent,
                {
                    data: group,
                    ...dialogDefaultFullScreen
                }
            );
            this.closeLoadingSnack(snack);
        });

        return await firstValueFrom(obs);
    }

    async getAssetsByState(state: string, pageIndex: number, pageSize: number, searchTerm: string, sortItem: string, sortDesc: boolean): Promise<IApiQueryResult>{
        const api = await this.api.getAssetsByState(state, pageIndex, pageSize, searchTerm, sortItem, sortDesc, this.SelectedCompanyId);
        return api;
    }

    async getProjectionColumns(yearId: number, startDate: string, endDate: string, isTax: boolean = false){
        const api = await this.api.getProjectionColumns(yearId, startDate, endDate, this.SelectedCompanyId, isTax);
        return api;
    }

    async getAssetDepreciationAssets(yearId: number, startDate: string, endDate: string, groupId: number, pageIndex: number, pageSize: number, searchTerm: string, sortItem: string = null, sortDesc: boolean = false, isTax: boolean = false): Promise<IApiResult>{
        const api = await this.api.getFinancialYearDepreciationAssets(yearId, startDate, endDate, groupId, pageIndex, pageSize, searchTerm, this.SelectedCompanyId, sortItem, sortDesc, isTax);
        return api;
    }

    
    async requestFinancialYearDepreciation(isTax: boolean){
        const snack = await this.showLoadingSnack();
        const dateInfo = this._selectedDateRange.value;
        const yearId = dateInfo.yearId;
        const yearsList = await this.getFinancialYears();
        const form = this.fb.group({
            startDate: [moment(dateInfo.startDate)],
            endDate: [moment(dateInfo.endDate)],
            yearId: [dateInfo.yearId.toString()]
        });

        const c = this._selectedCompany.value;
        const region = c.region ? ( c.region.toLowerCase() === 'en-gb' ? 'DD/MM/YYYY' : 'MM/DD/YYYY' ): (c.defaultCurrency.toLowerCase() === 'usd' || c.defaultCurrency.toLowerCase() === 'cad') ? 'MM/DD/YYYY' : 'DD/MM/YYYY' ;

        const years = yearsList.map((y):IKeyValue => {
            const ys = moment(y.yearStart).format(region);
            const ye = moment(y.yearEnd).format(region);
            const v: IKeyValue = {
                key: y.id.toString(),
                value: `${ys} - ${ye}`
            }
            return v;
        });

        const obs = new Observable<any>(o => {
            const asyncFunc = async() => {
                const formItems: IItem[] = [
                    {label: 'Financial year', control: 'select', fieldName: 'yearId', dropdownList: years }                
                ]
    
                if(c.companyVersion > 1){
                    formItems.push({label: 'Start date', control: 'datepicker', fieldName: 'startDate'});
                    formItems.push({label: 'End date', control: 'datepicker', fieldName: 'endDate'});
                }else{
                    isTax = false;
                }

                const group: IGroupContainer = {
                    title: isTax ? 'Asset Tax Depreciation Report' : 'Asset Depreciation Report',
                    actionText: 'Export',
                    description: `Please confirm the date range to use on your asset ${isTax ? 'tax ' : ''}depreciation report`,
                    showCompanyStatus: true,
                    company: c,
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: dateInfo,
                    form: form,
                    groups: [
                        {
                            header: 'group',
                            id: '1',
                            sections: [
                                {
                                    header: 'Select date range',
                                    description: 'Please choose a financial year or custom date range for your report',
                                    form: form,
                                    allowEdit: false,
                                    items: formItems
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const {yearId, startDate, endDate} = form.getRawValue();
                        const yearIdNum = parseInt(yearId);
                        let sd;
                        let ed;
                        if(startDate.format) {
                            sd = startDate.format('YYYY-MM-DD');
                        }else{
                            sd = startDate;
                        }
                        if(endDate.format) {
                            ed = endDate.format('YYYY-MM-DD');
                        }else{
                            ed = endDate;
                        }
                        if(c.companyVersion === 1){    
                            const statusResult = await this.api.getFinancialYearDepreciationReportStatus(yearIdNum, this.SelectedCompanyId);
                            const outputType = statusResult.data.outputType;

                            if(outputType === 'Offline'){
                                const result = await this.api.requestFinancialYearDeprecationReport(yearIdNum, this.SelectedCompanyId)
                                this.dlg.showOk('Deprecation Projection Report', `Your depreciation projection report has been requested and will be delivered to ${result.data.destination} when it has been generated.`);
                                o.next(true);
                                dRef.close();
                            }else{
                                const dlg = await this.dlg.showPleaseWait('Please wait while we prepare your projection report');
                                this.api.downloadFinancialYearDepreciationReport(yearIdNum, null, null, this.SelectedCompanyId, false).subscribe(result =>{
                                    dlg.close();
                                    this.dlg.showOk('Depreciation Projection Report', `Your depreciation projection report has now been downloaded`);
                                    o.next(result);
                                    dRef.close();
                                });
                            }  
                        }else{
                            const dlg = await this.dlg.showPleaseWait('Please wait while we prepare your projection report');
                            this.api.downloadFinancialYearDepreciationReport(yearIdNum, sd, ed, this.SelectedCompanyId, isTax).subscribe(result =>{
                                dlg.close();
                                this.dlg.showOk('Depreciation Projection Report', `Your depreciation projection report has now been downloaded`);
                                o.next(result);
                                dRef.close();
                            });
                        }    
                    }
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
                this.closeLoadingSnack(snack);
            }

            asyncFunc();
        });

        return await firstValueFrom(obs);
    }




    async getTransactions(feature: string,
        state: string,
        pageIndex: number,
        pageSize: number,
        startDate: string,
        endDate: string)
    {
        console.log("Companyservice getTransactions called", feature, state, pageIndex, pageSize, startDate, endDate);
        const api = await this.api.getTransactions(this.SelectedCompanyId,
            feature, 
            state,
            pageIndex,
            pageSize,
            startDate,
            endDate);

        return api;
    }

    private _transactionsRefreshed = new Subject<boolean>();
    get TransactionsRefreshed() {
        return this._transactionsRefreshed.asObservable();
    }

    async getTransactionsExternal(): Promise<IApiResult> {
        if(this._selectedCompany.value.companyVersion === 2){
            const snack = await this.showLoadingSnack();
            const yearsList = await this.getFinancialYears();
            const dateInfo = this._selectedDateRange.value;
            const form = this.fb.group({
                year: [dateInfo.yearId.toString(), [Validators.required]]
            });

            const c = this._selectedCompany.value;
            const region = c.region ? ( c.region.toLowerCase() === 'en-gb' ? 'DD/MM/YYYY' : 'MM/DD/YYYY' ): (c.defaultCurrency.toLowerCase() === 'usd' || c.defaultCurrency.toLowerCase() === 'cad') ? 'MM/DD/YYYY' : 'DD/YY/YYYY' ;

            const years = yearsList.map((y):IKeyValue => {
                const ys = moment(y.yearStart).format(region);
                const ye = moment(y.yearEnd).format(region);
                const v: IKeyValue = {
                    key: y.id.toString(),
                    value: `${ys} - ${ye}`
                }
                return v;
            });
            
            const obs = new Observable<any>(o => {
                const formItems: IItem[] = [
                    {label: 'Financial year', control: 'select', fieldName: 'year', dropdownList: years }                
                ]
                const group: IGroupContainer = {
                    title: 'Transaction Refresh',
                    actionText: 'Refresh',
                    description: 'Please confirm the financial year to refresh transaction data',
                    showCompanyStatus: true,
                    company: c,
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: dateInfo,
                    form: form,
                    groups: [
                        {
                            header: 'group',
                            id: '1',
                            sections: [
                                {
                                    header: 'Select financial year',
                                    description: 'Please choose a financial year',
                                    form: form,
                                    allowEdit: false,
                                    items: formItems
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const {year} = form.getRawValue();
                        const yearId = parseInt(year);
                        const pleaseWait = await this.dlg.showPleaseWait('We are retrieving transaction data from your selected accounting software, which may take a few moments to completed');
                        try{
                            const api = await this.api.getTransactionsExternalV2(this.SelectedCompanyId, yearId);
                            pleaseWait.close();
                            if(api.result === ApiResultValues.Ok){
                                if(api.count > 0){
                                    this._transactionsRefreshed.next(true);        
                                    const c = this._selectedCompany.value;
                                    c.lastTransactionRefresh = new Date();
                                    this._selectedCompany.next(c);
                                    this.dlg.showOk('Transaction refresh', `We have now completed retrieving updated transaction data from your selected accounting software.`);
                                }

                                dRef.close();
                                o.next(api);
                            }
                            
                            return api;
                            
                        }catch{
                            pleaseWait.close();
                            return null;
                        }
                        
                    }
                }
                
                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
                this.closeLoadingSnack(snack);
            });
            return await firstValueFrom(obs);
        }else{
            const pleaseWait = await this.dlg.showPleaseWait('We are retrieving transaction data from your selected accounting software, which may take a few moments to completed');
            try{
                const api = await this.api.getTransactionsExternal(this.SelectedCompanyId);
                pleaseWait.close();
                if(api.result === ApiResultValues.Ok){
                    if(api.count > 0){
                        this._transactionsRefreshed.next(true);        
                        const c = this._selectedCompany.value;
                        c.lastTransactionRefresh = new Date();
                        this._selectedCompany.next(c);
                        this.dlg.showOk('Transaction refresh', `We have now completed retrieving updated transaction data from your selected accounting software.`);
                    }
                }
                
                return api;
                
            }catch{
                pleaseWait.close();
            }
            return null;
        }
        
    }

    //Selected asset can be used to hold the asset object until it is needed by UI
    private _selectedAsset = new BehaviorSubject<Asset | null>(null);
    get SelectedAsset() {
        return this._selectedAsset.asObservable();
    }

    async getAsset(id: number): Promise<Asset>{
        const current = this._selectedAsset.value;
        if(current){
            this.dlg.createLog("CompanyService",`Current asset in cache ${current.id} - requested ${id}`);
            const parsedId = parseInt(id.toString());
            if(current.id === parsedId){
                this.dlg.createLog("CompanyService",`Returning current asset in cache ${current.id}`);
                return current;
            }
        }

        this.dlg.createLog("CompanyService",`No current asset in cache - requested ${id}`);
        const apiRes = await this.api.getAsset(id, this.SelectedCompanyId);
        if(apiRes.result === ApiResultValues.Ok){
            this.dlg.createLog("CompanyService",`Asset ${id} retrieved from API`);
            this._selectedAsset.next(apiRes.data);
            return apiRes.data;
        }else{
            return null;
        }
    }

    async getAssetForTransaction(id: number){
        const res = await this.api.getAssetForTransaction(id, this.SelectedCompanyId);
        if(res.result === ApiResultValues.Ok){
            this._selectedAsset.next(res.data);
            this.router.navigate(['/company', this.SelectedCompanyId, 'assets','details', res.data.id]);
        }
    }

    async viewAsset(id: number){
        const res = await this.getAsset(id);
        if(res){
            this.router.navigate(['/company', this.SelectedCompanyId, 'assets', 'details', id]);
        }
    }

    private _createdAsset = new BehaviorSubject<Asset | null>(null);
    get CreatedAsset() {
        return this._createdAsset.asObservable();
    }

    
    async createAsset(){
        const snack = await this.showLoadingSnack();
        const newAsset = {id: 0, residualValue: 0, taxDepreciationRuleId: 0, bfDepreciationImportedTax: 0, bfDepreciationImported: 0};
        const resultAsset = await this.updateAsset(newAsset, 0, true, snack);
        if(resultAsset){
            //this.router.navigate(['/company', this.SelectedCompanyId, 'assets', 'details', resultAsset.id]);
            this._createdAsset.next(resultAsset);
            this._selectedAsset.next(resultAsset);  
        }

        return resultAsset;
    }

    async createAssetTransaction(id: number){
        const snack = await this.showLoadingSnack();
        const newAsset = await this.api.getBuildAssetForTransaction(id, this.SelectedCompanyId);
        if(newAsset.result === ApiResultValues.Ok){
            const asset = newAsset.data;
            const resultAsset = await this.updateAsset(asset, id, true, snack);
            //Do we want to do a redirect?
            //this.router.navigate(['/company', this.SelectedCompanyId, 'assets', 'details', apiRes.data.id]);
            if(resultAsset){
                this._createdAsset.next(resultAsset);
            this._selectedAsset.next(resultAsset);  
            }
            return resultAsset;
        }else{
            this.closeLoadingSnack(snack);
        }
    }

    async editAsset(id: number){
        const snack = await this.showLoadingSnack();
        const asset = await this.getAsset(id);
        if(asset){
            const updatedAsset = await this.updateAsset(asset, 0, false, snack);
            if(updatedAsset){
                this._selectedAsset.next(updatedAsset);
            }            
            return updatedAsset;
        }else{
            this.closeLoadingSnack(snack);
        }
    }

    private async updateAsset(entity: any, transactionId: number, isNew: boolean, snack: any){
        const obs = new Observable<any>(o => {
            const asyncFunc = async() => {
                const groupsFull = await this.getAssetGroups();

                if(groupsFull.length === 0){
                    this.dlg.showOk('No asset groups', 'You must first create asset groups before you can create assets.  Please create an asset group from company settings and then try again.');
                    this.closeLoadingSnack(snack);
                    o.next(null);
                    return;
                }
                
                const groups = groupsFull.map(x=>{
                    return {
                        key: x.id,
                        value: x.accountGroupName
                    };
                });
                
                
                const company = this._selectedCompany.value;
                const isPerpetual = company.perpetualFinancialYear;
                const companyStartDate = company.processingStartDate;
                const currentYear = await this.getCurrentFinancialYear();
                
                const rules = await this.getDepreciationRulesUI();
                const isTransaction = transactionId > 0;

                const form = this.fb.group({
                    assetName: [entity.assetName, [Validators.required, FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    description: [entity.description],
                    acquiredOn: [{ value: entity.acquiredOn, disabled: isTransaction || !isNew }, [Validators.required]], //, FinancialYearValidator.FinancialYearValidatorFactory(this.data.currentYear)
                    cost: [{ value: entity.cost, disabled: isTransaction || !isNew  }, [Validators.required]],
                    depreciationRuleId: [entity.depreciationRuleId, [Validators.required,NotValueValidatorDirective.notValueValidatorFactory(0)]],
                    accountGroupId: [{ value: entity.accountGroupId, disabled: isTransaction || !isNew  }, [Validators.required,NotValueValidatorDirective.notValueValidatorFactory(0)]],
                    bfDepreciationImported: [{value: entity.bfDepreciationImported, disabled: isNew || !(entity.bfDepreciationImported > 0)}],
                    assetRef: [{ value: entity.assetRef, disabled: company.useAutoAssetReference },[FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    supplierName: [entity.supplierName, [FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    class: [entity.class],
                    location: [entity.location, [FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    residualValue: [entity.residualValue, [LessThanCompareValidator.lessThanCompareValidatorFactory('cost')]],
                    depreciationCommenceDate: [{value: entity.depreciationCommenceDate, disabled: !isNew}, Validators.required],
                    depreciationCommencementDeferred: [{value: entity.depreciationCommencementDeferred, disabled: !isNew}],
                    bfDepreciationImportedTax: [{value: entity.bfDepreciationImportedTax, disabled: isNew || (!isNew &&  entity.bfDepreciationImported == 0)}, Validators.required],
                    taxDepreciationCommenceDate: [entity.taxDepreciationCommenceDate, [Validators.required]],
                    taxDepreciationRuleId: [entity.taxDepreciationRuleId, [Validators.required, NotValueValidatorDirective.notValueValidatorFactory(0)]],
                });


                if(company.enableClass){
                    form.controls.class.addValidators(Validators.required);
                }

                if(!company.enableAssetTaxDepreciation || company.companyVersion === 1){
                    //form.controls.taxDepreciationCommenceDate.clearValidators();
                    form.controls.taxDepreciationRuleId.clearValidators();
                    form.controls.bfDepreciationImportedTax.clearValidators();
                    form.controls.taxDepreciationCommenceDate.clearValidators();
                }

                // if(company.companyVersion === 1){
                //     form.controls.taxDepreciationCommenceDate.clearValidators();
                //     form.controls.taxDepreciationRuleId.clearValidators();
                //     form.controls.bfDepreciationImportedTax.clearValidators();
                // }

                form.controls.acquiredOn.valueChanges.subscribe(newvalue => {
                    const ysM = isPerpetual ? moment(companyStartDate) : moment(currentYear.yearStart);
                    const acqM = moment(newvalue);
                    const startAcqDiff = acqM.diff(ysM, 'days');

                    if(startAcqDiff < 0){
                        form.controls.bfDepreciationImported.setValidators([Validators.required]);
                        form.controls.bfDepreciationImported.enable();
                        if(company.enableAssetTaxDepreciation){
                            form.controls.bfDepreciationImportedTax.setValidators([Validators.required]);
                            form.controls.bfDepreciationImportedTax.enable();
                        }
                    }else{
                        form.controls.bfDepreciationImported.clearValidators();
                        form.controls.bfDepreciationImported.disable();
                        form.controls.bfDepreciationImported.setValue(0);
                        if(company.enableAssetTaxDepreciation){
                            form.controls.bfDepreciationImportedTax.clearValidators();
                            form.controls.bfDepreciationImportedTax.disable();
                            form.controls.bfDepreciationImportedTax.setValue(0);
                        }
                    }

                    if(company.defaultDepreciationStart === 'AcquisitionDate' && !form.controls.depreciationCommencementDeferred.value){
                        form.controls.depreciationCommenceDate.setValue(newvalue);
                    }
                });

                
                form.controls.accountGroupId.valueChanges.subscribe(newvalue => {
                    const g = groupsFull.find(x=> x.id === newvalue);
                    if(g){
                        const rule = rules.find(x=>x.key === g.depreciationRuleId);
                        if(rule){
                            form.controls.depreciationRuleId.setValue(parseInt(rule.key));
                        }
                    }
                });
                form.controls.depreciationCommencementDeferred.valueChanges.subscribe(newvalue => {
                    if(newvalue){
                        form.controls.depreciationCommenceDate.disable();
                        form.controls.depreciationCommenceDate.clearValidators();
                        form.controls.depreciationCommenceDate.setValue(null);
                    }else{
                        form.controls.depreciationCommenceDate.enable();
                        form.controls.depreciationCommenceDate.setValidators([Validators.required]);
                        if(company.defaultDepreciationStart === 'AcquisitionDate'){
                            form.controls.depreciationCommenceDate.setValue(form.controls.acquiredOn.value);
                        }
                    }
                });

                form.controls.depreciationCommenceDate.valueChanges.subscribe(newvalue => {
                    if(company.enableAssetTaxDepreciation){
                        form.controls.taxDepreciationCommenceDate.setValue(newvalue);
                    }
                });


                form.controls.depreciationRuleId.valueChanges.subscribe(newvalue => {
                    if(company.enableAssetTaxDepreciation){
                        form.controls.taxDepreciationRuleId.setValue(newvalue);
                    }
                });

                const title = isNew ? 'Create new asset' : 'Edit existing asset';
                let desc = isPerpetual? 'Your company has perpetual financial year activated.' : 'Your company does not have perpetual financial year activated.    ';
                if(isNew){
                    desc = '';
                    if(isTransaction){
                        desc =  desc + '  Please note that this asset is based on a transaction from your accounting package, which means that many values will not be editable';
                    }
                }else{
                    desc = desc + '  Only certain values can be edited after an asset has been created.  If you need to change disabled values, you will need to delete this asset and create it again.';
                }
                
                const groupContainer: IGroupContainer = {
                    title: title,
                    description: desc,
                    showCompanyStatus: true,
                    company: company,
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: entity,
                    form: form,
                    groups: [
                        {
                            header: 'Group 1',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Core information',
                                    description: '',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        {label: 'Asset ref', control: 'input', fieldName: 'assetRef'},
                                        {label: 'Asset name', control: 'input', fieldName: 'assetName'},
                                        {label: 'Supplier name', control: 'input', fieldName: 'supplierName'},
                                        {label: 'Description', control: 'textarea', fieldName: 'description'},
                                    ]
                                },
                                {
                                    header: 'Financial information',
                                    description: 'If you are intending to use a reducing balance depreciation method, please ensure that you set an appropriate residual value for the asset.  Please note that a 5% residual value will be applied if no value is set.',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        {label: 'Cost', control: 'input', fieldName: 'cost'},
                                        {label: 'Residual value', control: 'input', fieldName: 'residualValue'}
                                    ]
                                },
                                {
                                    header: 'Asset group',
                                    description: '',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        {label: 'Asset group', control: 'select', fieldName: 'accountGroupId', dropdownList: groups},
                                        {label: 'Depreciation rule', control: 'select', fieldName: 'depreciationRuleId', dropdownList: rules}
                                    ]
                                },
                                {
                                    header: 'Acquisition and depreciation dates',
                                    description: isPerpetual ? 'This company has perpetual financial year activated and does not need brought forward depreciation unless the asset was acquired prior to company processing start date.' 
                                            : 'This company does not have perpetual financial year activated and will need brought forward depreciation if the asset was acquired prior to the start of the current financial year.',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        {label: 'Acquisition date', control: 'datepicker', fieldName: 'acquiredOn', maxDate: currentYear.yearEnd},
                                        {label: 'Brought forward depreciation', control: 'input', fieldName: 'bfDepreciationImported'},
                                        {label: 'Depreciation start date', control: 'datepicker', fieldName: 'depreciationCommenceDate'},
                                        {label: 'Defer depreciation', control: 'checkbox', fieldName: 'depreciationCommencementDeferred'}
                                    ]
                                },
                                {
                                    header: 'Location and tracking',
                                    description: '',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        {label: 'Location', control: 'location', fieldName: 'location'}
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const updatedValues = form.getRawValue();
                        let apiRes: IApiResult;
                        if(transactionId > 0){
                            apiRes = await this.api.createAssetForTransaction(transactionId, updatedValues, this.SelectedCompanyId);
                        }else{
                            apiRes = await this.api.updateAsset(entity.id, updatedValues, this.SelectedCompanyId);
                        }

                        if(apiRes.result == ApiResultValues.Ok){
                            if(isNew){
                                this._createdAsset.next(apiRes.data);
                            }else{
                                this._selectedAsset.next(apiRes.data);
                            }
                            await this.refreshCompanySummaryData();
                            dRef.close();
                            o.next(apiRes.data);
                        }
                    }
                }

                if(company.enableAssetTaxDepreciation){
                    const taxSection: ISection = {
                        header: 'Tax information',
                        description: '',
                        form: form,
                        allowEdit: false,
                        items: [
                            {label: 'Tax depreciation rule', control: 'select', fieldName: 'taxDepreciationRuleId', dropdownList: rules},
                            {label: 'Tax depreciation brought forward', control: 'input', fieldName: 'bfDepreciationImportedTax'},
                            //{label: 'Tax depreciation commencement date', control: 'datepicker', fieldName: 'taxDepreciationCommenceDate'}
                        ]
                    };
                    groupContainer.groups[0].sections.splice(4, 0, taxSection);
                }

                if(company.enableClass){
                    groupContainer.groups[0].sections[4].items.push({
                        label: NtCompany.GetProviderClassTitle(company.externalDataSource.dataSource),
                        control: 'class',
                        fieldName: 'class'
                    });
                }


                this.matDlg.open(ModalEditorComponent,
                    {
                        data: groupContainer,
                        ...dialogDefaultFullScreen
                    }
                );

                if(snack){
                    this.closeLoadingSnack(snack);
                }
            }

            asyncFunc();
        });

        return await firstValueFrom(obs);
    }

    private _assetAdjustmentUpdated = new Subject<any>();
    get AssetAdjustmentUpdated() {
        return this._assetAdjustmentUpdated.asObservable();
    }

    async updateAssetAdjustmentPeriod(id: number, value: number){
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const data = {
                    id,
                    value
                };

                const form = this.fb.group({
                    id: [id, Validators.required],
                    value: [value, [Validators.required]]                
                })

                const gc: IGroupContainer = {
                    title: 'Edit adjustment value',
                    description: 'Edit this value to make an adjustment to the value of the asset.  This value will be posted in your end of year journals and amend the value of your asset.  A negative value will increase the value of your asset.',
                    dialogMode: true,
                    collapseGroupNav: true,
                    form:form,
                    data: data,
                    groups: [
                        {
                            id:'100',
                            header: 'Value',
                            sections: [
                                {
                                    header: 'Value',
                                    allowEdit: false,
                                    data: data,
                                    form: form,
                                    items: [
                                        {label: 'Adjustment', control: 'input', fieldName: 'value'}
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dref: MatDialogRef<ModalEditorComponent>) => {
                        o.next(null);
                        dref.close();
                    },
                    completeAction: async(dref: MatDialogRef<ModalEditorComponent>) => {
                        const { id, value} = form.getRawValue();
                        const res = await this.api.updateAssetAdjustmentPeriod(id, value, this.SelectedCompanyId);
                        if(res.result === ApiResultValues.Ok){
                            o.next(res.data);
                            this._assetAdjustmentUpdated.next(res.data);
                            dref.close();
                        }
                    }
                };

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: gc,
                        ...dialogDefaultFullScreen
                    }
                );
            }

            asyncF();
        });

        return await firstValueFrom(obs);
    }

    async editAssetBulk(command: string, assetIds: number[]){
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const c = this._selectedCompany.value;

                let dialogTitle = assetIds.length === 1 ? 'Edit Item' : 'Bulk Edit Items';
                let dialogDesc = `Your changes will affect ${assetIds.length} asset(s)`;
                let sectionTitle = '';
                let sectionDescription = '';
                let label = '';
                let itemControlType = 'input';

                switch(command){
                    case 'assetName':
                        sectionTitle = "Asset name";
                        sectionDescription = "Bulk edit asset name";
                        label="Asset name";
                        break;
                    case 'title':

                        break;
                    case 'assetRef':
                        sectionTitle = "Asset ref";
                        sectionDescription = "Bulk edit asset ref";
                        label = 'Asset ref';
                        break;
                    case 'class':
                        itemControlType = 'class';
                        sectionTitle = NtCompany.GetProviderClassTitle(c.externalDataSource.dataSource);
                        sectionDescription = "Bulk edit class";
                        label =  NtCompany.GetProviderClassTitle(c.externalDataSource.dataSource);
                        break;
                    case 'location':
                        itemControlType = 'location';
                        sectionTitle = NtCompany.GetProviderLocationTitle(c.externalDataSource.dataSource, c.useProviderLocation);
                        sectionDescription = "Bulk edit location";
                        label = NtCompany.GetProviderLocationTitle(c.externalDataSource.dataSource, c.useProviderLocation);
                        break;
                    case 'supplier':
                        sectionTitle = "Supplier";
                        sectionDescription = "Bulk edit supplier";
                        label = 'Asset ref';
                        break;

                    default:
                        await this.dlg.showOk('Bulk edit', 'The edit option you have selected does not appear to be one that is currently supported');
                        return;

                }

                const form = this.fb.group({
                    value: ['', [Validators.required]]
                });

                const group: IGroupContainer = {
                    title: dialogTitle,
                    description: dialogDesc,
                    form: form,
                    data: {},
                    dialogMode: true,
                    collapseGroupNav: true,
                    groups: [
                        {
                            header: 'group',
                            id: '1001',
                            sections: [
                                {
                                    header: sectionTitle,
                                    description: sectionDescription,
                                    allowEdit: false,
                                    form: form,
                                    data: {},
                                    items: [
                                        {label: label, control: itemControlType, fieldName: 'value'}
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const {value} = form.getRawValue();
                        const res = await this.api.updateAssetBulk(command, value, assetIds, this.SelectedCompanyId);
                        if(res.result === ApiResultValues.Ok){
                            o.next(true);
                            dRef.close();
                        }
                    }
                };

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
            }

            asyncF();
        });

        return await firstValueFrom(obs);
    }

    async editAccrualPrepaymentBulk(command: string, itemIds: number[]){
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const c = this._selectedCompany.value;

                let dialogTitle = itemIds.length === 1 ? 'Edit Item' : 'Bulk Edit Items';
                let dialogDesc = `Your changes will affect ${itemIds.length} items(s)`;
                let sectionTitle = '';
                let sectionDescription = '';
                let label = '';
                let itemControlType = 'input';

                switch(command){
                    case 'title':
                        sectionTitle = "Item title";
                        sectionDescription = "Bulk edit item title/details";
                        label = 'Title';
                        break;
                    case 'class':
                        itemControlType = 'class';
                        sectionTitle = NtCompany.GetProviderClassTitle(c.externalDataSource.dataSource);
                        sectionDescription = "Bulk edit class";
                        label =  NtCompany.GetProviderClassTitle(c.externalDataSource.dataSource);
                        break;
                    case 'location':
                        itemControlType = 'location';
                        sectionTitle = NtCompany.GetProviderLocationTitle(c.externalDataSource.dataSource, c.useProviderLocation);
                        sectionDescription = "Bulk edit location";
                        label = NtCompany.GetProviderLocationTitle(c.externalDataSource.dataSource, c.useProviderLocation);
                        break;
                    
                    default:
                        await this.dlg.showOk('Bulk edit', 'The edit option you have selected does not appear to be one that is currently supported');
                        return;

                }

                const form = this.fb.group({
                    value: ['', [Validators.required]]
                });

                const group: IGroupContainer = {
                    title: dialogTitle,
                    description: dialogDesc,
                    form: form,
                    data: {},
                    dialogMode: true,
                    collapseGroupNav: true,
                    groups: [
                        {
                            header: 'group',
                            id: '1001',
                            sections: [
                                {
                                    header: sectionTitle,
                                    description: sectionDescription,
                                    allowEdit: false,
                                    form: form,
                                    data: {},
                                    items: [
                                        {label: label, control: itemControlType, fieldName: 'value'}
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const {value} = form.getRawValue();
                        const res = await this.api.updateAccrualPrepaymentsBulk(command, value, itemIds, this.SelectedCompanyId);
                        if(res.result === ApiResultValues.Ok){
                            o.next(true);
                            dRef.close();
                        }
                    }
                };

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
            }

            asyncF();
        });

        return await firstValueFrom(obs);
    }

    async updateKitRemoveMember(id: number){
        const dlg = await this.dlg.showYesNo('Remove asset from kit', 'Are you sure that you want to remove the asset from the kit it is part of?');
        if(dlg === DialogBoxResults.Yes){
            const res = await this.api.updateKitRemoveAsset(id, this.SelectedCompanyId);
            return res;
        }else{
            return null; 
        }
    }

    async updateKitAddMember(asset: any){
        const obs = new Observable<any>(o => {
            const asyncF = async() =>{
                const snack = await this.showLoadingSnack();
                const assetEntity = await this.getAsset(asset.id);
                const availableKitsRes = await this.api.getAssetsKitParents(assetEntity.accountGroupId, this.SelectedCompanyId);
                const kits = availableKitsRes.data;

                const form = this.fb.group({
                    hasChildren: [0, [Validators.min(0), Validators.max(1)]]
                });

                const handleSelectionUpdate = (v: boolean) => {
                    const countSelected = kits.filter(x=>x.selected).length;
                    form.controls.hasChildren.setValue(countSelected);
                }

                const company = this.SelectedCompany;

                const section: ISection = {
                    header: 'Select available kit',
                    description: "Please choose one of the kits available for this asset's group",
                    form: form,
                    data: assetEntity,
                    allowEdit: false,
                    items: [],
                    component: AssetSelectListSharedComponent,
                    componentProps: [
                        {propName: 'assets', data: kits},
                        {propName: 'company', data: company},
                        {propName: 'multiSelect', data: false}
                    ],
                    componentOutputs: [
                        {outputName: 'selectionChanged', handler: handleSelectionUpdate}
                    ]
                };

                section.componentProps.push({
                    propName: 'section',
                    data: section
                });

                const group: IGroupContainer = {
                    title: 'Add asset to existing kit',
                    description: 'Use this tool to add an asset to an existing kit.  The kit must be of the same asset group as this asset.',
                    dialogMode: true,
                    data: assetEntity,
                    collapseGroupNav: true,
                    form: form,
                    groups: [
                        {
                            header: 'Group',
                            id: '1001',
                            sections: [section]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const parentId = kits.find(x=>x.selected).id;

                        const res = await this.api.updateKitAddAsset(parentId, assetEntity.id, this.SelectedCompanyId);
                        if(res.result === ApiResultValues.Ok){
                            o.next(res.data);
                            dRef.close();
                        }
                    }
                };

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );

                this.closeLoadingSnack(snack);
            }

            asyncF();
        });

        return await firstValueFrom(obs);
    }

    async createAssetKit(){
        const assetKit = await this.editAssetKit(0);
        if(assetKit){
            this._createdAsset.next(assetKit);
        }

        return assetKit;
    }

    async getAssetKitMembers(id: number){
        const apiRes = await this.api.getAssetsKitMembers(id, this.SelectedCompanyId);
        return apiRes.data;
    }

    async editAssetKit(id: number){
        const obs = new Observable<any>(o => {
            const asyncMethod = async() => {
                const snack = await this.showLoadingSnack();
                const currentYear = await this.getCurrentFinancialYear(); 

                let currentAssetList: any[];
                let kitMembers: any[] = [];

                const company = this._selectedCompany.value;
                const groupsFull = await this.getAssetGroups();
                const groups = groupsFull.map(x=>{
                    return {
                        key: x.id,
                        value: x.accountGroupName
                    };
                });

                const loadPossibleKitMembers = async(groupId: number) => {
                    var listRes = await this.api.getAssetsList(groupId, this.SelectedCompanyId);
                    if(listRes.result === ApiResultValues.Ok){
                        currentAssetList = listRes.data;
                        assetListSection.componentUpdate(currentAssetList);
                    }      
                }

                let entity;
                if(id > 0) {
                    entity = await this.getAsset(id);
                    const kitMembersRes = await this.api.getAssetsKitMembers(id, this.SelectedCompanyId);
                    kitMembers = kitMembersRes.data;
                    loadPossibleKitMembers(entity.accountGroupId);
                }else{
                    entity = {
                        assetName: '',
                        acquiredOn: new Date(),
                        accountGroupId: [groupsFull[0].id, [Validators.required]],
                        assetRef: ''
                    }
                }

                const form = this.fb.group({
                    assetName: [entity.assetName, [Validators.required, FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    acquiredOn: [ entity.acquiredOn, [Validators.required]],
                    accountGroupId: [{ value: entity.accountGroupId, disabled: id > 0 }, [Validators.required,NotValueValidatorDirective.notValueValidatorFactory(0)]],
                    assetRef: [{ value: entity.assetRef, disabled: company.useAutoAssetReference },[FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    hasChildren: [kitMembers.length, [Validators.min(2)]],
                });

                const handleAddNewSelectionUpdate = (v: boolean) => {
                    const countSelected = currentAssetList.filter(x=>x.selected).length;
                    form.controls.hasChildren.setValue(countSelected);
                }

                const assetListSection: ISection = {
                    header: 'Select kit assets',
                    description: 'Select the assets that should be part of this kit.  You must select at least two assets to create the kit.',
                    allowEdit: false,
                    items:[],
                    component: AssetSelectListSharedComponent,
                    componentProps: [
                        {propName: 'assets', data: []},
                        {propName: 'company', data: company}
                    ],
                    componentOutputs: [{outputName: 'selectionChanged', handler: handleAddNewSelectionUpdate}],
                    data: entity,
                    form: form,
                };

                assetListSection.componentProps.push({
                    propName: 'section',
                    data: assetListSection
                });

                const handleRemoveExistingSelectionUpdate = (v: boolean) => {
                    const countSelected = kitMembers.filter(x=>x.selected).length;
                    form.controls.hasChildren.setValue(countSelected);
                }

                const existingSection: ISection = {
                    header: 'Existing kit members',
                    description: 'Select assets to remove from this kit',
                    allowEdit: false,
                    items: [],
                    component: AssetSelectListSharedComponent,
                    componentProps: [
                        {propName: 'assets', data: kitMembers},
                        {propName: 'company', data: company},
                    ],
                    componentOutputs: [{outputName: 'selectionChanged', handler: handleRemoveExistingSelectionUpdate}],
                    data:entity,
                    form: form,
                };
                existingSection.componentProps.push({
                    propName: 'section',
                    data: existingSection
                });

                

                const mainSection: ISection = {
                    header: 'Kit information',
                    description: 'Please enter primary data on kit',
                    allowEdit: false,
                    form: form,
                    data: entity,
                    items: [
                        {label: 'Asset ref', control: 'input', fieldName: 'assetRef'},
                        {label: 'Name', control: 'input', fieldName: 'assetName'},
                        {label: 'Acquisition date', control: 'datepicker', fieldName: 'acquiredOn', maxDate: currentYear.yearEnd},
                        {label: 'Asset group', control: 'select', fieldName: 'accountGroupId', dropdownList: groups}
                    ]
                };

                form.controls.accountGroupId.valueChanges.subscribe(async(v) => {
                    await loadPossibleKitMembers(v);              
                });

                const group: IGroupContainer = {
                    title: 'Create/Edit Kit Asset',
                    description: 'Use this tool to create or edit a kit asset',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: entity,
                    form: form,
                    groups: [
                        {
                            header: 'Group1',
                            id: '1001',
                            sections: [
                                mainSection,
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const {assetRef, assetName, accountGroupId, acquiredOn} = form.getRawValue();
                        const asset = {assetRef, assetName, accountGroupId, acquiredOn};
                        const newMembers = currentAssetList.filter(x=>x.selected).map(a => {
                            return a.id;
                        });
                        const removeMembers = kitMembers ? kitMembers.filter(x=>x.selected).map(a => {
                            return a.id;
                        }) : [];

                        const res = await this.api.updateAssetKit(id, asset, newMembers,removeMembers , this.SelectedCompanyId);
                        if(res.result === ApiResultValues.Ok){
                            this._selectedAsset.next(res.data);
                            dRef.close();
                            this.router.navigate(['/company', this.SelectedCompanyId, 'assets', 'details', res.data.id]);
                            o.next(res.data);
                        }
                    }
                };

                if(id> 0){
                    group.groups[0].sections.push(existingSection);
                }
                group.groups[0].sections.push(assetListSection);

                this.matDlg.open(ModalEditorComponent,
                        {
                            data: group,
                            ...dialogDefaultFullScreen
                        }
                );
                this.closeLoadingSnack(snack);
            }

            asyncMethod();
        });

        return await firstValueFrom(obs);
    }

    async convertAssetToKit(asset: any){
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const currentValue = asset.summary ? asset.summary.curValue : (asset.curValue ? asset.curValue : asset.currentValue);
                const assetName = asset.name ? asset.name : asset.assetName;
                const form = this.fb.group({
                    children: this.fb.array([]),
                    hadChildren: [0, [Validators.min(1)]],
                    currentValueTotal: [{value: 0, disabled: true}],
                    allocationTotal: [{value: 0, disabled: true}]
                },{
                    validators: EqualsValueValidator.EqualsValueValidatorFactory(currentValue, 'currentValueTotal')
                });

                const fixFloat = (value: number, precision: number): number => {
                    return parseFloat(value.toFixed(precision));
                }

                const calculateTotals = () => {
                    let percentage = 0;
                    let value = 0;

                    form.controls.children.controls.forEach(grp => {
                        const g = (grp as any) as FormGroup;
                        if (g.controls["allocation"].value) {
                            const parsed = fixFloat(parseFloat(g.controls["allocation"].value), 1);
                            percentage += parsed;
                            percentage = fixFloat(percentage, 1);
                        }
                        if (g.controls["value"].value) {
                            const parsed = fixFloat(parseFloat(g.controls["value"].value), 2);
                            value += parsed;
                            value = fixFloat(value, 2);
                            console.log("Calculate totals.  Item value: " + g.controls["value"].value + ".  Parsed value: " + parsed + ". New total: " + value);
                        }
                    });

                    form.controls.currentValueTotal.setValue(value, {emitEvent: false});
                    form.controls.allocationTotal.setValue(percentage, {emitEvent: false});

                    if (percentage === 100 && value === currentValue && form.controls.children.length > 0) {
                        console.log(`Is valid: true. Current values: percentage: ${percentage}, value: ${value}, assetValue: ${currentValue}`);
                    } else {
                        console.log(`Is valid: false. Current values: percentage: ${percentage}, value: ${value}, assetValue: ${currentValue}`);
                    }
                }

                const addChild = () => {
                    const group = this.fb.group({
                        ref: '',
                        name: ['', Validators.required],
                        description: '',
                        allocation: [0, [Validators.required, Validators.max(100)]],
                        value: [0, [Validators.required, Validators.max(currentValue)]]
                    });
                    //add the subscriptions for changes
                    group.controls.allocation.valueChanges.subscribe(r => {
                    //Changed the %tage
                        const percentageFactor = (currentValue * r / 100);
                        const curVal = parseFloat(fixFloat(percentageFactor, 2).toFixed(2)); //parseFloat((this.asset.currentValue * r / 100).toFixed(2));
                        group.controls.value.setValue(curVal, { emitEvent: false });
                        calculateTotals();
                        form.updateValueAndValidity();
                    });
            
                    group.controls.value.valueChanges.subscribe(r => {
                    //Changed the value
                        const p = r / currentValue * 100;
                        const percentage = parseFloat(fixFloat(p, 1).toFixed(1)); //parseFloat((r / this.asset.currentValue * 100).toFixed(1));
                        group.controls.allocation.setValue(percentage, { emitEvent: false });
                        calculateTotals();
                        form.updateValueAndValidity();
                    });
            
                    form.controls.children.push(group as any as FormControl);
                    form.controls.hadChildren.setValue(form.controls.children.length);
                }

                //Initialise with at least one child.
                addChild();

                const removeChild = (index: number) => {
                    if(form.controls.children.length > 1){
                        form.controls.children.removeAt(index);
                        form.controls.hadChildren.setValue(form.controls.children.length);
                    }
                }

                const data = {
                    asset,
                    form,
                    company: this._selectedCompany.value
                };

                const group: IGroupContainer = {
                    title: 'Split selected asset into Kit',
                    description: `<div style="display: flex"><h3 style="flex: 50">${assetName}</h3><h3 style="flex: 50">Current value: ${currentValue}</h3></div><p>This tool will split the selected asset into child assets and create a kit.  All postings made for the selected asset will be allocated to the children according to the percentage or amount values in the table below.  You must provide at least two children and values must match exactly.</p>`,
                    dialogMode: true,
                    collapseGroupNav: true,
                    form: form,
                    data: data,
                    groups: [
                        {
                            header: 'group1',
                            id: '100',
                            sections: [
                                {
                                    header: 'Allocate kit child values',
                                    description:'The allocated values must equal the current asset value',
                                    allowEdit: false,
                                    items: [],
                                    data: data,
                                    form: form,
                                    component: AssetConvertKitComponent,
                                    componentProps: [
                                        {propName: 'value', data: currentValue},
                                        {propName: 'currency', data: this._selectedCompany.value.defaultCurrency ?? 'GBP'},
                                        {propName: 'form', data: form}
                                    ],
                                    componentOutputs: [
                                        {outputName: 'addChildEvent', handler: addChild},
                                        {outputName: 'removeChildEvent', handler: removeChild}
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dref: MatDialogRef<ModalEditorComponent>) => {
                        o.next(null);
                        dref.close()
                    },
                    completeAction: async(dref: MatDialogRef<ModalEditorComponent>) => {
                        const model = new AssetConvertToKit();
                        model.assetId = asset.id;

                        form.controls.children.controls.forEach(grp => {
                            const g = grp as any as FormGroup;
                            const child = new KitChild()
                            child.allocation = g.controls["allocation"].value;
                            child.description = g.controls["description"].value;
                            child.name = g.controls["name"].value;
                            child.ref = g.controls["ref"].value;
                            model.newChildren.push(child);
                        });

                        const res = await this.api.assetConvertToKit(model, this.SelectedCompanyId);
                        if(res.result === ApiResultValues.Ok){
                            o.next(true);
                            dref.close();
                        }
                    }
                };

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
            };

            asyncF();
        });

        return await firstValueFrom(obs);
    }

    async disposeAsset(sourceAsset: any){
        const obs = new Observable<any>(o => {
            const asyncMethod = async() => {
                const snack = await this.showLoadingSnack();

                const currentValue = sourceAsset.summary ? sourceAsset.summary.curValue : (sourceAsset.curValue ? sourceAsset.curValue : sourceAsset.currentValue);
                const assetName = sourceAsset.name ? sourceAsset.name : sourceAsset.assetName;

                const asset = await this.getAsset(sourceAsset.id);

                const disposalForm = this.fb.group({
                    disposalDate: [asset.disposalDate, Validators.required],
                    disposalValue: [0, Validators.required],
                    reverseCurrentYear: [{ value: false, disabled: asset.assetState !== 'Active' }]
                });

                const group: IGroupContainer = {
                    title: 'Dispose asset',
                    description: `<div style="display: flex"><h3 style="flex: 50">${assetName}</h3><h3 style="flex: 50">Current value: ${currentValue}</h3></div><p>Please provide the date that this asset should be diposed, its disposal amount and whether to reverse any current year depreciation (if applicable).</p>`,
                    dialogMode: true,
                    showCompanyStatus: true,
                    company: this._selectedCompany.value,
                    collapseGroupNav: true,
                    data: asset,
                    form: disposalForm,
                    groups: [
                        {
                            header: 'Group1',
                            id: '10001',
                            sections: [
                                {
                                    header: 'Disposal information',
                                    description: '',
                                    form: disposalForm,
                                    allowEdit: false,
                                    items: [
                                        {label: 'Disposal date', control: 'datepicker', fieldName: 'disposalDate'},
                                        {label: 'Sale proceeds', control: 'input', fieldName: 'disposalValue'},
                                        {label: 'Reverse current year depreciation', control: 'checkbox', fieldName: 'reverseCurrentYear'}
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        //Firstly do a preflight check
                        const {disposalDate} = disposalForm.getRawValue();
                        const model: AssetDisposalModel = {
                            assetId: sourceAsset.id,
                            disposalDate: disposalDate,
                            disposalValue: 0,
                            assetDisposalNominalAccount: 0,
                            preflight: true,
                            reverseCurrentYear: true
                        };
                        const preflight = await this.api.disposeAsset(model, this.SelectedCompanyId);

                        if(preflight.result !== ApiResultValues.Ok){
                            await this.dlg.showOk('Disposal error', 'There was an error checking your disposal configuration.  Please try again, and if the problem persists, contact support using the Help menu above.');
                            return;
                        }

                        const pfResult = preflight.data;
                        if(pfResult.result !== 'PreflightCompleted')
                        {
                            let message = '';
                            if(pfResult.result === 'FailLegacy'){
                                message = 'There is currently no disposal journaling configuration for this asset group.  Please edit the company settings, and ensure that the fixed asset register disposal mappings for this asset group are complete.';
                            }else if(pfResult.result === 'FailNoConfiguration'){
                                message = 'There is currently missing or incomplete disposal journaling configuration for this asset group.  This configuration is required in order to correctly dispose of an asset.  Please edit the company settings, and ensure that the fixed asset register disposal mappings for this asset group are complete.';
                            }else if(pfResult.result === 'GeneralError'){
                                message = 'Unfortunately, the preflight check of your disposal configuration has revealed an error in your data.  Please contact support using the Help menu above.';
                            }else if(pfResult.result === 'KitParent'){
                                message = 'This asset is a kit parent, meaning it acts as a container to other assets.  Kit parents cannot be disposed directly.  Please dispose of the kit members.  The parent will be marked as disposed automatically.';
                            }else if(pfResult.result === 'OrphanedFromNominal'){
                                message = ' The preflight check of your disposal configuration has revealed an error in your nominal accout configuration.  The nominal account that this asset has been assigned to could not be found.  Please contact support and advise that this has occurred, along with info on the date/time that you attempted to dispose of the asset and we will resolve this issue for you.';
                            }

                            message = message + '  Please correct your data and try again.';

                            await this.dlg.showOk('Asset disposal preflight error', message);
                            return;
                        }

                        const disposalModel = {
                            assetId: sourceAsset.id,
                            preflight: false,
                            ...disposalForm.getRawValue()
                        };

                        const dlg = await this.dlg.showPleaseWait('Please wait while we dispose of the asset and post any journal entries required');

                        const disResult = await this.api.disposeAsset(disposalModel, this.SelectedCompanyId);
                        await dlg.close();

                        if(disResult.result === ApiResultValues.Ok){
                            const innerResult = disResult.data;
                            if(innerResult.result === 'Completed'){
                                dRef.close();
                                this._selectedAsset.next(innerResult.asset);
                                
                                await this.refreshCompanySummaryData();
                                o.next(innerResult.asset);
                                o.complete();
                            }else{
                                await this.dlg.showOk('Disposal error',  `There was an error disposing this asset: ${disResult.message}`);
                            }
                        }
                    }
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );

                this.closeLoadingSnack(snack);
            }

            asyncMethod();
        });

        return await firstValueFrom(obs);
    }

    async deleteAssetReverse(asset: any){
        const confirm = await this.dlg.showYesNo('Reverse asset deletion', 'Are you sure that you want to reverse the deletion of this asset?  This action cannot be undone.');
        if(confirm !== DialogBoxResults.Yes){
            return null;
        }

        const dlg = await this.dlg.showPleaseWait('Please wait while we dispose of the asset and post any journal entries required');

        const disResult = await this.api.deleteAssetReverse(asset.id, this.SelectedCompanyId);
        await dlg.close();

        if(disResult.result === ApiResultValues.Ok){
            await this.refreshCompanySummaryData();
            return true;
        }

        return false;
    }

    async disposeAssetReverse(asset: any){
        const obs = new Observable<any>(o => {
            const asyncFunc = async() => {
                const snack = await this.showLoadingSnack();
                const currentValue = asset.summary ? asset.summary.curValue : (asset.curValue ? asset.curValue : asset.currentValue);
                const assetName = asset.name ? asset.name : asset.assetName;

                const dispModel: AssetDisposalModel = {
                    assetId: asset.id,
                    disposalDate: new Date(),
                    disposalValue: 0,
                    assetDisposalNominalAccount: 0,
                    preflight: true,
                    reverseCurrentYear: true
                };
                const preflight = await this.api.disposeAssetReverse(dispModel, this.SelectedCompanyId);
                if(preflight.result !== ApiResultValues.Ok){
                    o.next(null);
                    this.closeLoadingSnack(snack);
                    return;
                }

                const pfResult = preflight.data;
                if(pfResult.result !== 'PreflightCompleted'){
                    let message ='';

                    if(pfResult.result === 'NotDisposed'){
                        message = 'This asset does not appear to be in a disposed state and cannot therefore have its disposal reversed.';
                    }else if(pfResult.result !== 'InvalidFinancialYear'){
                        message = 'This asset was not disposed during the current financial year and cannot therefore have its disposal reversed.';
                    }else if(pfResult.result !== 'GeneralError'){
                        message = 'There is currently no disposal journaling configuration for this asset group.  This is required in order to correctly reverse the disposal of an asset.'
                    }else if(pfResult.result !== 'FailNoConfiguration'){
                        message = 'We have been unable to identify the journal entry that was created when the asset was disposed.  To continue, please contact support and we will investigate and resolve the error.'
                    }

                    this.closeLoadingSnack(snack);
                    await this.dlg.showOk('Asset disposal reverse preflight error', message);
                    o.next(null);
                    return;
                }

                const msg = `<div style="display: flex"><h3 style="flex: 50">${assetName}</h3><h3 style="flex: 50">Current value: ${currentValue}</h3></div><p>This asset has been disposed of, but is in a state that means we can reverse this process for you.  nettTracker will post a correction journal entry to reverse the journal that was created when the asset was originally disposed.  We strongly recommend that you allow this journal to be posted.</p>`;
                const form = this.fb.group({
                    skipJournal: false
                });

                const group: IGroupContainer = {
                    title: 'Reverse Asset Disposal',
                    description: msg,
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: dispModel,
                    form: form,
                    groups: [
                        {
                            header: 'Group1',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Post corrective journal',
                                    description: 'If you have already deleted the disposal journal from your accounts package, please tick the box below, and nettTracker will not create the additional journal for you.  Please be careful selecting this option.',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        {label: 'Skip correction journal', control: 'checkbox', fieldName: 'skipJournal'}
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const {skipJournal} = form.getRawValue();
                        dispModel.reverseCurrentYear = !skipJournal;
                        dispModel.preflight = false;
                        const dlg = await this.dlg.showPleaseWait('Please wait while we reverse the disposal of the asset and post any journal entries required');
                        const dispRes = await this.api.disposeAssetReverse(dispModel, this.SelectedCompanyId);
                        await dlg.close();

                        if(dispRes.result === ApiResultValues.Ok){
                            const innerRes = dispRes.data;
                            if(innerRes.result === 'Completed'){
                                dRef.close();
                                this._selectedAsset.next(innerRes.asset);
                                await this.refreshCompanySummaryData();
                                o.next(innerRes.asset);
                                o.complete();
                            }else{
                                await this.dlg.showOk('Disposal reversal error',  `There was an error reversing the disposal of this asset: ${dispRes.message}`);
                            }
                        }
                    }
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );

                this.closeLoadingSnack(snack);
            }

            asyncFunc();
        });

        return await firstValueFrom(obs);
    }

    async getAssetPeriods(id: number){
        return await this.api.getAssetPeriods(id, this.SelectedCompanyId);
    }

    async getAccrualPrepaymentPeriods(id: number){
        return await this.api.getAccrualPrepaymentPeriods(id, this.SelectedCompanyId);
    }

    async getAssetPostings(id: number){
        return await this.api.getAssetPostings(id, this.SelectedCompanyId);
    }

    async getAssetTaxPostings(id: number){
        return await this.api.getAssetTaxPostings(id, this.SelectedCompanyId);
    }

    async createAssetPosting(id: number, isActive: boolean) {
        const newPosting = {
            parentId: id,
            id: 0,
            postingDate: new Date(),
        };

        return await this.updateAssetPosting(newPosting, isActive);
    }

    async editAssetPosting(p: any, isActive: boolean) {
        return await this.updateAssetPosting(p, isActive);
    }

    private async updateAssetPosting(p: any, isActive: boolean) {
        const obs = new Observable<any>(o => {      
            const asyncMethod = async() => {
                const form = this.fb.group({
                        id: [p.id],
                        postingDate: [p.postingDate, [Validators.required]],
                        amount: [p.amount, [Validators.required]],
                        closed: [p.closed]
                    });

                form.controls.closed.valueChanges.subscribe(v => {
                    if(v){
                        this.dlg.showOk('Posting marked as closed', 'You have marked this posting as closed.  Closed postings are finalised and will not be included in any journal entries.  Closed postings cannot be deleted.  You should only mark a posting as closed if you are certain that no further changes are required.');
                    }
                });

                let desc = 'Changes to the postings for an asset will affect the asset value and depreciation calculations.  Any updates will result in a recalculation of depreciation that is required for the remaining lifes of the asset.  However, if the asset has completed its automated depreciation schedule, adding an adjustment will not trigger any futher depreciation.';
                if(!isActive){
                    desc = desc + '  As this asset is currently inactive, no further automated changes will be made by nettTracker.  If your posting is not closed, a journal entry will be created in due course.';
                }

                const group: IGroupContainer = {
                    title: `${p.id === 0 ? 'Create' : 'Edit'} Asset  Posting`,
                    description: desc,
                    showCompanyStatus: true,
                    company: this._selectedCompany.value,
                    dialogMode: true,
                    collapseGroupNav: true,
                    form: form,
                    data: null,
                    groups: [
                        {
                            header: 'group 1',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Posting details',
                                    description:'To increase the asset value, enter a negative amount.  To decrease the asset value, enter a positive amount.',
                                    allowEdit: false,
                                    form: form,
                                    data: p,
                                    items: [
                                        { label: 'Posting date', control: 'datepicker', fieldName: 'postingDate'}, //, maxDate: this._selectedCompany.value.currentFinancialYear.yearEnd, minDate: this._selectedCompany.value.currentFinancialYear.yearStart },
                                        { label: 'Amount', control: 'input', fieldName: 'amount' }
                                    ]
                                },
                                {
                                    header: 'Marking as closed',
                                    description: 'A closed posting is one that has been finalised and will not be included in any journal entries.  Closed postings cannot be deleted.  You should only mark a posting as closed if you are certain that no further changes are required.',
                                    allowEdit: false,
                                    form: form,
                                    data: p,
                                    items: [{ label: 'Closed', control: 'checkbox', fieldName: 'closed' }]
                                }
                            ]
                        }]
                    ,
                    cancelAction: (dref: MatDialogRef<ModalEditorComponent>) => {
                        dref.close();
                        o.next(null);
                    },
                    completeAction: async(dref: MatDialogRef<ModalEditorComponent>) => {
                        const {postingDate, amount, closed} = form.getRawValue();
                        const model = {
                            parentId: p.parentId,
                            postingDate: postingDate,
                            amount: amount,
                            closed: closed
                        };

                        const res = await this.api.updateAssetPosting(model, p.id, this.SelectedCompanyId);
                        if(res.result === ApiResultValues.Ok){
                            this._selectedAsset.next(res.data);
                            dref.close();
                            o.next(res.data);
                        }
                    }
                };

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );

            };

            asyncMethod();

        });

        return await firstValueFrom(obs);
    }

    async deleteAssetPosting(id: number){
        const dlgRes = await  this.dlg.showConfirmDelete();
        if(dlgRes === DialogBoxResults.Yes){
            const res = await this.api.deleteAssetPosting(id, this.SelectedCompanyId);
            if(res.result === ApiResultValues.Ok){
                this._selectedAsset.next(res.data);
                return true;
            }
        }
            
        return false;
    }

    async createAssetTaxPosting(id: number, isActive: boolean) {
        const newPosting = {
            parentId: id,
            postingDate: new Date(),
            id: 0,
        };

        return await this.updateAssetTaxPosting(newPosting, isActive);
    }

    async editAssetTaxPosting(p: any, isActive: boolean) {
        return await this.updateAssetTaxPosting(p, isActive);
    }

    private async updateAssetTaxPosting(p: any, isActive: boolean) {
        const obs = new Observable<any>(o => {      
            const asyncMethod = async() => {
                const form = this.fb.group({
                    id: [p.id],
                    postingDate: [p.postingDate, [Validators.required]],
                    amount: [p.amount, [Validators.required]],
                    closed: [p.closed]
                });

                let desc = 'Changes to the postings for an asset will affect the asset tax value and depreciation calculations.'
                if(!isActive){
                    desc = desc + '  As this asset is currently inactive, no further automated changes will be made by nettTracker.';
                }

                const group: IGroupContainer = {
                    title: `${p.id === 0 ? 'Create' : 'Edit'} Asset Tax Posting`,
                    description: desc,
                    showCompanyStatus: true,
                    company: this._selectedCompany.value,
                    dialogMode: true,
                    collapseGroupNav: true,
                    form: form,
                    data: null,
                    groups: [
                        {
                            header: 'group 1',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Posting details',
                                    allowEdit: false,
                                    form: form,
                                    data: p,
                                    items: [
                                        { label: 'Posting date', control: 'datepicker', fieldName: 'postingDate'}, //, maxDate: this._selectedCompany.value.currentFinancialYear.yearEnd, minDate: this._selectedCompany.value.currentFinancialYear.yearStart },
                                        { label: 'Amount', control: 'input', fieldName: 'amount' },
                                        //{ label: 'Closed', control: 'checkbox', fieldName: 'closed' }
                                    ]
                                }
                            ]
                        }]
                    ,
                    cancelAction: (dref: MatDialogRef<ModalEditorComponent>) => {
                        dref.close();
                        o.next(null);
                    },
                    completeAction: async(dref: MatDialogRef<ModalEditorComponent>) => {
                        const {postingDate, amount, closed} = form.getRawValue();
                        const model = {
                            parentId: p.parentId,
                            postingDate: postingDate,
                            amount: amount,
                            closed: closed
                        };

                        const res = await this.api.updateAssetTaxPosting(model, p.id, this.SelectedCompanyId);
                        if(res.result === ApiResultValues.Ok){
                            this._selectedAsset.next(res.data);
                            dref.close();
                            o.next(res.data);
                        }
                    }
                };

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );

            };

            asyncMethod();

        });

        return await firstValueFrom(obs);
    }

    async deleteAssetTaxPosting(id: number){
        const dlgRes = await  this.dlg.showConfirmDelete();
        if(dlgRes === DialogBoxResults.Yes){
            const res = await this.api.deleteAssetTaxPosting(id, this.SelectedCompanyId);
            if(res.result === ApiResultValues.Ok){
                this._selectedAsset.next(res.data);
                return true;
            }
        }
            
        return false;
    }

    async accrualPrepaymentPostRelease(id: number){
        const obs = new Observable<any>(o => {
            const asyncM = async() => {
                const snack = await this.showLoadingSnack();
                const currentYear = await this.getCurrentFinancialYear();
                const item = await this.getAccrualPrepaymentItem(id);
                const company = this._selectedCompany.value;

                let desc = '';
                let status = '';
                
                if(item.isAccrual){
                    desc = `<h2>${item.title} | Accrued value: ${item.balance}</h2><p>Accruals will automatically be released back to the profit and loss account if an actual/expected P&L date was given.  If not, you can release back the value to the profit and loss account at any time.</p>`;
                    if(item.status === 'Holding'){
                        status = '<p>This item has completed posting the monthly amounts required.  You can now release all or part of the accrued value from your balance sheet account.</p>';
                    }else if(item.status === 'Active'){
                        status = '<p>This item has not yet completed posting the monthly amounts required.  You can release all or part of the currently accrued value from your balance sheet account.</p><p>You can also choose to end any further accrual postings by selecting "Post and complete" for this accrual.</p>';
                    }else{
                        status = '<p>This item has not reached a holding state, which means that nettTracker has not yet posted all of the period values.  If you reverse the accrued value manually, you will have an imbalance until the final values are posted.</p>';
                    }
                }else{
                    desc = `<h2>${item.title} | Prepaid value: ${item.balance}</h2><p>Prepayments will automatically be released to the profit and loss account if an actual/expected P&L date was given.  If the item is holding or you have added some value since creating the item, you may be able to post an additional release to the PnL account.</p>`;
                    if(item.status === 'Active'){
                        status = '<p>This item has not yet completed posting the monthly amounts required.  You can only release value if the amount does not result in a negative balance once the automated processing is completed.</p>';
                    }else{
                        status = '<p>This has posted all of the period values.  If you release a value manually, you will have an imbalance until the final values are posted.</p>';
                    }
                }

                status += '<p>A posting will not immediately generate a journal entry.  Instead, nettTracker will include the posting in the next scheduled journal run for this company.  If you do not want a journal to be created, set the posting to be closed.</p>';

                const form = this.fb.group({
                    releaseDate: [item.pnLTransactionDate, [Validators.required]],
                    value: [item.balance, [Validators.required, Validators.max(item.balance)]],
                    postAndComplete: false,
                    closed: [false]
                });

                form.controls.postAndComplete.valueChanges.subscribe(nv => {
                    if(nv){
                        form.controls.value.setValue(item.balance);
                        form.controls.value.disable();
                    }else{
                        form.controls.value.enable();
                    }
                });

                const group: IGroupContainer = {
                    title: item.isAccrual ? 'Release Accrual' : 'Release Prepayment/Deferred Income',
                    description: desc + status,
                    showCompanyStatus: true,
                    company: company,
                    dialogMode: true,
                    collapseGroupNav: true,
                    actionText: 'Release',
                    form: form,
                    data: item,
                    groups: [
                        {
                            header: 'group',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Release date',
                                    allowEdit: false,
                                    form: form,
                                    data: item,
                                    items: [
                                        {label: 'Release date', control: 'datepicker', fieldName: 'releaseDate', maxDate: currentYear.yearEnd, minDate: currentYear.yearStart}
                                    ]
                                },
                                {
                                    header: 'Amount',
                                    allowEdit: false,
                                    form: form,
                                    data: item,
                                    items: [
                                        { label: 'Release amount', control: 'input', fieldName: 'value'}
                                    ]
                                },
                                {
                                    header: 'Marking as closed',
                                    description: 'A closed posting is one that has been finalised and will not be included in any journal entries.  Closed postings cannot be deleted.  You should only mark a posting as closed if you are certain that no further changes are required.',
                                    allowEdit: false,
                                    form: form,
                                    data: item,
                                    items: [{ label: 'Closed', control: 'checkbox', fieldName: 'closed' }]
                                }
                                
                            ]
                        }
                    ],
                    completeAction: async(dref: MatDialogRef<ModalEditorComponent>) => {
                        const model = {
                            id: id,
                            ...form.getRawValue()
                        };
                        const res = await this.api.accrualPrepaymentPostRelease(model, this.SelectedCompanyId);
                        
                        if(res.result === ApiResultValues.Ok){
                            dref.close();
                            this._selectedAccrualPrepaymentItem.next(res.data);
                            await this.refreshCompanySummaryData();
                            o.next(true);
                        }
                    },
                    cancelAction: (dref: MatDialogRef<ModalEditorComponent>) => {
                        dref.close();
                        o.next(null);
                    }
                }

                if(item.isAccrual){
                    group.groups[0].sections.push({
                        header: 'Post and complete',
                        description: 'To complete this accrual and stop further provisions, choose the option below',
                        allowEdit: false,
                        form: form,
                        data: item,
                        items: [
                            { label: 'Post and complete', control:'checkbox', fieldName: 'postAndComplete'}
                        ]
                    });
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
                this.closeLoadingSnack(snack);
            }
            asyncM();
        });
        return await firstValueFrom(obs);
    }

    async accrualPrepaymentPostAccrual(id: number){
        const obs = new Observable<any>(o => {
            const asyncM = async() => {
                const snack = await this.showLoadingSnack();
                const currentYear = await this.getCurrentFinancialYear();
                const item = await this.getAccrualPrepaymentItem(id);
                const company = this._selectedCompany.value;

                let desc = 'Additional accruals increase the balance sheet value of an accrual item, and post the corresponding value to the profit and loss account.  You can add additional accruals at any time.';
                
            
                const form = this.fb.group({
                    releaseDate: [item.pnLTransactionDate, [Validators.required]],
                    value: [item.balance, [Validators.required]],
                    closed: [false]
                });


                const group: IGroupContainer = {
                    title:  'Post Accrual Amount',
                    description: desc,
                    showCompanyStatus: true,
                    company: company,
                    dialogMode: true,
                    collapseGroupNav: true,
                    actionText: 'Post',
                    form: form,
                    data: item,
                    groups: [
                        {
                            header: 'group',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Accrual date date',
                                    allowEdit: false,
                                    form: form,
                                    data: item,
                                    items: [
                                        {label: 'Accrual date', control: 'datepicker', fieldName: 'releaseDate', maxDate: currentYear.yearEnd, minDate: currentYear.yearStart}
                                    ]
                                },
                                {
                                    header: 'Amount',
                                    allowEdit: false,
                                    form: form,
                                    data: item,
                                    items: [
                                        { label: 'Accrual amount', control: 'input', fieldName: 'value'}
                                    ]
                                },
                                {
                                    header: 'Marking as closed',
                                    description: 'A closed posting is one that has been finalised and will not be included in any journal entries.  Closed postings cannot be deleted.  You should only mark a posting as closed if you are certain that no further changes are required.',
                                    allowEdit: false,
                                    form: form,
                                    data: item,
                                    items: [{ label: 'Closed', control: 'checkbox', fieldName: 'closed' }]
                                }
                                
                            ]
                        }
                    ],
                    completeAction: async(dref: MatDialogRef<ModalEditorComponent>) => {
                        const model = {
                            id: id,
                            ...form.getRawValue()
                        };
                        const res = await this.api.accrualPrepaymentPostAccrual(model, this.SelectedCompanyId);
                        
                        if(res.result === ApiResultValues.Ok){
                            dref.close();
                            this._selectedAccrualPrepaymentItem.next(res.data);
                            await this.refreshCompanySummaryData();
                            o.next(true);
                        }
                    },
                    cancelAction: (dref: MatDialogRef<ModalEditorComponent>) => {
                        dref.close();
                        o.next(null);
                    }
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
                this.closeLoadingSnack(snack);
            }
            asyncM();
        });
        return await firstValueFrom(obs);
    }

    async deleteAccrualPrepaymentPosting(posting: any){
        const dlgRes = await  this.dlg.showConfirmDelete();
        if(dlgRes === DialogBoxResults.Yes){
            const res = await this.api.deleteAccrualPrepaymentPosting(posting.id, this.SelectedCompanyId);
            if(res.result === ApiResultValues.Ok){
                this._selectedAccrualPrepaymentItem.next(res.data);
                return true;
            }
        }

        return false;
    }

    async editAccrualPrepaymentPosting(p: any) {
        const obs = new Observable<any>(o => {

            const asyncMethod = async() => {
                const snack = await this.showLoadingSnack();
                const currentYear = await this.getCurrentFinancialYear();
                
                const form = this.fb.group({
                    postingDate: [p.postingDate, [Validators.required]],
                });

                const group: IGroupContainer = {
                    title: 'Update Posting Date',
                    description: '',
                    showCompanyStatus: true,
                    company: this._selectedCompany.value,
                    dialogMode: true,
                    collapseGroupNav: true,
                    actionText: 'Post',
                    form: form,
                    data: {},
                    groups: [
                        {
                            header: 'group',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Posting date',
                                    allowEdit: false,
                                    form: form,
                                    data: {},
                                    items: [
                                        {label: 'Posting date', control: 'datepicker', fieldName: 'postingDate', maxDate: currentYear.yearEnd, minDate: currentYear.yearStart}
                                    ]
                                }                                
                            ]
                        }
                    ],
                    completeAction: async(dref: MatDialogRef<ModalEditorComponent>) => {
                        const {postingDate} = form.getRawValue();
                        const res = await this.api.updateAccrualPrepaymentPosting(p.id, postingDate, this.SelectedCompanyId);
                        
                        if(res.result === ApiResultValues.Ok){
                            dref.close();
                            this._selectedAccrualPrepaymentItem.next(res.data);
                            await this.refreshCompanySummaryData();
                            o.next(true);
                        }
                    },
                    cancelAction: (dref: MatDialogRef<ModalEditorComponent>) => {
                        dref.close();
                        o.next(null);
                    }
                }
                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
                this.closeLoadingSnack(snack);
            }
            asyncMethod();
        });

        return await firstValueFrom(obs);
    }

    async deleteAccrualPrepaymentItem(id: number){
        const obs = new Observable<any>(o => {
            const asyncM = async() => {
                const snack = await this.showLoadingSnack();
                const currentYear = await this.getCurrentFinancialYear();
                const item = await this.getAccrualPrepaymentItem(id);

                const form = this.fb.group({
                    reverse: [true, Validators.required],
                    reversalDate: [currentYear.yearEnd, Validators.required]
                });

                const group: IGroupContainer = {
                    title: 'Delete Accrual/Prepayment Item',
                    description: 'You can delete an accrual/prepayment at any time.  If this item currently has a value, your balance sheet will be adjusted automatically (using date specified or year-end date).',
                    dialogMode: true,
                    company: this._selectedCompany.value,
                    showCompanyStatus: true,
                    collapseGroupNav: true,
                    actionText: 'Delete',
                    form: form,
                    data: item,
                    groups: [
                        {
                            header: 'group1',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Reversal date',
                                    description: 'Please enter the date for the reversal journal (must be within current financial year)',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        {label: 'Reversal date', control: 'datepicker', fieldName: 'reversalDate', maxDate: currentYear.yearEnd, minDate: currentYear.yearStart}
                                    ]
                                },{
                                    header: 'Post reversal journal',
                                    description: 'Please confirm you wish nettTracker to reverse current balances',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        {label: 'Post reversal journal', control: 'checkbox', fieldName: 'reverse'}
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const model = {
                            itemId: id,
                            ...form.getRawValue()
                        };
                        const dlg = await this.dlg.showPleaseWait('Please wait while we delete this item and post any journal entries required');
                        const delRes = await this.api.deleteAccrualPrepaymentItem(model, this.SelectedCompanyId);
                        await dlg.close();

                        if(delRes.result === ApiResultValues.Ok){
                            dRef.close();
                            this._selectedAccrualPrepaymentItem.next(null);
                            await this.refreshCompanySummaryData();
                            o.next(true);
                        }
                    }
                };

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
                this.closeLoadingSnack(snack);
            }
            asyncM();
        });

        return await firstValueFrom(obs);
    }

    async deleteAccrualPrepaymentReverse(accrual: any){
        const confirm = await this.dlg.showYesNo('Reverse accrual/prepayment deletion', 'Are you sure that you want to reverse the deletion of this accrual/prepayment?  This action cannot be undone.');
        if(confirm !== DialogBoxResults.Yes){
            return null;
        }

        const dlg = await this.dlg.showPleaseWait('Please wait while we dispose of the asset and post any journal entries required');

        const disResult = await this.api.deleteAccrualPrepaymentReverse(accrual.id, this.SelectedCompanyId);
        await dlg.close();

        if(disResult.result === ApiResultValues.Ok){
            await this.refreshCompanySummaryData();
            return true;
        }

        return false;
    }

    async downloadAccrualPrepaymentStatement(){
        const snack = await this.showLoadingSnack();
        const yearsList = await this.getFinancialYears();
        const dateInfo = this._selectedDateRange.value;
        const form = this.fb.group({
            startDate: [moment(dateInfo.startDate)],
            endDate: [moment(dateInfo.endDate)],
            yearId: [dateInfo.yearId.toString()]
        });

        form.controls.yearId.valueChanges.subscribe(v => {
            const year = yearsList.find(x=>x.id === parseInt(v));
            form.controls.startDate.setValue(moment(year.yearStart), {emitEvent: false});
            form.controls.endDate.setValue(moment(year.yearEnd), {emitEvent: false});
        })

        const c = this._selectedCompany.value;
        const region = c.region ? ( c.region.toLowerCase() === 'en-gb' ? 'DD/MM/YYYY' : 'MM/DD/YYYY' ): (c.defaultCurrency.toLowerCase() === 'usd' || c.defaultCurrency.toLowerCase() === 'cad') ? 'MM/DD/YYYY' : 'DD/YY/YYYY' ;

        const years = yearsList.map((y):IKeyValue => {
            const ys = moment(y.yearStart).format(region);
            const ye = moment(y.yearEnd).format(region);
            const v: IKeyValue = {
                key: y.id.toString(),
                value: `${ys} - ${ye}`
            }
            return v;
        });

        
        const obs = new Observable<any>(o => {
            const formItems: IItem[] = [
                {label: 'Financial year', control: 'select', fieldName: 'yearId', dropdownList: years }                
            ]

            if(c.companyVersion > 1){
                formItems.push({label: 'Start date', control: 'datepicker', fieldName: 'startDate'});
                formItems.push({label: 'End date', control: 'datepicker', fieldName: 'endDate'});
            }

            const group: IGroupContainer = {
                title: 'Statement Report',
                actionText: 'Export',
                description: 'Please confirm the date range to use on your accrual prepayment report',
                showCompanyStatus: true,
                company: c,
                dialogMode: true,
                collapseGroupNav: true,
                data: dateInfo,
                form: form,
                groups: [
                    {
                        header: 'group',
                        id: '1',
                        sections: [
                            {
                                header: 'Select date range',
                                description: 'Please choose a financial year or custom date range for your report',
                                form: form,
                                allowEdit: false,
                                items: formItems
                            }
                        ]
                    }
                ],
                cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                    dRef.close();
                    o.next(null);
                },
                completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                    const {startDate, endDate, yearId} = form.getRawValue();
                    let sd;
                    let ed;
                    if(startDate.format) {
                        sd = startDate.format('YYYY-MM-DD');
                    }else{
                        sd = startDate;
                    }
                    if(endDate.format) {
                        ed = endDate.format('YYYY-MM-DD');
                    }else{
                        ed = endDate;
                    }
                    if(!sd || !ed){
                        dRef.close();
                        o.next(false);
                    }else{
                        const dlg = await this.dlg.showPleaseWait('Please wait while we prepare your accrual prepayment report report');
                        this.api.downloadAccrualPrepaymentStatement(this.SelectedCompanyId, sd, ed, parseInt(yearId)).subscribe(result =>{
                            dlg.close();
                            if(result){
                                this.dlg.showOk('Accrual Prepayment Statement Report', `Your accrual prepayment statement report has now been downloaded`);
                            }
                            o.next(result);
                            dRef.close();
                        });
                    }
                    
                }
            }
            
            this.matDlg.open(ModalEditorComponent,
                {
                    data: group,
                    ...dialogDefaultFullScreen
                }
            );
            this.closeLoadingSnack(snack);
        });

        return await firstValueFrom(obs);
    }

    async downloadAccrualPrepaymentProjection(){
        const snack = await this.showLoadingSnack();
        const yearsList = await this.getFinancialYears();
        const dateInfo = this._selectedDateRange.value;
        const c = this._selectedCompany.value;
        const region = c.region ? ( c.region.toLowerCase() === 'en-gb' ? 'DD/MM/YYYY' : 'MM/DD/YYYY' ): (c.defaultCurrency.toLowerCase() === 'usd' || c.defaultCurrency.toLowerCase() === 'cad') ? 'MM/DD/YYYY' : 'DD/YY/YYYY' ;

        const form = this.fb.group({
            startDate: [moment(dateInfo.startDate)],
            endDate: [moment(dateInfo.endDate)],
            yearId: [dateInfo.yearId.toString()]
        });

        form.controls.yearId.valueChanges.subscribe(v => {
            const year = yearsList.find(x=>x.id === parseInt(v));
            form.controls.startDate.setValue(moment(year.yearStart), {emitEvent: false});
            form.controls.endDate.setValue(moment(year.yearEnd), {emitEvent: false});
        })

        
        const years = yearsList.map((y):IKeyValue => {
            const ys = moment(y.yearStart).format(region);
            const ye = moment(y.yearEnd).format(region);
            const v: IKeyValue = {
                key: y.id.toString(),
                value: `${ys} - ${ye}`
            }
            return v;
        });

        
        const obs = new Observable<any>(o => {
            const formItems: IItem[] = [
                {label: 'Financial year', control: 'select', fieldName: 'yearId', dropdownList: years }                
            ]

            if(c.companyVersion > 1){
                formItems.push({label: 'Start date', control: 'datepicker', fieldName: 'startDate'});
                formItems.push({label: 'End date', control: 'datepicker', fieldName: 'endDate'});
            }

            const group: IGroupContainer = {
                title: 'Projection Report',
                actionText: 'Export',
                description: 'Please confirm the date range to use on your accrual prepayment projection report',
                showCompanyStatus: true,
                company: c,
                dialogMode: true,
                collapseGroupNav: true,
                data: dateInfo,
                form: form,
                groups: [
                    {
                        header: 'group',
                        id: '1',
                        sections: [
                            {
                                header: 'Select date range',
                                description: 'Please choose a financial year or custom date range for your report',
                                form: form,
                                allowEdit: false,
                                items: formItems
                            }
                        ]
                    }
                ],
                cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                    dRef.close();
                    o.next(null);
                },
                completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                    const {startDate, endDate, yearId} = form.getRawValue();
                    let sd;
                    let ed;
                    if(startDate.format) {
                        sd = startDate.format('YYYY-MM-DD');
                    }else{
                        sd = startDate;
                    }
                    if(endDate.format) {
                        ed = endDate.format('YYYY-MM-DD');
                    }else{
                        ed = endDate;
                    }
                    if(!sd || !ed){
                        dRef.close();
                        o.next(false);
                    }else{
                        const dlg = await this.dlg.showPleaseWait('Please wait while we prepare your accrual prepayment report report');
                        this.api.downloadAccrualPrepaymentProjection(this.SelectedCompanyId, sd, ed, parseInt(yearId)).subscribe(result =>{
                            dlg.close();
                            if(result){
                                this.dlg.showOk('Accrual Prepayment Projection Report', `Your accrual prepayment projection report has now been downloaded`);
                            }
                            o.next(result);
                            dRef.close();
                        });
                    }
                    
                }
            }
            
            this.matDlg.open(ModalEditorComponent,
                {
                    data: group,
                    ...dialogDefaultFullScreen
                }
            );

            this.closeLoadingSnack(snack);
        });

        return await firstValueFrom(obs);
    }

    async deleteAsset(asset: any){
        const obs = new Observable<any>(o => {
            const asyncMethod = async() => {
                const snack = await this.showLoadingSnack();
                const currentValue = asset.summary ? asset.summary.curValue : asset.currentValue;
                const assetName = asset.summary ? asset.name : asset.assetName;
                const preflightRes = await this.api.deleteAssetPreflight(asset.id, this.SelectedCompanyId);

                if(preflightRes.result !== ApiResultValues.Ok){
                    o.next(null);
                    this.closeLoadingSnack(snack);
                    return;
                }

                const pfData = preflightRes.data;

                if(pfData.kitParentHasChildren){
                    await this.dlg.showOk('Delete asset', 'This asset is a kit parent and has children.  Please delete the children before deleting this asset');
                    o.next(null);
                    return;
                }

                const currentYear = await this.getCurrentFinancialYear();                
                const form = this.fb.group({
                    reverseCurrentYearDep: pfData.hasCurrentYearDep,
                    reversePrevYearDep: false,
                    acceptConsequences: [(pfData.importedCurrentFinancialYear || pfData.isCurrentFinancialYear), [Validators.requiredTrue]],
                    transactionInvocation: false,
                    deletionDate: [currentYear.yearEnd, [Validators.required]],
                    confirm: [false, [Validators.requiredTrue]]
                });

                const sections: ISection[] = [];
                sections.push({
                    header: 'Deletion date',
                    description:'Please enter the date of deletion.  This is the date a journal will be posted for and should be within the current financial year',
                    form: form,
                    allowEdit: false,
                    items: [
                        {label: 'Deletion date', control: 'datepicker', fieldName: 'deletionDate', minDate: currentYear.yearStart, maxDate: currentYear.yearEnd}
                    ]
                });
                if(pfData.hasCurrentYearDep){
                    sections.push({
                        header:'Current year depreciation',
                        description: 'This asset has depreciation posted for the current year, which should be reversed.',
                        form: form,
                        allowEdit: false,
                        items: [
                            {label: 'Reverse current year dep', control:'checkbox', fieldName: 'reverseCurrentYearDep'}
                        ]
                    });
                }
                if(!pfData.isCurrentFinancialYear){
                    let desc = '';
                    if(pfData.importedCurrentFinancialYear){
                        desc = 'This asset was acquired in a previous year however it was imported during the current financial year.  Please take care deleting this asset.'
                    }else{
                        desc = 'This asset was added to nettTracker during a previous financial year.  Deleting is not recomended and may cause an unexpected impact on your balance sheet.  Only continue if you are aware of the consequences';
                    }
                    sections.push({
                        header: 'Previous year depreciation',
                        description: desc,
                        form: form,
                        allowEdit: false,
                        items: [
                            {
                                label: 'Reverse previous year dep', control: 'checkbox', fieldName: 'reversePrevYearDep'
                            }
                        ]
                    });

                    //Extra confirmation because import was in a previous year and needs to be added to sections after one above
                    if(!pfData.importedCurrentFinancialYear){
                        sections.push({
                            header: 'Accept consequences',
                            description: 'Deleting this asset may cause an error in your balance sheet and prevent nettTracker from correctly balancing your accounts.  Please confirm your understanding of this.',
                            form: form,
                            allowEdit: false,
                            items: [
                                { label: 'Accept consequences', control: 'checkbox', fieldName: 'acceptConsequences'}
                            ]
                        });
                    }

                    
                }

                sections.push({
                    header: 'Confirm deletion',
                    description: 'Please tick box to confirm you wish to delete this asset',
                    form: form,
                    allowEdit: false,
                    items: [
                        { label: 'I confirm deletion', control: 'checkbox', fieldName: 'confirm'}
                    ]
                });

                const group: IGroupContainer = {
                    title:'Delete asset',
                    description: `<div style="display: flex"><h3 style="flex: 50">${assetName}</h3><h3 style="flex: 50">Current value: ${currentValue}</h3></div><p>Please specify the data in order to delete this asset.</p>`,
                    dialogMode: true,
                    showCompanyStatus: true,
                    company: this._selectedCompany.value,
                    collapseGroupNav: true,
                    data: pfData,
                    form: form,
                    groups: [
                        {
                            header: 'group1',
                            id: '100',
                            sections: sections
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const model = {
                            assetId: asset.id,
                            ...form.getRawValue()
                        };
                        const dlg = await this.dlg.showPleaseWait('Please wait while we delete this asset and post any journal entries required');
                        const delRes = await this.api.deleteAsset(asset.id, model, this.SelectedCompanyId);
                        await dlg.close();

                        if(delRes.result === ApiResultValues.Ok){
                            const delResData = delRes.data;
                            if(delResData.isDeleted){
                                dRef.close();
                                this._selectedAsset.next(null);
                                this.router.navigate(['/company', this.SelectedCompanyId, 'assets','statement']);
                                await this.refreshCompanySummaryData();
                                o.next(true);
                            }else{
                                await this.dlg.showOk('Deletion error',  `There was an error deleting this asset: ${delRes.message}`);
                            }
                        }

                    }
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
                this.closeLoadingSnack(snack);
            }

            asyncMethod();
        });

        return await firstValueFrom(obs);
    }

    async assetStartDepreciating(id: number){
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const snack = await this.showLoadingSnack();
                const currentYear = await this.getCurrentFinancialYear();
                const asset = await this.getAsset(id);

                if(!asset){
                    o.next(null);
                    return;
                }

                const form = this.fb.group({
                    depreciationCommenceDate: ['',[Validators.required]]
                });

                const group: IGroupContainer = {
                    title: 'Start asset depreciation',
                    description: `<div style="display: flex"><h3 style="flex: 50">${asset.assetName}</h3></div><p>Use this tool to start the asset depreciating from the date you select.  Once start, the depreciation cannot be stopped.</p>`,
                    dialogMode: true,
                    collapseGroupNav: true,
                    form: form,
                    data: asset,
                    groups: [
                        {
                            header: 'Group1',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Depreciation start date',
                                    description: 'Enter depreciation start date',
                                    allowEdit: false,
                                    form: form,
                                    data: asset,
                                    items: [
                                        {label: 'Depreciation start date', control: 'datepicker', fieldName: 'depreciationCommenceDate'}
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const model = form.getRawValue();
                        const apiRes = await this.api.assetStartDepreciating(id, model, this.SelectedCompanyId);
                        if(apiRes.result === ApiResultValues.Ok){
                            this._selectedAsset.next(apiRes.data);
                            o.next(apiRes.data);
                            dRef.close();
                        }
                    }
                }

                

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
                this.closeLoadingSnack(snack);

            }
            asyncF();
        });

        return await firstValueFrom(obs);
    }

    async setTransactionIgnored(id: number): Promise<boolean>{
        const res = await this.dlg.showYesNo('Ignore transaction', 'Are you sure that you want to mark this transaction as ignored?  It related to a balance sheet account and may result in an issue reconciling your asset balance.');
        if(res === DialogBoxResults.Yes){
            const apiRes = await this.api.updateTransactionIgnored(id, this.SelectedCompanyId);
            if(apiRes.result === ApiResultValues.Ok){
                return true;
            }else{
                return false;
            }
        }else{
            return false;
        }
    }

    async setTransactionRevisedIgnored(transaction:any, assetId?: number): Promise<boolean> {
        if(!transaction){
            const a = await this.getAsset(assetId);
            if(!a){
                return false;
            }

            const res = await this.api.getTransaction(a.localTransactionId, this.SelectedCompanyId);
            if(res.result !== ApiResultValues.Ok){
                await this.dlg.showOk('Cannot identify transaction', 'We were unable to identify the transaction that this asset is related to.  Please contact support for further assistance.');
                return false;
            }else{
                transaction = res.data;
            }
        }

        const res = await this.dlg.showYesNo('Ignore revised transaction', 'Are you sure that you want to mark this updated/revised transaction as ignored?  It related to a balance sheet account and may result in an issue reconciling your asset balance.');
        if(res === DialogBoxResults.Yes){
            const apiRes = await this.api.updateTransactionRevisedIgnore(transaction.id, this.SelectedCompanyId);
            if(apiRes.result === ApiResultValues.Ok){
                return true;
            }else{
                return false;
            }
        }else{
            return false;
        }
    }

    async updateTransactionRevisedAsset(transaction: any, assetId?: number): Promise<boolean> {
        if(!transaction){
            const a = await this.getAsset(assetId);
            if(!a){
                return false;
            }

            const res = await this.api.getTransaction(a.localTransactionId, this.SelectedCompanyId);
            if(res.result !== ApiResultValues.Ok){
                await this.dlg.showOk('Cannot identify transaction', 'We were unable to identify the transaction that this asset is related to.  Please contact support for further assistance.');
                return false;
            }else{
                transaction = res.data;
            }
        }

        let msg = '';
        let title = '';
        if(transaction.actionTaken === 'Recoded'){
            title = 'Recoded transaction';
            msg = `This transaction has been modified such that it is either no longer in a balance sheet account mapped to nettTracker, or
                has had its transaction date modified so that it is no longer within the current year.  An asset has been created using this transaction and therefore, needs to be removed from your asset register and any
                depreciation posted will need to be reversed.  This asset is currently shown in your asset register as 'Eroneous'.  Are you sure you wish to complete this process?`;  
        }else if(transaction.actionTaken === 'ModifiedAccount'){
            title = 'Modified account transaction';
            msg = `This transaction has been modified so that it is now in a different balance sheet account to the one that it was allocated
                to when first seen by nettTracker.  An asset has been created using this transaction and therefore needs to be revised with the new account information and
                any depreciation processed must be reversed.  To achieve this, nettTracker will now remove the existing asset, reverse posted
                depreciation and create a new asset with the updated transaction information.  Are you sure you wish to complete this process?`
        }else if(transaction.actionTaken === 'ModifiedAmount'){
            title = 'Modified amount transaction';
            msg = ` This transaction has been modified so that it is now has a different transaction value to the one that it was allocated
                    to when first seen by nettTracker.  An asset has been created using this transaction and therefore needs to be revised with the new transaction information and
                    any depreciation processed must be reversed.  To achieve this, nettTracker will now remove the existing asset, reverse posted
                    depreciation and create a new asset with the updated transaction information.  Are you sure you wish to complete this process?`
        }else{
            await this.dlg.showOk('Unable to update transaction', 'We were unable to identify the action taaken on this transaction and therefore cannot update the asset related to it.');
            return false;
        }

        const res = await this.dlg.showYesNo(title, msg);
        if(res === DialogBoxResults.Yes){
            const dlg = await this.dlg.showPleaseWait('Please wait while we perform action on the asset and transaction');
            const apiRes = await this.api.updateTransactionAsset(transaction.id, this.SelectedCompanyId);
            await dlg.close();
            if(apiRes.result === ApiResultValues.Ok){
                return true;
            }else{
                return false;
            }
        }else{
            return false;
        }
    }

    async updateTransactionRevisedPrepayment(transaction: any, itemId?: number): Promise<boolean> {
        if(!transaction){
            const a = await this.getAccrualPrepaymentItem(itemId);
            if(!a){
                return false;
            }

            const res = await this.api.getTransaction(a.localTransactionId, this.SelectedCompanyId);
            if(res.result !== ApiResultValues.Ok){
                await this.dlg.showOk('Cannot identify transaction', 'We were unable to identify the transaction that this prepayment is related to.  Please contact support for further assistance.');
                return false;
            }else{
                transaction = res.data;
            }
        }

        let msg = '';
        let title = '';
        if(transaction.actionTaken === 'Recoded'){
            title = 'Recoded transaction';
            msg = `This transaction has been modified such that it is either no longer in a balance sheet account mapped to nettTracker, or
                has had its transaction date modified so that it is no longer within the current year.  An prepayment has been created using this transaction and therefore, needs to be removed from your prepayments and any
                adjustments posted will need to be reversed.  This prepayment is currently shown in your statement as 'Eroneous'.  Are you sure you wish to complete this process?`;  
        }else if(transaction.actionTaken === 'ModifiedAccount'){
            title = 'Modified account transaction';
            msg = `This transaction has been modified so that it is now in a different balance sheet account to the one that it was allocated
                to when first seen by nettTracker.  An prepayment has been created using this transaction and therefore needs to be revised with the new account information and
                any depreciation processed must be reversed.  To achieve this, nettTracker will now remove the existing prepayment and reverse postings, leaving the updated transaction ready to be 
                used again.  Are you sure you wish to complete this process?`
        }else if(transaction.actionTaken === 'ModifiedAmount'){
            title = 'Modified amount transaction';
            msg = ` This transaction has been modified so that it is now has a different transaction value to the one that it was allocated
                    to when first seen by nettTracker.  A prepayment has been created using this transaction and therefore needs to be revised with the new transaction information and
                    any postings processed must be reversed.  To achieve this, nettTracker will now remove the existing prepayment, reverse postings and create a new prepayment with the
                    updated transaction information.  Are you sure you wish to complete this process?`
        }else{
            await this.dlg.showOk('Unable to update transaction', 'We were unable to identify the action taken on this transaction and therefore cannot update the prepayment related to it.');
            return false;
        }

        const res = await this.dlg.showYesNo(title, msg);
        if(res === DialogBoxResults.Yes){
            const dlg = await this.dlg.showPleaseWait('Please wait while we perform action on the prepayment and transaction');
            const apiRes = await this.api.updateTransactionPrepayment(transaction.id, this.SelectedCompanyId);
            await dlg.close();
            if(apiRes.result === ApiResultValues.Ok){
                return true;
            }else{
                return false;
            }
        }else{
            return false;
        }
    }

    async getCanCompanyPost(option: CompanyPostingOptions): Promise<boolean>{
        const apires = await this.api.getCompanyCanPost(this.SelectedCompanyId, option);
        if(apires.result === ApiResultValues.Ok){
            return true;
        }else{
            await this.dlg.showOk('Company posting status', 'The company is does not have the necessary mappings in order to make postings to your online accounts for the purpose you need.  Please correct your configuration and try again.');
            return false;
        }
    }

    async processPeriodEnd(){
        const obs = new Observable<any>(o => {
            const asyncMethod = async() => {
                const snack = await this.showLoadingSnack();
                const checkPost = await this.getCanCompanyPost(CompanyPostingOptions.Periodic);
                const cyear = await this.getCurrentFinancialYear();

                if(!checkPost){
                    this.closeLoadingSnack(snack);
                    return;
                }

                const form = this.fb.group({
                    processingDate: [this._selectedCompany.value.nextProcessingDate, [Validators.required]]
                });

                const data = {processingDate: this._selectedCompany.value.nextProcessingDate};

                const group: IGroupContainer = {
                    title: 'Process Period End',
                    actionText: 'Run',
                    description: 'This tool will make the necessary postings for your company up to the selected processing date.  *IMPORTANT: if adjusting for prior periods, please ensure the books are not closed in your accounting software as this will prevent journal entries from being posted correctly.',
                    dialogMode: true,
                    showCompanyStatus: true,
                    company: this._selectedCompany.value,
                    collapseGroupNav: true,
                    form: form,
                    data: data,
                    groups: [
                        {
                            header: 'group1',
                            id: '1',
                            sections: [
                                {
                                    header: 'Period end date',
                                    description: 'Please confirm the period end date to process',
                                    allowEdit: false,
                                    form: form,
                                    data: data,
                                    items: [
                                        {label: 'Period end date', control: 'datepicker', fieldName: 'processingDate', maxDate: cyear.yearEnd, minDate: this._selectedCompany.value.processingStartDate}
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const {processingDate } = form.getRawValue();
                        let waitMsg = `Please wait while we make your postings.  Warning: if your company has a large number of items that may require processing, which may take more than 10 minutes to complete.  It is important you do not request again if your request times-out.`;
                        const dlg = await this.dlg.showPleaseWait(waitMsg);
                        const apiRes = await this.api.processPeriods(processingDate, this.SelectedCompanyId);

                        if(apiRes.result === ApiResultValues.Ok){
                            const updatedCompany = await this.api.getCompany(this.SelectedCompanyId);
                            const companyList = this._companyList.value;
                            
                            const currentCompany = companyList.find(x=>x.safeId === this.SelectedCompanyId);
                            const idx = companyList.indexOf(currentCompany);
                            companyList[idx]=updatedCompany.data;

                            this._companyList.next(companyList);
                            this._selectedCompany.next(updatedCompany.data);

                            o.next(true);

                            await dlg.close();
                            dRef.close();
                        }else{
                            await dlg.close();
                        }
                    }
                }

                this.matDlg.open(ModalEditorComponent,
                        {
                            data: group,
                            ...dialogDefaultFullScreen
                        }
                );

                this.closeLoadingSnack(snack);
            }

            asyncMethod();
        });

        return await firstValueFrom(obs);
    }

    async processYearEndPerpetual(){
        const obs = new Observable<any>(o => {
            const asyncFunc = async() => {
                const company = this._selectedCompany.value;

                if(!company.perpetualFinancialYear){
                    await this.dlg.showOk("Process year end", "This company is not configured to use perpetual financial years and therefore cannot have this process applied to it.");
                    o.next(null);
                    return;
                }

                if(!company.useYearEndJournals){
                    await this.dlg.showOk("Process year end", "This company is not configured to use year-end adjustment journals and therefore cannot have this process applied to it.");
                    o.next(null);
                    return;
                }
                
                const yearsList = await this.getFinancialYears();
                const region = company.region ? ( company.region.toLowerCase() === 'en-gb' ? 'DD/MM/YYYY' : 'MM/DD/YYYY' ): (company.defaultCurrency.toLowerCase() === 'usd' || company.defaultCurrency.toLowerCase() === 'cad') ? 'MM/DD/YYYY' : 'DD/MM/YYYY' ;

                const form = this.fb.group({
                    yearId: []
                });

                form.controls.yearId.valueChanges.subscribe(v => {
                    const year = yearsList.find(x=>x.id === parseInt(v));
                    if(!year.closed){
                        this.dlg.showOk('Year not closed', 'The selected financial year is not yet closed.  Year-end adjustment journals should only be posted for closed financial years.');
                    }
                });

                const years = yearsList.map((y):IKeyValue => {
                    const ys = moment(y.yearStart).format(region);
                    const ye = moment(y.yearEnd).format(region);
                    const v: IKeyValue = {
                        key: y.id.toString(),
                        value: `${ys} - ${ye}${y.closed ? '' : ' (ACTIVE YEAR)'}${y.completed ? ' (JOURNALS ALREADY POSTED FOR THIS YEAR)' : ''}`
                    }
                    return v;
                });

                const groupComplete: IGroupContainer = {
                    title: 'Post Year-End Adjustment Journals',
                    description: '<p>This tool will post any year-end adjustment journals required for the selected financial year.  Please ensure you have reviewed the year-end draft report prior to posting these journals to ensure all adjustments are correct.  This journal cannot be reversed within nettTracker, so please take care when posting.</p><p>If you select a year that is marked as Completed, you will be potentially creating a duplicate journal entry.</p>',
                    dialogMode: true,
                    actionText: 'Post Journals',
                    showCompanyStatus: true,
                    company: company,
                    collapseGroupNav: true,
                    form: form,
                    data: {},
                    groups: [
                        {
                            header: 'group1',
                            id: '1',
                            sections: [
                                {
                                    header: 'Year to process',
                                    description: 'Please confirm the year for which you want to post year-end adjustment journals',
                                    allowEdit: false,
                                    form: form,
                                    data: {},
                                    items: [
                                        {label: 'Year to process', control: 'select', fieldName: 'yearId', dropdownList: years}
                                    ]
                                },
                                
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const {yearId} = form.getRawValue();
                        const year = yearsList.find(x=>x.id === parseInt(yearId));

                        if(year.completed){
                            const confirmRes = await this.dlg.showYesNo('Year end already processed', 'The year-end journals have already been posted for the selected financial year.  Are you sure you would like to post futher journals?   This is likely to create a duplicate posting in your accounts package.');
                            if(confirmRes !== DialogBoxResults.Yes){
                                return;
                            }
                        }

                        let waitMsg = `Please wait while we run a preflight check on your data to ensure it is ready for year-end journals.`;
                        let dlg = await this.dlg.showPleaseWait(waitMsg);
                        let apiRes = await this.api.processYearEndPreflightPerpetual(yearId, this.SelectedCompanyId);
                        await dlg.close();

                        if(apiRes.result !== ApiResultValues.Ok){
                            return;
                        }

                        const result = apiRes.data.result;

                        if(result == 'MissingConfiguration'){
                            await this.dlg.showOk('Year end preflight check', 'Your company is missing some configuration needed to process the year end journals.  Please review your company configuration and try again.');
                        }

                        if(result == 'YearCompleted'){
                            const confirmRes = await this.dlg.showOk('Year end preflight check', 'The year end process has already been completed for the selected financial year.  Are you sure you wish to process again?');
                            if(confirmRes !== DialogBoxResults.Yes){
                                return;
                            }
                        }

                        if(result === 'NoJournalCreated'){
                            await this.dlg.showOk('Year end preflight check', 'There are no year-end adjustment journals to post for the selected financial year.  No further action is required.');
                            o.next(true);
                            
                            dRef.close();
                            return;
                        }

                        const finalConfirm = await this.dlg.showYesNo('Confirm year-end journal posting', 'The preflight check has completed successfully.  Are you sure you wish to post the year-end adjustment journals for the selected financial year?  This action cannot be reversed within nettTracker.');
                        if(finalConfirm !== DialogBoxResults.Yes){
                            o.next(true);
                            
                            dRef.close();
                            return;
                        }

                        const model = {
                            yearId: yearId,
                            postJournals: true
                        };

                        waitMsg = `Please wait while we make your postings.  Warning: if your company has a large number of items that may require processing, which may take more than 10 minutes to complete.  It is important you do not request again if your request times-out.`;
                        dlg = await this.dlg.showPleaseWait(waitMsg);
                        apiRes = await this.api.processYearEnd(model, this.SelectedCompanyId);
                        await dlg.close();

                        if(apiRes.result === ApiResultValues.Ok){
                            const updatedCompany = await this.api.getCompany(this.SelectedCompanyId);
                            const companyList = this._companyList.value;
                            const currentCompany = companyList.find(x=>x.safeId === this.SelectedCompanyId);
                            const idx = companyList.indexOf(currentCompany);
                            companyList[idx]=updatedCompany.data;

                            this._companyList.next(companyList);
                            this._selectedCompany.next(updatedCompany.data);

                            o.next(true);

                            
                            dRef.close();
                        }
                    }
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: groupComplete,
                        ...dialogDefaultFullScreen
                    }
                );
            };
            asyncFunc();
        });

        return await firstValueFrom(obs);

    }

    async processYearEnd() {
        const obs = new Observable<any>(o => {
            const asyncFunc = async() => {
                const company = this._selectedCompany.value;

                if(!company.awaitingYearEnd){
                    await this.dlg.showOk("Process year end", "This company is not awaiting its year end processing and cannot have this process applied to it.");
                    o.next(null);
                    return;
                }

                if(company.perpetualFinancialYear){
                    await this.dlg.showOk("Process year end", "This company is configured to use perpetual financial years and therefore cannot have a year end process applied to it.");
                    o.next(null);
                    return;
                }

                const draftComplete = company.yearEndDraftComplete;
                const proposedYE = moment(company.financialYearEnd).add(1, 'y');
                const maxDate = moment(company.financialYearEnd).add(18, 'M').toDate();
                const minDate = moment(company.financialYearEnd).add(1, 'M').toDate();
                const data = {endOfYearDate: proposedYE, postJournals: company.useYearEndJournals};

                if(!draftComplete){
                    const dlgRes = await this.dlg.showYesNo('Process year end', 'Would you like to begin the year-end process on this company?  We will need to run checks on your data and prepare any write-offs that are needed.  You can then edit the adustment period values to make any amendments');
                    if(dlgRes === DialogBoxResults.Yes){
                        const apiRes = await this.api.processYearEnd(data, this.SelectedCompanyId);

                        if(apiRes.result === ApiResultValues.Ok){
                            const updatedCompany = await this.api.getCompany(this.SelectedCompanyId);
                            const companyList = this._companyList.value;
                            const currentCompany = companyList.find(x=>x.safeId === this.SelectedCompanyId);
                            const idx = companyList.indexOf(currentCompany);
                            companyList[idx]=updatedCompany.data;

                            this._companyList.next(companyList);
                            this._selectedCompany.next(updatedCompany.data);
                            this.router.navigate(['/company', updatedCompany.safeId]);

                            o.next(true);
                        }else{
                            await this.dlg.showOk('Process year end', 'There was an error starting the year-end process for this company.');
                            o.next(false);
                        }
                    }else{
                        o.next(false);
                    }

                    return;
                }

                const form = this.fb.group({
                    endOfYearDate: [proposedYE, Validators.required],
                    postJournals: {value: company.useYearEndJournals, disabled: true} 
                });

                form.controls.endOfYearDate.valueChanges.subscribe(v => {
                    const d = moment(v).endOf('month').startOf('day');
                    const vDate = moment(v);
                    if (d.diff(vDate, 'days') > 0) {
                        form.controls.endOfYearDate.setValue(d, { emitEvent: false })
                    }
                });

                const big = this._companySummary.value ?  this._companySummary.value.farSummary?.assetCount > 500 : false;

                const groupComplete: IGroupContainer = {
                    title: 'Process Year End',
                    description: 'This tool will complete your nettTracker year-end, post adjustment journals as required and create any end of year adjustments that are configured for your company.  It is non-reversible and cannot be completed if there are pending period end postings or transactions that have not been processed.',
                    dialogMode: true,
                    actionText: big ? 'Process Year End (This may take a while)' : 'Process Year End',
                    showCompanyStatus: true,
                    company: company,
                    collapseGroupNav: true,
                    form: form,
                    data: data,
                    groups: [
                        {
                            header: 'group1',
                            id: '1',
                            sections: [
                                {
                                    header: 'Next year end date',
                                    description: 'Please confirm the date for the next year end',
                                    allowEdit: false,
                                    form: form,
                                    data: data,
                                    items: [
                                        {label: 'Year end date', control: 'datepicker', fieldName: 'endOfYearDate', minDate: minDate, maxDate: maxDate}
                                    ]
                                },
                                
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const model = form.getRawValue();
                        let waitMsg = `Please wait while we make your postings.  Warning: if your company has a large number of items that may require processing, which may take more than 10 minutes to complete.  It is important you do not request again if your request times-out.`;
                        const dlg = await this.dlg.showPleaseWait(waitMsg);
                        const apiRes = await this.api.processYearEnd(model, this.SelectedCompanyId);

                        if(apiRes.result === ApiResultValues.Ok){
                            const updatedCompany = await this.api.getCompany(this.SelectedCompanyId);
                            const companyList = this._companyList.value;
                            const currentCompany = companyList.find(x=>x.safeId === this.SelectedCompanyId);
                            const idx = companyList.indexOf(currentCompany);
                            companyList[idx]=updatedCompany.data;

                            this._companyList.next(companyList);
                            this._selectedCompany.next(updatedCompany.data);

                            o.next(true);

                            await dlg.close();
                            dRef.close();
                        }else{
                            await dlg.close();
                        }
                    }
                }

                if(company.useYearEndJournals){
                    groupComplete.groups[0].sections.push({
                        header: 'Post year end journals',
                        description: 'If your company is configured to make year-end postings, this option cannot be changed',
                        allowEdit: false,
                        form: form,
                        data: data,
                        items: [
                            {label: 'Post journals', control: 'checkbox', fieldName: 'postJournals'}
                        ]
                    });
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: groupComplete,
                        ...dialogDefaultFullScreen
                    }
                );
            }

            asyncFunc();
        });

        return await firstValueFrom(obs);
    }

    async donloadAssetImportTemplate(){
        const obs = this.api.downloadAssetImportTemplate(this.SelectedCompanyId);

        return await firstValueFrom(obs);
    }

    async uploadAssets(file: any){
        const dlg = await this.dlg.showPleaseWait("Please wait while we upload your assets and extract the data");

        const uploadResult = this.api.importAssetsFileUpload(file, this.SelectedCompanyId);

        const data = await firstValueFrom(uploadResult.data);

        dlg.close();

        return data;
    }

    async importAssets(data: AssetUploadModel[]): Promise<IApiQueryResult>
    {
        const dlg = await this.dlg.showPleaseWait("Please wait while we create the assets for your imported data.  This may take some time, as we also create depreciation projections while importing.");

        const importResult = await this.api.importAssets(data, this.SelectedCompanyId);

        dlg.close();

        if(importResult.result === ApiResultValues.Ok){
            this.refreshCompanySummaryData();
        }
        
        return importResult;
    }



    async donloadAccrualImportTemplate(type: string){
        const obs = this.api.downloadAccrualPrepaymentTemplate(this.SelectedCompanyId, type);

        return await firstValueFrom(obs);
    }

    async uploadAccrualPrepayments(file: any){
        const dlg = await this.dlg.showPleaseWait("Please wait while we upload your accrual and prepayments and extract the data");

        const uploadResult = this.api.importAcrrualsFileUpload(file, this.SelectedCompanyId);

        const data = await firstValueFrom(uploadResult.data);

        dlg.close();

        return data;
    }

    async importAccrualPrepayments(data: AccrualPrepaymentUploadModel[]): Promise<IApiResult>
    {
        const dlg = await this.dlg.showPleaseWait("Please wait while we create the accrual and prepayments for your imported data.  This may take some time, as we also create projections while importing.");

        const importResult = await this.api.importAccrualPrepayments(data, this.SelectedCompanyId);

        dlg.close();

        if(importResult.result === ApiResultValues.Ok){
            this.refreshCompanySummaryData()
        }
        
        return importResult;
    }



    //private classCache: IKeyValue[] = [];
    //private locationCache: IKeyValue[] = [];

    private classCache = new BehaviorSubject<any[]>(null);
    get ClassTrackingData() {
        return this.classCache.asObservable();
    }

    private locationCache = new BehaviorSubject<any[]>(null);
    get LocationTrackingData() {
        return this.locationCache.asObservable();
    }
    
    // getCompanyProviderTrackingData(option: string): IKeyValue[] {
    //     if(option === 'class'){
    //         return this.classCache;
    //     }else{
    //         return this.locationCache;
    //     }
    // }

    private async getCompanyProviderTrackingDataInteral(supressDialog: boolean): Promise<void>{
        let dlg;
        if(!supressDialog){
            dlg = await this.dlg.showPleaseWait("Please wait while we retrieve the tracking category data for your company.");
        }

        const res = await this.api.getCompanyProviderTrackingData(this.SelectedCompanyId);

        if(res.result === ApiResultValues.Ok){
            this.dlg.createLog('Company tracking data has been received', '', res.data.class, res.data.location);
            const classCache = res.data.class.map(m => {
                return{
                    key: m,
                    value: m
                };
            });
            this.classCache.next(classCache);
            const locationCache = res.data.location.map(m => {
                return{
                    key: m,
                    value: m
                };
            });
            this.locationCache.next(locationCache);
        }else{
            this.dlg.createLog('Company tracking data request received an error', '', res.data);
            this.locationCache.next([]);
            this.classCache.next([]);
        }

        if(dlg){
            dlg.close();
        }     
    }

    private _selectedAccrualPrepaymentItem = new BehaviorSubject<any | null>(null);
    get SelectedAccrualPrepaymentItem() {
        return this._selectedAccrualPrepaymentItem.asObservable();
    }
    private _createdAccrualPrepaymentItem = new BehaviorSubject<any | null>(null);
    get CreatedAccrualPrepaymentItem() {
        return this._createdAccrualPrepaymentItem.asObservable();
    }

    


    async getYearAccrualPrepaymentStatement(groupId: number,
            yearId: number,
            startDate: string,
            endDate: string,
            summary: boolean,
            pageIndex: number,
            pageSize: number,
            searchTerm: string,
            sortDesc: boolean,
            sortItem: string,
        ){

        const model = {
            groupId,
            yearId,
            summary,
            pageIndex,
            pageSize,
            searchTerm,
            sortDesc,
            sortItem,
            startDate,
            endDate
        };

        return await this.api.getAccrualPrepaymentStatement(model, this.SelectedCompanyId);
    }

    async getYearAccrualPrepaymentProjection(type: string,
            groupId: number,
            yearId: number,
            startDate: string,
            endDate: string,
            summary: boolean,
            pageIndex: number,
            pageSize: number,
            searchTerm: string,
            sortDesc: boolean,
            sortItem: string){
        const model = {
            type,
            groupId,
            yearId,
            startDate,
            endDate,
            summary,
            pageIndex,
            pageSize,
            searchTerm,
            sortDesc,
            sortItem,
            reloadExternalBalance: false
        };    

        return await this.api.getAccrualPrepaymentProjection(model, this.SelectedCompanyId);
    }



    async getAccrualPrepaymentItem(id: number): Promise<any> {
        const current = this._selectedAccrualPrepaymentItem.value;
        if(current){
            if(current.id === id){
                return current;
            }
        }

        const apiRes = await this.api.getAccrualPrepaymentItem(id, this.SelectedCompanyId);
        if(apiRes.result === ApiResultValues.Ok){
            this._selectedAccrualPrepaymentItem.next(apiRes.data);
            return apiRes.data;
        }else{
            return null;
        }
    }

    async getAccrualPrepaymentEntity(id: number): Promise<IApiResult> {
        const apiRes = await this.api.getAccrualPrepaymentEntity(id, this.SelectedCompanyId);

        return apiRes.data;
    }

    async getAccrualPaymentItems(): Promise<IApiQueryResult> {
        const apiRes = await this.api.getAccrualPrepaymentItems(this.SelectedCompanyId);

        return apiRes;

    }

    async getAccrualPaymentItemsDeleted(): Promise<IApiQueryResult> {
        const apiRes = await this.api.getAccrualPrepaymentItemsDeleted(this.SelectedCompanyId);

        return apiRes;

    }

    async accrualPrepaymentBulkNominalUpdate(items: any[]): Promise<IApiResult> {
        const dlg = await this.dlg.showPleaseWait("Please wait while we update your items with new accounts");
        const apiRes = await this.api.accrualPrepaymentBulkNominalUpdate(items, this.SelectedCompanyId);
        dlg.close();
        return apiRes;
    }

    async viewAccrualPrepaymentItem(id: number) {
        const res = await this.getAccrualPrepaymentItem(id);
        if(res){
            this.router.navigate(['/company', this.SelectedCompanyId, 'accrual-prepayments', 'details', id]);
        }
    }

    async getAccrualPrepaymentItemForTransaction(id: number){
        const res = await this.api.getAccrualPrepaymentItemForTransaction(id, this.SelectedCompanyId);
        if(res.result === ApiResultValues.Ok){
            this._selectedAccrualPrepaymentItem.next(res.data);
            this.router.navigate(['/company', this.SelectedCompanyId, 'accrual-prepayments','details', res.data.id]);
        }
    }

    async createAccrualPrepaymentItem(){
        const snack = await this.showLoadingSnack();
        const newItem = {id: 0, pnLAccountId: 0};
        const resultItem = await this.updateAccrualPrepaymentItem(newItem, 0, true, snack);
        if(resultItem){
            this._createdAccrualPrepaymentItem.next(resultItem);
            this._selectedAccrualPrepaymentItem.next(resultItem);
        }

        return resultItem;
    }

    async createPrepaymentTransaction(id: number){
        const snack = await this.showLoadingSnack();
        const newItem = await this.api.getBuildPrepaymentForTransaction(id, this.SelectedCompanyId);
        if(newItem.result === ApiResultValues.Ok){
            const item = newItem.data;
            const resultItem = await this.updateAccrualPrepaymentItem(item, id, true, snack);
            //Do we want to do a redirect?
            //this.router.navigate(['/company', this.SelectedCompanyId, 'assets', 'details', apiRes.data.id]);
            if(resultItem){
                this._createdAccrualPrepaymentItem.next(resultItem);
            this._selectedAccrualPrepaymentItem.next(resultItem);
            }
            return resultItem;
        }else{
            this.closeLoadingSnack(snack);
        }
    }

    async editAccrualPrepaymentItem(id: number) {
        const snack = await this.showLoadingSnack();
        const item = await this.getAccrualPrepaymentEntity(id);
        if(item){
            const updateItem = await this.updateAccrualPrepaymentItem(item, 0, false, snack);
            this._selectedAccrualPrepaymentItem.next(updateItem);
            return updateItem;
        }else{
            this.closeLoadingSnack(snack);
            return null;
        }
    }

    private async updateAccrualPrepaymentItem(entity: any,transactionId: number, isNew: boolean, snack: any){
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const groupsFull = await this.getAccrualPrepaymentGroups();

                if(groupsFull.length === 0){
                    this.closeLoadingSnack(snack);
                    await this.dlg.showOk('No accrual/prepayment groups', 'You must first create accrual/prepayment groups before you can create an accrual/prepayment item.  Please create the necessary groups from company settings and try again.');
                    o.next(null);
                    return;
                }
                const groups = groupsFull.map(x=>{
                    return {
                        key: x.id,
                        value: x.groupName
                    };
                });
                
                const company = this._selectedCompany.value;
                const isStandalone = company.externalDataSource.dataSource.toLowerCase() === 'standalone';
                const currentYear = await this.getCurrentFinancialYear();
                const accounts = await this.getNominalAccounts();
                accounts.unshift({id: 0, name: '- please select an account'});
                accounts.unshift({id: -1, name: 'Create new nominal account for mapping'});

                const periods = [];
                if(company.companyVersion > 1){
                    periods.push({key: 0, value: 'Hold balance until reversed'});
                }

                for(let x = 1; x < 121; x++){
                    periods.push({key: x, value: `${x} month${x > 1 ? 's' : ''}`});
                }

                const isTransaction = transactionId > 0;
                const inactveItem = entity.status ? entity.status === 'Pending' || (entity.status === 'Holding' && entity.periodCount === 0 ) : true;
                const enableEdit = isNew || inactveItem;

                const form = this.fb.group({
                    groupId: [{value: entity.groupId, disabled: !isNew || isTransaction}, [Validators.required, NotValueValidatorDirective.notValueValidatorFactory(0)]],
                    title: [entity.title, [Validators.required, FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    notes: entity.notes,
                    class: entity.class,
                    location: entity.location,
                    pnLTransactionDate: [{value: entity.pnLTransactionDate, disabled: isTransaction}],
                    totalAmount: [{value: entity.totalAmount, disabled: (!enableEdit || isTransaction)}, [Validators.required, Validators.min(1)]],
                    periodStart: [{value: entity.periodStart, disabled: (!enableEdit)}, [Validators.required]],
                    periodCount: [{value: entity.periodCount, disabled: (!enableEdit)}, [Validators.required]],
                    pnLAccountId: [{value: entity.pnLAccountId, disabled: (!enableEdit)}, [NotValueValidatorDirective.notValueValidatorFactory(0)]],
                    reversalPosted: [{value: entity.reversalPosted, disabled: isTransaction || !enableEdit}],
                    accruedTotal: [{value: entity.accruedTotal, disabled: (!enableEdit)}]
                });

                if(company.enableClass){
                    form.controls.class.addValidators(Validators.required);
                }
                if(!isStandalone && isNew){
                    form.controls.pnLAccountId.setValidators([Validators.required]);
                }

                form.controls.periodCount.valueChanges.subscribe(v => { 
                    const isAccrual = checkIsGroupAccrual(form.controls.groupId.value);
                    if(v === 0){
                         
                        form.controls.accruedTotal.setValue(form.controls.totalAmount.value);
                        form.controls.accruedTotal.disable();
                        //We need to upate the text for period processing date to reflect different purpose.
                        const item = container.groups[0].sections[2].items.find(x=>x.fieldName === 'periodStart');
                        item.label = isAccrual ? 'Date Value was accrued in balance sheet' : 'Date Value was prepaid in balance sheet';
                        
                        //Pnl transaction date is still reuqired for prepayments even with 0 period count as 
                        //  we need to know when to the reveral took place
                        if(isAccrual){
                            form.controls.reversalPosted.setValue(true);
                            form.controls.reversalPosted.disable();
                        }else{
                            form.controls.pnLTransactionDate.setValidators([Validators.required]);
                            form.controls.pnLTransactionDate.updateValueAndValidity();

                            form.controls.periodStart.disable();
                            form.controls.periodStart.clearValidators();
                            form.controls.periodStart.updateValueAndValidity();

                            form.controls.reversalPosted.setValue(true);
                            form.controls.reversalPosted.enable();
                            form.controls.reversalPosted.updateValueAndValidity();
                        }
                    }else{
                        form.controls.accruedTotal.enable();
                        form.controls.accruedTotal.setValue(0);
                        //Restore text for the period start field
                        const item = container.groups[0].sections[2].items.find(x=>x.fieldName === 'periodStart');
                        item.label = 'Date to start processing';

                        if(isAccrual){
                            form.controls.reversalPosted.setValue(false);
                            form.controls.reversalPosted.enable();

                        }else{
                            form.controls.pnLTransactionDate.setValidators([Validators.required]);
                            form.controls.pnLTransactionDate.updateValueAndValidity();

                            form.controls.periodStart.enable();
                            form.controls.periodStart.setValidators([Validators.required]);
                            form.controls.periodStart.updateValueAndValidity();

                            form.controls.reversalPosted.setValue(true);
                            form.controls.reversalPosted.enable();
                            form.controls.reversalPosted.updateValueAndValidity();
                        }
                    }
                });

                const minDate = company.companyVersion > 1 ? company.processingStartDate : currentYear.yearStart;

                const checkIsGroupAccrual = (groupKey: number): boolean => {
                    const group = groupsFull.find(x=>x.id === groupKey);
                    if(group){
                        if(group.type.startsWith('Accru')){
                            return true;
                        }else{
                            return false;
                        }
                    }else{
                        return false;
                    }
                }

                const title = isNew ? 'Create new accrual/prepayment item' : 'Edit accrual/prepayment item';
                let msg = '';
                if(isNew){
                    msg = 'Please complete the form below to create a new accrual/prepayment item for your company.';
                }else if(!inactveItem){
                    msg = 'You are viewing an accrual/prepayment item that has already begun processing and is therefore limited fields you can edit.';
                }

                if(isTransaction){
                    msg += '  As this item is being created from a transaction, some fields are not editable.';
                }

                const container: IGroupContainer = {
                    title: title,
                    description: msg,
                    showCompanyStatus: true,
                    company: company,
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: entity,
                    form: form,
                    groups: [
                        {
                            header: 'group',
                            id: '1000',
                            sections: [
                                {
                                    header: 'Group & type',
                                    description: 'Select the group that this item will belong to',
                                    form: form,
                                    allowEdit: false,
                                    items: [
                                        { label: 'Group', control: 'select', fieldName: 'groupId', dropdownList: groups}
                                    ]
                                },                                
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const updatedValues = form.getRawValue();
                        if(Object.keys(updatedValues).length > 0 ){
                            let result: IApiResult;
                            if(isNew){
                                if(isTransaction){
                                    result = await this.api.createPrepaymentForTransaction(transactionId, updatedValues, this.SelectedCompanyId);
                                }else{
                                    result = await this.api.createAccrualPrepaymentItem(updatedValues, this.SelectedCompanyId);
                                }
                                
                            }else{
                                result = await this.api.updateAccrualPrepaymentItem(entity.id, updatedValues, this.SelectedCompanyId);
                            }

                            if(result.result === ApiResultValues.Ok){
                                if(isNew){
                                    this._createdAccrualPrepaymentItem.next(result.data);
                                }else{
                                    this._selectedAccrualPrepaymentItem.next(result.data);
                                }
                                dRef.close();
                                o.next(result.data);
                                await this.refreshCompanySummaryData();
                                return result.data;
                            }
                        }else{
                            dRef.close();
                            o.next(null);
                        }
                    }
                }

                const updateFunc = this.getNominalAccountTypeAheadUpdateFunction(container);

                const createSections = (v) => {
                    const isAccrual = checkIsGroupAccrual(v);
                    let placeholderTransaction = '';
                    let placeholderReversal = ''
                    let placeholderPeriodStart = '';

                    if(isAccrual){
                        form.controls.pnLTransactionDate.clearValidators();
                        form.controls.pnLTransactionDate.updateValueAndValidity();
                        //We need to upate the text for period processing date to reflect different purpose.
                        if(entity.periodCount === 0 && !isNew){
                            placeholderPeriodStart ='Date value was held in balance sheet';
                        }else{
                            placeholderPeriodStart = 'Date to start processing';
                        }

                        placeholderTransaction = 'Invoice/Bill date (if known)';
                        placeholderReversal = 'Value already held in balance sheet';
                    }else{
                        form.controls.pnLTransactionDate.setValidators([Validators.required]);
                        form.controls.pnLTransactionDate.updateValueAndValidity();

                        if(form.controls.periodCount.value === 0){
                            form.controls.periodStart.disable();
                            form.controls.periodStart.clearValidators();
                        }

                        placeholderTransaction = 'Invoice/Bill date';
                        placeholderReversal = 'Value already held in balance sheet';
                        if(entity.periodCount === 0 && !isNew){
                            placeholderPeriodStart ='Date value was held in balance sheet';
                        }else{
                            placeholderPeriodStart = 'Date to start processing';
                        }
                    }
                    
                    const coreSection = {
                            header: 'Core information',
                            description: '',
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Name', control: 'input', fieldName: 'title'},
                                {label: 'Notes', control: 'richtext', fieldName: 'notes'},
                                {label: 'PnL account for periodic entries', control: 'type-ahead-nominal', fieldName: 'pnLAccountId', typeAheadData: accounts, typeAheadHelperFunction: updateFunc}
                            ]
                        };

                    const fieldThree = isAccrual ? 
                            {label: placeholderReversal, control: 'input', fieldName: 'accruedTotal'} : 
                            {label: placeholderReversal, control: 'checkbox', fieldName: 'reversalPosted'};

                    const finSection = {
                            header: 'Financial data',
                            description: isTransaction ? 'As this item is being created from a transaction, some fields are not editable.' : !inactveItem ? 'This item has already begun processing and is therefore limited fields you can edit.' : '',
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Total value to accrue/prepay', control: 'input', fieldName: 'totalAmount'},
                                {label: 'Number of monthly postings', control: 'select', fieldName: 'periodCount', dropdownList: periods},
                                {label: placeholderPeriodStart, control: 'datepicker', fieldName: 'periodStart', minDate: minDate},
                                {label: placeholderTransaction, control: 'datepicker', fieldName: 'pnLTransactionDate', minDate: minDate},
                                fieldThree,
                                
                            ]
                        };
                    const locSection = {
                            header: 'Location and tracking',
                            description: '',
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Location', control: 'location', fieldName: 'location'}
                            ]
                        }
                    ;

                    if(company.enableClass){
                    locSection.items.push({
                            label: NtCompany.GetProviderClassLabel(company.externalDataSource.dataSource),
                            control: 'class',
                            fieldName: 'class'
                        });
                    }

                    return [coreSection, finSection, locSection];
                }

                form.controls.groupId.valueChanges.subscribe(v => {
                    //We need to check whether the group is an accrual in/ex, prepay or def
                    const isAccrual = checkIsGroupAccrual(v);
                    if(isAccrual){
                        form.controls.pnLTransactionDate.clearValidators();
                    }else if(!isTransaction){
                        form.controls.pnLTransactionDate.setValidators(Validators.required);
                    }
                    form.controls.pnLTransactionDate.updateValueAndValidity();

                    container.groups[0].sections.splice(1,container.groups[0].sections.length - 1);
                    const sections = createSections(v);
                    sections.forEach(s => {
                        container.groups[0].sections.push(s);
                    });

                });

                if(!isNew || isTransaction){
                    const sections = createSections(entity.groupId);
                    sections.forEach(s => {
                        container.groups[0].sections.push(s);
                    });
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: container,
                        ...dialogDefaultFullScreen
                    }
                );
                this.closeLoadingSnack(snack);
            }
            asyncF();
        });

        return await firstValueFrom(obs);
    }

    async getJournalEntries(pageIndex: number, pageSize: number): Promise<IApiQueryResult> {
        const res = await this.api.getJournalEntries(pageIndex, pageSize, this.SelectedCompanyId);
        return res;
    }

    async getJournalEntry(id: number): Promise<any> {
        const res = await this.api.getJournalEntry(id, this.SelectedCompanyId);
        if(res.result === ApiResultValues.Ok){
            return res.data;
        }else{
            return null;
        }
    }

    async updateJournalEntryDate(journal: any): Promise<IApiResult> {
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const form = this.fb.group({
                    journalDate: [journal.journalDate, [Validators.required]]
                });

                const g: IGroupContainer = {
                    title: 'Update journal entry date',
                    description: 'If you are unable to process journal entry, it may be necessary to update the posting date that is in being sent to your accounts package.',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: journal,
                    form: form,
                    groups: [
                        {
                            header: 'header',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Details',
                                    description: '',
                                    form: form,
                                    data: journal,
                                    allowEdit: false,
                                    items: [
                                        {label: 'Journal date', control: 'datepicker', fieldName: 'journalDate'}
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dref: MatDialogRef<ModalEditorComponent>) => {
                        dref.close();
                        o.next(null);
                    },
                    completeAction: async(dref: MatDialogRef<ModalEditorComponent>) => {
                        const {journalDate} = form.getRawValue();
                        const res = await this.api.updateProviderJournalDate(journal.id, journalDate, this.SelectedCompanyId);
                        if(res.result === ApiResultValues.Ok){
                            dref.close();
                            o.next(res.data);
                        }
                    }
                };

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: g,
                        ...dialogDefaultFullScreen
                    }
                );
            };

            asyncF();
        });

        return await firstValueFrom(obs);
    }

    async updateJournalLineAccounts(journalLine: any): Promise<IApiResult> {
        const obs = new Observable<any>(o => {
            const asyncF = async() => {

                const accounts = await this.getNominalAccounts();
                const form = this.fb.group({
                    debitAccount: [journalLine.debitAccount, [Validators.required]],
                    creditAccount: [journalLine.creditAccount, [Validators.required]]
                });

                const g: IGroupContainer = {
                    title: 'Update journal line accounts',
                    description: 'If you are unable to process journal line, it may be necessary to update the accounts that are being sent to your accounts package.',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: journalLine,
                    form: form,
                    groups: [
                        {
                            header: 'header',
                            id: '1001',
                            sections: []
                        }
                    ],
                    cancelAction: (dref: MatDialogRef<ModalEditorComponent>) => {
                        dref.close();
                        o.next(null);
                    },
                    completeAction: async(dref: MatDialogRef<ModalEditorComponent>) => {
                        const {debitAccount, creditAccount} = form.getRawValue();
                        const res = await this.api.updateProviderJournalLine(journalLine.id, debitAccount, creditAccount, this.SelectedCompanyId);
                        if(res.result === ApiResultValues.Ok){
                            dref.close();
                            o.next(res.data);
                        }
                    }
                };

                const updateFunc = this.getNominalAccountTypeAheadUpdateFunction(g);

                const s = {
                                    header: 'Details',
                                    description: '',
                                    form: form,
                                    data: journalLine,
                                    allowEdit: false,
                                    items: [
                                        {label: 'Debit account', control: 'type-ahead-nominal',  typeAheadData: accounts, typeAheadHelperFunction: updateFunc, fieldName: 'debitAccount'},
                                        {label: 'Credit account', control: 'type-ahead-nominal', typeAheadData: accounts, typeAheadHelperFunction: updateFunc, fieldName: 'creditAccount'}
                                    ]
                                };
                g.groups[0].sections.push(s);

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: g,
                        ...dialogDefaultFullScreen
                    }
                );
            };

            asyncF();
        });

        return await firstValueFrom(obs);
    }

    async getJournalEntryLines(id: number, pageIndex: number, pageSize: number): Promise<IApiQueryResult> {
        const res = await this.api.getJournalEntryLines(id, pageIndex, pageSize, this.SelectedCompanyId);
        return res;
    }

    async getJournalEntriesForPeriod(id: number): Promise<IApiQueryResult>{
        const res = await this.api.getJournalEntriesForPeriod(id, this.SelectedCompanyId);
        return res;
    }

    async getReportingPeriod(id: number): Promise<any>{
        const years = await this.getFinancialYears();
        let period;
        years.forEach(y => {
            const p = y.reportingPeriods.find(x=>x.id === id);
            if(p){
                period = p;
            }
        });

        return period;
    }

    async getJournalPostings(id: number): Promise<IApiQueryResult> {
        const res = await this.api.getJournalPostings(id, this.SelectedCompanyId);
        return res;
    }

    async getJournalErrors(): Promise<IApiQueryResult> {
        const res = await this.api.getJournalErrors(this.SelectedCompanyId);
        return res;
    }

    async repostJournalEntry(id: number): Promise<IApiQueryResult> {
        const dlg = await this.dlg.showPleaseWait('Please wait while we retry posting this journal entry');
        const res = await this.api.repostJournalEntry(id, this.SelectedCompanyId);
        dlg.close();
        if(res.result === ApiResultValues.Ok){
            await this.refreshCompanySummaryData();
        }
        return res;
    }

    async resolveJournalEntry(id: number): Promise<IApiQueryResult> {
        const res = await this.api.resolveJournalEntry(id, this.SelectedCompanyId);
        if(res.result === ApiResultValues.Ok){
            await this.refreshCompanySummaryData();
        }
        return res;
    }

    async viewJournalEntry(id: number){
        this.router.navigate(['/company', this.SelectedCompanyId, 'journals', 'details', id, 'lines']);
    }

    async updateJournalEntry(entry: any){
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const form = this.fb.group({
                    journalRef: [entry.journalRef, [Validators.required]],
                    journalDate: [entry.journalDate, [Validators.required]]
                });

                const g: IGroupContainer = {
                    title: 'Update journal entry',
                    description: 'If you are unable to process journal entry, it may be necessary to update the reference or the posting date that is in being sent to your accounts package.',
                    dialogMode: true,
                    collapseGroupNav: true,
                    data: entry,
                    form: form,
                    groups: [
                        {
                            header: 'header',
                            id: '1001',
                            sections: [
                                {
                                    header: 'Details',
                                    description: '',
                                    form: form,
                                    data: entry,
                                    allowEdit: false,
                                    items: [
                                        {label: 'Journal reference', control: 'input', fieldName: 'journalRef'},
                                        {label: 'Journal date', control: 'datepicker', fieldName: 'journalDate'}
                                    ]
                                }
                            ]
                        }
                    ],
                    cancelAction: (dref: MatDialogRef<ModalEditorComponent>) => {
                        dref.close();
                        o.next(null);
                    },
                    completeAction: async(dref: MatDialogRef<ModalEditorComponent>) => {
                        const {journalRef, journalDate} = form.getRawValue();
                        const res = await this.api.updateJournalEntry(entry.id, journalRef, journalDate, this.SelectedCompanyId);
                        if(res.result === ApiResultValues.Ok){
                            dref.close();
                            o.next(res.data);
                        }
                    }
                };

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: g,
                        ...dialogDefaultFullScreen
                    }
                );
            }

            asyncF();
        });

        return await firstValueFrom(obs);
    }

    private _selectedLoanItem = new BehaviorSubject<any | null>(null);
    get SelectedLoanItem() {
        return this._selectedLoanItem.asObservable();
    }

    async getLoanItem(id: number): Promise<any>{
        const current = this._selectedLoanItem.value;
        if(current){
            if(current.id === id){
                return current;
            }
        }

        const apiRes = await this.api.getLoanItem(id, this.SelectedCompanyId);
        if(apiRes.result === ApiResultValues.Ok){
            this._selectedLoanItem.next(apiRes.data);
            return apiRes.data;
        }else{
            return null;
        }
    }

    private _loanTypeCounts: any[] = [];
    async getLoanTypeCount(): Promise<any>{
        if(this._loanTypeCounts.length === 0){
            const apiRes = await this.api.getLoanTypeCount(this.SelectedCompanyId);
            this._loanTypeCounts = apiRes.data;
        }
        return this._loanTypeCounts;
    }

    async getLoanItemPostings(id: number): Promise<any>{
        const apiRes = await this.api.getLoanItemPostings(id, this.SelectedCompanyId);
        return apiRes;
    }

    
    async viewLoanItem(id: number){
        const res = await this.getLoanItem(id);
        if(res){
            this.router.navigate(['/company', this.SelectedCompanyId, 'loans', 'details', id]);
        }
    }

    async getLoanStatement(yearId: number,
            startDate: string,
            endDate: string,
            summary: boolean,
            pageIndex: number,
            pageSize: number,
            searchTerm: string,
            sortDesc: boolean,
            sortItem: string,
            loanType: string
        ){

        const model = {yearId,
            summary,
            pageIndex,
            pageSize,
            searchTerm,
            sortDesc,
            sortItem,
            startDate,
            endDate,
            loanType
        };

        return await this.api.getLoanStatement(model, this.SelectedCompanyId);
    }




    private _createdLoanItem = new BehaviorSubject<any | null>(null);
    get CreatedLoanItem() {
        return this._createdLoanItem.asObservable();
    }

    async createLoanItem(){
        const snack = await this.showLoadingSnack();
        const newItem = {id: 0, loanBalanceAccountId: 0, interestAccountId: 0, loanType: 'HirePurchase', interestResolution: 'Monthly', interestMode: 'Linear', paymentTiming: 'Arrears'};
        const resultItem = await this.updateLoanItem(newItem, true, snack);
        if(resultItem){
            this._createdLoanItem.next(resultItem);
        }
        
        return resultItem;
    }

    async createLeaseItem(){
        const snack = await this.showLoadingSnack();
        const newItem = {id: 0, loanBalanceAccountId: 0, paymentTiming: 'Advance', interestAccountId: 0, loanType: 'Lease', interestMode: 'Variable'};
        const resultItem = await this.updateLeaseItem(newItem, true, snack);
        if(resultItem){
            this._createdLoanItem.next(resultItem);
        }
        return resultItem;
    }

    async editLoanItem(id: number){
        const snack = await this.showLoadingSnack();
        const loan = await this.getLoanItem(id);
        if(loan){
            let updatedLoan;;
            if(loan.loanType === 'Lease'){
                updatedLoan = await this.updateLeaseItem(loan, false, snack);
            }else{
                updatedLoan = await this.updateLoanItem(loan, false, snack);
            }
            this._selectedLoanItem.next(updatedLoan);
            return updatedLoan;
        }else{
            this.closeLoadingSnack(snack);
            return null;
        }
    }

    private async updateLeaseItem(entity: any, isNew: boolean, snack: any) {
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const company = this._selectedCompany.value;
                const isStandalone = company.externalDataSource.dataSource.toLowerCase() === 'standalone';
                const currentYear = await this.getCurrentFinancialYear();
                const assetGroups = await this.getAssetGroups();
                if(assetGroups.length === 0){
                    this.closeLoadingSnack(snack);
                    await this.dlg.showOk('No asset groups', 'You must first create asset groups before you can create a lease item.  Please create the necessary asset groups from asset section of company settings and try again.');
                    o.next(null);
                    return;
                }
                const accounts = await this.getNominalAccounts();
                accounts.unshift({id: 0, name: '- please select an account'});
                accounts.unshift({id: -1, name: 'Create new nominal account for mapping'});

                const form = this.fb.group({
                    name: [entity.name, [Validators.required, FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    description: [entity.description, [ FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    reference: [entity.reference, [ FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    class: [entity.class, [ FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    location: [entity.location, [ FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    loanType: [entity.loanType, [Validators.required]],
                    interestMode: [entity.interestMode, [Validators.required]],
                    interestResolution: [entity.interestResolution, [Validators.required]],
                    repaymentAmount: [entity.repaymentAmount],
                    //interestRate: [{value:entity.interestRate, disabled: true}],
                    advanceDate: [entity.advanceDate, [Validators.required]],
                    repaymentStartDate: [entity.repaymentStartDate, [Validators.required]],
                    principal: [entity.principal, [Validators.required, Validators.min(1)]],
                    totalRepayments: [entity.totalRepayments,{validators:[Validators.required, Validators.min(1)], updateOn: 'blur'}],
                    residualValue: [entity.residualValue, [Validators.min(0)]],
                    isAsset: [entity.isAsset],
                    relatedAssetId: [entity.relatedAssetId],
                    assetGroupId: [entity.assetGroupId],
                    prepaymentAmount: [entity.prepaymentAmount],
                    arrangementFees: [entity.arrangementFees],
                    incentivesReceived: [entity.incentivesReceived],
                    interestAccountId : [entity.interestAccountId, [Validators.required, NotValueValidatorDirective.notValueValidatorFactory(0)]],
                    loanBalanceAccountId: [entity.loanBalanceAccountId, [Validators.required, NotValueValidatorDirective.notValueValidatorFactory(0)]],
                    nettTrackerProcessingStart: [entity.nettTrackerProcessingStart],
                    paymentTiming: [entity.paymentTiming]
                });

                if(company.enableClass){
                    form.controls.class.addValidators(Validators.required);
                }

                const interestResolutions = [
                    {key: 'Monthly', value: 'Monthly'},
                    {key: 'Quarterly', value: 'Quarterly'},
                    {key: 'Annually', value: 'Annually'},
                ];

                const paymentTiming = [
                    {key: 'Advance', value: 'In advance'},
                    {key: 'Arrears', value: 'In arrears'},
                ]
                
                const group: IGroupContainer ={
                    title: isNew ? 'Create Lease' : 'Update Lease',
                    description:  `${isNew ? 'Create' : 'Update'} a lease item for your company.  Please ensure that you have the correct accounts set up in NettTracker.`,
                    dialogMode: true,
                    showCompanyStatus: true,
                    company: company,
                    collapseGroupNav: true,
                    data: entity,
                    form: form,
                    groups: [
                        {
                            header: 'Lease details',
                            id: '1001',
                            sections: [
                                
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    }
                    ,
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const updatedValues = form.getRawValue();
                        if(Object.keys(updatedValues).length > 0 ){
                            updatedValues['id'] = entity.id;
                            //updatedValues['interestMode'] = entity.interestMode;
                            updatedValues['loanType'] = entity.loanType;
                            let result;
                            result = await this.api.updateLoanItem(updatedValues, this.SelectedCompanyId);

                            if(result.result === ApiResultValues.Ok){
                                if(isNew){
                                    this._createdLoanItem.next(result.data);
                                }else{
                                    this._selectedLoanItem.next(result.data);
                                }
                                await this.refreshCompanySummaryData();
                                dRef.close();
                                o.next(result.data);
                                return result.data;
                            }
                        }else{
                            dRef.close();
                            o.next(null);
                        }
                    }
                }

                const updateFunc = this.getNominalAccountTypeAheadUpdateFunction(group);

                const createSections = (): ISection[] => {
                    const sections = [];
                    sections.push({
                            header: 'Name and reference',
                            description: 'Please enter the name of the lease item you wish to create or update.',
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Name', control: 'input', fieldName: 'name'},
                                {label: 'Description', control: 'input', fieldName: 'description'},
                                {label: 'Reference', control: 'input', fieldName: 'reference'}
                            ]
                        });

                    if(isNew){
                        sections.push({
                            header: 'Lease details',
                            description: 'Please enter the financial details of the lease item you wish to create or update.',
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Asset value', control: 'input', fieldName: 'principal'},
                                {label: 'Prepayment amount', control: 'input', fieldName: 'prepaymentAmount'},
                                {label: 'Arrangement fees', control: 'input', fieldName: 'arrangementFees'},
                                {label: 'Incentives received', control: 'input', fieldName: 'incentivesReceived'},
                                {label: 'Residual value', control: 'input', fieldName: 'residualValue'},
                                {label: 'Repayment amount', control: 'input', fieldName: 'repaymentAmount'},
                                {label: 'Total repayments', control: 'input', fieldName: 'totalRepayments'},
                                {label: 'Payment interval', control: 'select', fieldName: 'interestResolution', dropdownList: interestResolutions},
                                {label: 'Payment timing', control: 'select', fieldName: 'paymentTiming', dropdownList: paymentTiming},
                            ]
                        },
                        {
                            header: 'Dates',
                            description: 'Please enter any date information for the lease item.  If you are bringing in a lease with existing balances, please ensure that the nettTracker processing start date is set to the date you wish nettTracker to begin posting interest from.',
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Lease date', control: 'datepicker', fieldName: 'advanceDate', minDate: currentYear.yearStart},
                                {label: 'First payment date', control: 'datepicker', fieldName: 'repaymentStartDate', minDate: currentYear.yearStart},
                                {label: 'nettTracker processing start', control: 'datepicker', fieldName: 'nettTrackerProcessingStart', minDate: currentYear.yearStart},
                            ]
                        },{
                            header: 'Asset membership',
                            description: 'This lease should relate to an asset, and one will be created with this lease.  Please select the asset group the asset will belong to.  This should not include other assets or your nettTracker balances will not match your external accounts.',
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Asset group', control: 'select', fieldName: 'assetGroupId', dropdownList: assetGroups.map(ag => ({key: ag.id, value: ag.accountGroupName}))}
                            ]
                        });
                    }

                    sections.push({
                            header: 'Posting Accounts',
                            description: 'Please select the accounts that will be used for posting the lease interest and balance.',
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Interest account', control: 'type-ahead-nominal', fieldName: 'interestAccountId', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                                {label: 'Lease balance account', control: 'type-ahead-nominal', fieldName: 'loanBalanceAccountId', typeAheadData: accounts, typeAheadHelperFunction: updateFunc}
                            ]
                        },
                        {
                            header: 'Location and tracking',
                            description: 'If you are using class or location tracking, please select the appropriate class and location for this loan item.',
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Location', control: 'location', fieldName: 'location'}
                            ]
                        });

                    return sections;
                }

                group.groups[0].sections = createSections();

                if(company.enableClass){
                    const length = group.groups[0].sections.length;
                    group.groups[0].sections[length - 1].items.push({
                        label: NtCompany.GetProviderClassTitle(company.externalDataSource.dataSource),
                        control: 'class',
                        fieldName: 'class'
                    });
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );

                if(snack){
                    this.closeLoadingSnack(snack);
                }

            };
            
            asyncF();
        });
        return await firstValueFrom(obs);
    }

    private async updateLoanItem(entity: any, isNew: boolean, snack: any) {
        const obs = new Observable<any>(o => {
            const asyncF = async() => {
                const company = this._selectedCompany.value;
                const isStandalone = company.externalDataSource.dataSource.toLowerCase() === 'standalone';
                const currentYear = await this.getCurrentFinancialYear();
                const accounts = await this.getNominalAccounts();
                accounts.unshift({id: 0, name: '- please select an account'});
                accounts.unshift({id: -1, name: 'Create new nominal account for mapping'});

                const form = this.fb.group({
                    name: [entity.name, [Validators.required, FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    description: [entity.description, [ FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    reference: [entity.reference, [ FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    class: [entity.class, [ FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    location: [entity.location, [ FormFieldCharacterValidator.FormFieldCharacterValidatorFactory()]],
                    loanType: [entity.loanType, [Validators.required]],
                    interestMode: [entity.interestMode, [Validators.required]],
                    interestResolution: [entity.interestResolution, [Validators.required]],
                    repaymentAmount: [entity.repaymentAmount],
                    interestRate: [{value:entity.interestRate, disabled: true}],
                    advanceDate: [entity.advanceDate, [Validators.required]],
                    repaymentStartDate: [entity.repaymentStartDate, [Validators.required]],
                    principal: [entity.principal, [Validators.required, Validators.min(1)]],
                    totalRepayments: [entity.totalRepayments,{validators:[Validators.required, Validators.min(1)], updateOn: 'blur'}],
                    interestTotal : [{value: entity.interestTotal, disabled: true}],
                    residualValue: [entity.residualValue, [Validators.min(0)]],
                    isAsset: [entity.isAsset],
                    relatedAssetId: [entity.relatedAssetId],
                    interestAccountId : [entity.interestAccountId, [Validators.required, NotValueValidatorDirective.notValueValidatorFactory(0)]],
                    loanBalanceAccountId: [entity.loanBalanceAccountId, [Validators.required, NotValueValidatorDirective.notValueValidatorFactory(0)]],
                    nettTrackerProcessingStart: [entity.nettTrackerProcessingStart],
                });

                const interestResolutions = [
                    {key: 'Monthly', value: 'Monthly'},
                    {key: 'Quarterly', value: 'Quarterly'},
                    {key: 'Annually', value: 'Annually'},
                ];

                if(company.enableClass){
                    form.controls.class.addValidators(Validators.required);
                }
                
                const updateInterestTotal = async (totalRepayments: number, principal: number, repaymentAmount: number) => {
                    if(principal > 0 && repaymentAmount > 0 && totalRepayments > 0){
                        const interestTotal = (repaymentAmount * totalRepayments) - principal;
                        form.controls.interestTotal.setValue(interestTotal);

                        const data = {
                            principal: principal,
                            repaymentAmount: repaymentAmount,
                            totalRepayments: totalRepayments,
                            interestTotal: interestTotal
                        }

                        const apiRes = await this.api.getLoanAPR(data, this.SelectedCompanyId);
                        if(apiRes.result === ApiResultValues.Ok){
                            const apr = formatPercent(apiRes.data, 'en-US', '1.2-2');
                            form.controls.interestRate.setValue(apr);
                        }
                    };
                };

                form.controls.totalRepayments.valueChanges.subscribe(v => {
                    updateInterestTotal(v, form.controls.principal.value, form.controls.repaymentAmount.value);
                });

                form.controls.principal.valueChanges.subscribe(v => {
                    updateInterestTotal(form.controls.totalRepayments.value, v, form.controls.repaymentAmount.value);
                });

                form.controls.repaymentAmount.valueChanges.subscribe(v => {
                    updateInterestTotal(form.controls.totalRepayments.value, form.controls.principal.value, v);
                });
                
                const loanTypes = [{key: 'Fixed', value: 'Fixed Rate'},
                    {key: 'HirePurchase', value: 'Hire Purchase'},
                    {key: 'Director', value: "Director's Loan"}];
                const interestModes = [{key: 'Linear', value: 'Linear'},
                    {key: 'SumOfDigits', value: 'Sum of Digits'}];

                
                const group: IGroupContainer ={
                    title: isNew ? 'Create Loan' : 'Update Loan',
                    description:  `${isNew ? 'Create' : 'Update'} a loan item for your company.  Please ensure that you have the correct accounts set up in NettTracker.`,
                    dialogMode: true,
                    showCompanyStatus: true,
                    company: company,
                    collapseGroupNav: true,
                    data: entity,
                    form: form,
                    groups: [
                        {
                            header: 'Loan details',
                            id: '1001',
                            sections: [
                                
                            ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    }
                    ,
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const updatedValues = form.getRawValue();
                        if(Object.keys(updatedValues).length > 0 ){
                            updatedValues['id'] = entity.id;
                            //updatedValues['interestResolution'] = entity.interestResolution;
                            //updatedValues['interestMode'] = entity.interestMode;
                            updatedValues['loanType'] = entity.loanType;
                            updatedValues['paymentTiming'] = entity.paymentTiming;  

                            let result;
                            result = await this.api.updateLoanItem(updatedValues, this.SelectedCompanyId);

                            if(result.result === ApiResultValues.Ok){
                                if(isNew){
                                    this._createdLoanItem.next(result.data);
                                }else{
                                    this._selectedLoanItem.next(result.data);
                                }
                                await this.refreshCompanySummaryData();
                                dRef.close();
                                o.next(result.data);
                                return result.data;
                            }
                        }else{
                            dRef.close();
                            o.next(null);
                        }
                    }
                }

                const updateFunc = this.getNominalAccountTypeAheadUpdateFunction(group);

                const createSections = () => {
                    const sections = [];

                    sections.push({
                            header: 'Core information',
                                    description: 'Please enter the details of the loan item you wish to create or update.',
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Name', control: 'input', fieldName: 'name'},
                                {label: 'Description', control: 'input', fieldName: 'description'},
                                {label: 'Reference', control: 'input', fieldName: 'reference'}
                            ]
                        });

                    if(isNew){
                        sections.push( {
                            header: 'Loan details',
                                    description: 'Please enter the details of the loan item you wish to create or update.',
                            form: form,
                            allowEdit: false,
                            items: [
                               //{label: 'Loan type', control: 'select', fieldName: 'loanType', dropdownList: loanTypes},
                                {label: 'Principal', control: 'input', fieldName: 'principal'},
                                {label: 'Repayment amount', control: 'input', fieldName: 'repaymentAmount'},
                                {label: 'Total repayments', control: 'input', fieldName: 'totalRepayments'},
                                {label: 'Interest total', control: 'input', fieldName: 'interestTotal'},
                                {label: 'Interest rate (%)', control: 'input', fieldName: 'interestRate'},
                                {label: 'Interest mode', control: 'select', fieldName: 'interestMode', dropdownList: interestModes},
                                {label: 'Payment interval', control: 'select', fieldName: 'interestResolution', dropdownList: interestResolutions},
                            ]
                        },
                        {
                            header: 'Dates',
                            description: 'Please enter any date information for the loan item.  If you are bringing in a loan with existing balances, please ensure that the nettTracker processing start date is set to the date you wish nettTracker to begin posting interest from.',
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Advance date', control: 'datepicker', fieldName: 'advanceDate', minDate: currentYear.yearStart},
                                {label: 'Repayment start date', control: 'datepicker', fieldName: 'repaymentStartDate', minDate: currentYear.yearStart},
                                {label: 'nettTracker processing start', control: 'datepicker', fieldName: 'nettTrackerProcessingStart', minDate: currentYear.yearStart},
                            ]
                        });
                    }

                    sections.push({
                            header: 'Accounts',
                            description: 'Please select the accounts that will be used for the loan item.  If you do not have the accounts set up, please create them first.',
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Interest account', control: 'type-ahead-nominal', fieldName: 'interestAccountId', typeAheadData: accounts, typeAheadHelperFunction: updateFunc},
                                {label: 'Loan balance account', control: 'type-ahead-nominal', fieldName: 'loanBalanceAccountId', typeAheadData: accounts, typeAheadHelperFunction: updateFunc}
                            ]
                        },
                        {
                            header: 'Location and tracking',
                            description: 'If you are using class or location tracking, please select the appropriate class and location for this loan item.',
                            form: form,
                            allowEdit: false,
                            items: [
                                {label: 'Location', control: 'location', fieldName: 'location'}
                            ]
                        });
                    return sections;
                }

                group.groups[0].sections = createSections();

                if(company.enableClass){
                    group.groups[0].sections[1].items.push({
                        label: NtCompany.GetProviderClassTitle(company.externalDataSource.dataSource),
                        control: 'class',
                        fieldName: 'class'
                    });
                }

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );

                if(snack){
                    this.closeLoadingSnack(snack);
                }
            };

            asyncF();
        });

        return await firstValueFrom(obs);
    }

    async getLoanItems(): Promise<IApiQueryResult> {
        const apiRes = await this.api.getLoanItems(this.SelectedCompanyId);
        return apiRes;
    }


    async loanNominalBulkUpdate(items: any[]): Promise<IApiResult> {
        const dlg = await this.dlg.showPleaseWait("Please wait while we update your items with new accounts");
        const apiRes = await this.api.loanNominalBulkUpdate(items, this.SelectedCompanyId);
        dlg.close();
        return apiRes;
    }

    async deleteLoanItem(id: number){
        const obs = new Observable<any>(o => {
            const asyncM = async() => {
                const snack = await this.showLoadingSnack();
                const currentYear = await this.getCurrentFinancialYear();
                const item = await this.getLoanItem(id);

                const form = this.fb.group({
                    postJournal: [true, Validators.required],
                    deletionDate: [currentYear.yearEnd, Validators.required]
                });

                const group: IGroupContainer = {
                    title: 'Delete Loan/Lease Item',
                    description: 'You can delete a loan/lease item at any time.  Selecting the option below will create a journal to reverse any interest postings made.  You will need to manually create the relevant accounting entries to remove the loan balance, if required, from your external accounts package.',
                    dialogMode: true,
                    company: this._selectedCompany.value,
                    showCompanyStatus: true,
                    collapseGroupNav: true,
                    actionText: 'Delete',
                    form: form,
                    data: item,
                    groups: [
                        {
                            header: 'group1',
                            id: '1001',
                            sections: [ ]
                        }
                    ],
                    cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                        dRef.close();
                        o.next(null);
                    },
                    completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                        const {deletionDate, postJournal} = form.getRawValue();
                        const model = {
                            id,
                            ...form.getRawValue()
                        };
                        const dlg = await this.dlg.showPleaseWait('Please wait while we delete this item and post any journal entries required');
                        const delRes = await this.api.deleteLoan(model, this.SelectedCompanyId);
                        await dlg.close();

                        if(delRes.result === ApiResultValues.Ok){
                            dRef.close();
                            this._selectedLoanItem.next(null);
                            await this.refreshCompanySummaryData();
                            o.next(true);
                        }
                    }
                };

                const sections: ISection[] = [{
                        header: 'Reversal date',
                        description: 'Please enter the date for the reversal journal (must be within current financial year)',
                        form: form,
                        allowEdit: false,
                        items: [
                            {label: 'Deletion date', control: 'datepicker', fieldName: 'deletionDate', maxDate: currentYear.yearEnd, minDate: currentYear.yearStart}
                        ]
                    }];

                if(item.loanType === 'Lease'){
                    sections.push({
                        header: 'Post reversal journal',
                        description: 'Please confirm you wish nettTracker to reverse the total interest posted for this loan/lease item.  This will also reverse the balance sheet journal created for the right of use asset, if post journals is selected.',
                        form: form,
                        allowEdit: false,
                        items: [
                            {label: 'Post reversal journals', control: 'checkbox', fieldName: 'postJournal'}
                        ]
                    });
                    //Add the asset warning
                    // sections.push({
                    //     header: 'Related asset',
                    //     description: 'This lease is related to an asset in your asset register.  You can choose to include the asset when deleting the lease.  If you do not include the asset, you will need to manually dispose of the asset from the asset register and the asset with be disassociated from this lease.',
                    //     form: form,
                    //     allowEdit: false,
                    //     items: [
                    //         {label: 'Delete related asset', control: 'checkbox', fieldName: 'deleteRelatedAsset'}
                    //     ]
                    // })
                }else{
                    sections.push({
                        header: 'Post reversal journal',
                        description: 'Please confirm you wish nettTracker to reverse the total interest posted for this loan/lease item',
                        form: form,
                        allowEdit: false,
                        items: [
                            {label: 'Post reversal journal', control: 'checkbox', fieldName: 'postJournal'}
                        ]
                    });
                }

                group.groups[0].sections = sections;

                this.matDlg.open(ModalEditorComponent,
                    {
                        data: group,
                        ...dialogDefaultFullScreen
                    }
                );
                this.closeLoadingSnack(snack);
            }
            asyncM();
        });

        return await firstValueFrom(obs);
    }

    async deleteLoanReverse(loan: any){
        const msg = loan.loanType === 'Lease' ?
            'Reversing the deletion of this lease will recreate the lease item in your company.  NettTracker will also post any journal entries required to reinstate the lease in your accounts.  You must ensure that the lease details match those prior to deletion.  Please note that no asset processing will be carried out by reversing this deletion.  Are you sure you wish to proceed?' :
            'Reversing the deletion of this loan will recreate the loan item in your company.  NettTracker will also post any journal entries required to reinstate the loan in your accounts.  You must ensure that the loan details match those prior to deletion.  Are you sure you wish to proceed?';
        const confirm = await this.dlg.showYesNo('Reverse loan deletion', msg);
        if(confirm !== DialogBoxResults.Yes){
            return null;
        }

        const dlg = await this.dlg.showPleaseWait('Please wait while we restore the loan and post any journal entries required');

        const disResult = await this.api.deleteLoanReversal(loan.id, this.SelectedCompanyId);
        await dlg.close();

        if(disResult.result === ApiResultValues.Ok){
            await this.refreshCompanySummaryData();
            return true;
        }

        return false;
    }

    async getDeletedLoans(){
        return await this.api.getLoansDeleted(this.SelectedCompanyId);
    }

    async getYearLoanProjection(yearId: number,
            startDate: string,
            endDate: string,
            summary: boolean,
            pageIndex: number,
            pageSize: number,
            searchTerm: string,
            sortDesc: boolean,
            sortItem: string,
            loanType: string){
        const model = {
            yearId,
            startDate,
            endDate,
            summary,
            pageIndex,
            pageSize,
            searchTerm,
            sortDesc,
            sortItem,
            loanType,
            reloadExternalBalance: false
        };    

        return await this.api.getLoanProjection(model, this.SelectedCompanyId);
    }

    async downloadLoanStatement(){
        const snack = await this.showLoadingSnack();
        const yearsList = await this.getFinancialYears();
        const dateInfo = this._selectedDateRange.value;
        const c = this._selectedCompany.value;
        const region = c.region ? ( c.region.toLowerCase() === 'en-gb' ? 'DD/MM/YYYY' : 'MM/DD/YYYY' ): (c.defaultCurrency.toLowerCase() === 'usd' || c.defaultCurrency.toLowerCase() === 'cad') ? 'MM/DD/YYYY' : 'DD/MM/YYYY' ;


        const form = this.fb.group({
            startDate: [moment(dateInfo.startDate)],
            endDate: [moment(dateInfo.endDate)],
            yearId: [dateInfo.yearId.toString()]
        });

        form.controls.yearId.valueChanges.subscribe(v => {
            const year = yearsList.find(x=>x.id === parseInt(v));
            form.controls.startDate.setValue(moment(year.yearStart), {emitEvent: false});
            form.controls.endDate.setValue(moment(year.yearEnd), {emitEvent: false});
        })

        const years = yearsList.map((y):IKeyValue => {
            const ys = moment(y.yearStart).format(region);
            const ye = moment(y.yearEnd).format(region);
            const v: IKeyValue = {
                key: y.id.toString(),
                value: `${ys} - ${ye}`
            }
            return v;
        });

        
        const obs = new Observable<any>(o => {
            const formItems: IItem[] = [
                {label: 'Financial year', control: 'select', fieldName: 'yearId', dropdownList: years }                
            ]

            if(c.companyVersion > 1){
                formItems.push({label: 'Start date', control: 'datepicker', fieldName: 'startDate'});
                formItems.push({label: 'End date', control: 'datepicker', fieldName: 'endDate'});
            }

            const group: IGroupContainer = {
                title: 'Loan Statement Report',
                actionText: 'Export',
                description: 'Please confirm the date range to use on your loan/lease statement report',
                showCompanyStatus: true,
                company: c,
                dialogMode: true,
                collapseGroupNav: true,
                data: dateInfo,
                form: form,
                groups: [
                    {
                        header: 'group',
                        id: '1',
                        sections: [
                            {
                                header: 'Select date range',
                                description: 'Please choose a financial year or custom date range for your report',
                                form: form,
                                allowEdit: false,
                                items: formItems
                            }
                        ]
                    }
                ],
                cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                    dRef.close();
                    o.next(null);
                },
                completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                    const {startDate, endDate, yearId} = form.getRawValue();
                    let sd;
                    let ed;
                    if(startDate.format) {
                        sd = startDate.format('YYYY-MM-DD');
                    }else{
                        sd = startDate;
                    }
                    if(endDate.format) {
                        ed = endDate.format('YYYY-MM-DD');
                    }else{
                        ed = endDate;
                    }
                    if(!sd || !ed){
                        dRef.close();
                        o.next(false);
                    }else{
                        const dlg = await this.dlg.showPleaseWait('Please wait while we prepare your loan/lease statement report');
                        this.api.downloadLoanStatement(this.SelectedCompanyId, sd, ed, parseInt(yearId)).subscribe(result =>{
                            dlg.close();
                            if(result){
                                this.dlg.showOk('Asset Register Report', `Your asset register report has now been downloaded`);
                            }
                            o.next(result);
                            dRef.close();
                        });
                    }
                    
                }
            }
            
            this.matDlg.open(ModalEditorComponent,
                {
                    data: group,
                    ...dialogDefaultFullScreen
                }
            );
            this.closeLoadingSnack(snack);
        });

        return await firstValueFrom(obs);
    }

    async downloadLoanProjection(){
        const snack = await this.showLoadingSnack();
        const yearsList = await this.getFinancialYears();
        const dateInfo = this._selectedDateRange.value;
        const c = this._selectedCompany.value;
        const region = c.region ? ( c.region.toLowerCase() === 'en-gb' ? 'DD/MM/YYYY' : 'MM/DD/YYYY' ): (c.defaultCurrency.toLowerCase() === 'usd' || c.defaultCurrency.toLowerCase() === 'cad') ? 'MM/DD/YYYY' : 'DD/YY/YYYY' ;

        const form = this.fb.group({
            startDate: [moment(dateInfo.startDate)],
            endDate: [moment(dateInfo.endDate)],
            yearId: [dateInfo.yearId.toString()]
        });

        form.controls.yearId.valueChanges.subscribe(v => {
            const year = yearsList.find(x=>x.id === parseInt(v));
            form.controls.startDate.setValue(moment(year.yearStart), {emitEvent: false});
            form.controls.endDate.setValue(moment(year.yearEnd), {emitEvent: false});
        })
        
        const years = yearsList.map((y):IKeyValue => {
            const ys = moment(y.yearStart).format(region);
            const ye = moment(y.yearEnd).format(region);
            const v: IKeyValue = {
                key: y.id.toString(),
                value: `${ys} - ${ye}`
            }
            return v;
        });
        
        const obs = new Observable<any>(o => {
            const formItems: IItem[] = [
                {label: 'Financial year', control: 'select', fieldName: 'yearId', dropdownList: years }                
            ]

            if(c.companyVersion > 1){
                formItems.push({label: 'Start date', control: 'datepicker', fieldName: 'startDate'});
                formItems.push({label: 'End date', control: 'datepicker', fieldName: 'endDate'});
            }

            const group: IGroupContainer = {
                title: 'Projection Report',
                actionText: 'Export',
                description: 'Please confirm the date range to use on your loan/lease projection report',
                showCompanyStatus: true,
                company: c,
                dialogMode: true,
                collapseGroupNav: true,
                data: dateInfo,
                form: form,
                groups: [
                    {
                        header: 'group',
                        id: '1',
                        sections: [
                            {
                                header: 'Select date range',
                                description: 'Please choose a financial year or custom date range for your report',
                                form: form,
                                allowEdit: false,
                                items: formItems
                            }
                        ]
                    }
                ],
                cancelAction: (dRef: MatDialogRef<ModalEditorComponent>) => {
                    dRef.close();
                    o.next(null);
                },
                completeAction: async(dRef: MatDialogRef<ModalEditorComponent>) => {
                    const {startDate, endDate, yearId} = form.getRawValue();
                    let sd;
                    let ed;
                    if(startDate.format) {
                        sd = startDate.format('YYYY-MM-DD');
                    }else{
                        sd = startDate;
                    }
                    if(endDate.format) {
                        ed = endDate.format('YYYY-MM-DD');
                    }else{
                        ed = endDate;
                    }
                    if(!sd || !ed){
                        dRef.close();
                        o.next(false);
                    }else{
                        const dlg = await this.dlg.showPleaseWait('Please wait while we prepare your loan projection report');
                        this.api.downloadLoanProjection(this.SelectedCompanyId, sd, ed, parseInt(yearId)).subscribe(result =>{
                            dlg.close();
                            if(result){
                                this.dlg.showOk('Loan Projection Report', `Your loan projection report has now been downloaded`);
                            }
                            o.next(result);
                            dRef.close();
                        });
                    }
                    
                }
            }
            
            this.matDlg.open(ModalEditorComponent,
                {
                    data: group,
                    ...dialogDefaultFullScreen
                }
            );

            this.closeLoadingSnack(snack);
        });

        return await firstValueFrom(obs);
    }

}
